import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createTextWidgetSvgDataUrl } from "../utils/textWidget";
import { getCanvasRasterSize, createRenderCanvas, releaseCanvas, clearCanvasCache, isCanvasUsable, installCanvasRecovery } from "../utils/canvasResources";

const WIDTH = 1414; // A4 landscape ratio (sqrt(2) : 1)
const PAGE_HEIGHT = 1000;
const DEFAULT_PAGE_COUNT = 4;
const MAX_PAGE_COUNT = 100;
const ERASER_RADIUS = 6;
const ERASER_TOUCH_RADIUS = 16;
const ERASER_PEN_RADIUS = 10;
const ERASER_TOUCH_DEVICE_MOUSE_RADIUS = 14;
const PEN_CURSOR =
  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'32\' height=\'32\' viewBox=\'0 0 32 32\'%3E%3Cg transform=\'rotate(-25 16 16)\'%3E%3Cpath d=\'M9 22 L13 26 L24 15 L20 11 Z\' fill=\'%23111827\'/%3E%3Cpath d=\'M19.5 10.5 L22.8 7.2 C23.6 6.4 24.9 6.4 25.7 7.2 L26.8 8.3 C27.6 9.1 27.6 10.4 26.8 11.2 L23.5 14.5 Z\' fill=\'%23f59e0b\'/%3E%3Cpath d=\'M8.4 22.6 L12.2 26.4 L7 27.8 Z\' fill=\'%23fb7185\'/%3E%3C/g%3E%3C/svg%3E") 10 27, crosshair';
const HIGHLIGHTER_CURSOR =
  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'32\' height=\'32\' viewBox=\'0 0 32 32\'%3E%3Cg transform=\'translate(32 0) scale(-1 1) rotate(-25 16 16)\'%3E%3Crect x=\'8\' y=\'9\' width=\'16\' height=\'8\' rx=\'2\' fill=\'%23fbbf24\'/%3E%3Cpath d=\'M9 17 H23 V22 C23 23.1 22.1 24 21 24 H11 C9.9 24 9 23.1 9 22 Z\' fill=\'%23111827\'/%3E%3Crect x=\'11\' y=\'21\' width=\'10\' height=\'3\' rx=\'1.5\' fill=\'%23fde68a\' fill-opacity=\'0.72\'/%3E%3C/g%3E%3C/svg%3E") 15 28, crosshair';
const ERASER_CURSOR =
  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'32\' height=\'32\' viewBox=\'0 0 32 32\'%3E%3Cg transform=\'rotate(-25 16 16)\'%3E%3Crect x=\'8\' y=\'11\' width=\'16\' height=\'10\' rx=\'2\' fill=\'%23f8fafc\' stroke=\'%2394a3b8\' stroke-width=\'1.5\'/%3E%3Crect x=\'8\' y=\'11\' width=\'6\' height=\'10\' rx=\'2\' fill=\'%23fb7185\'/%3E%3C/g%3E%3C/svg%3E") 9 24, cell';
const SHAPE_CURSOR = "crosshair";
const POLYGON_CLOSE_TOLERANCE = 22;
const MAX_PAGE_CACHE_CANVASES = 4;
const LOW_MEMORY_PAGE_CACHE_CANVASES = 2;
const MEDIUM_MEMORY_PAGE_CACHE_CANVASES = 3;
const LOW_MEMORY_IMAGE_CACHE_ENTRIES = 20;
const MEDIUM_MEMORY_IMAGE_CACHE_ENTRIES = 32;
const DEFAULT_IMAGE_CACHE_ENTRIES = 48;
const PAGE_SCROLL_STEP_OPTIONS = [
  { value: 1, label: "1" },
  { value: 0.5, label: "1/2" },
  { value: 0.25, label: "1/4" },
];
const ELEMENT_APPEARANCE_COLORS = ["#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6", "#f8fafc", "#000000"];

function isFirefoxRuntime() {
  if (typeof navigator === "undefined") return false;
  return /(firefox|fxios)/i.test(String(navigator.userAgent || ""));
}

function isWhiteboardDebugEnabled() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem("whiteboardDebug") === "1";
  } catch {
    return false;
  }
}

function getStrokeAlpha(stroke, explicitAlpha) {
  if (typeof explicitAlpha === "number") return explicitAlpha;
  if (stroke?.tool === "highlighter" || stroke?.mode === "highlighter") return 0.42;
  return 1;
}

function drawStroke(ctx, stroke, options = {}) {
  const points = Array.isArray(stroke?.points) ? stroke.points : [];
  if (!points.length) return;

  ctx.save();
  ctx.globalAlpha = getStrokeAlpha(stroke, options.alpha);
  const resolvedStrokeStyle = options.strokeStyle || stroke.color || "#111827";
  const resolvedLineWidth = Number(options.lineWidth || stroke.width || 3);
  ctx.strokeStyle = resolvedStrokeStyle;
  ctx.lineWidth = resolvedLineWidth;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  if (options.dashed || stroke?.lineStyle === "dashed") ctx.setLineDash([10, 8]);

  // Single-click freehand stroke should render as a visible dot.
  if (points.length === 1) {
    const p = points[0];
    const radius = Math.max(1, resolvedLineWidth / 2);
    ctx.beginPath();
    ctx.fillStyle = resolvedStrokeStyle;
    ctx.arc(Number(p?.x || 0), Number(p?.y || 0), radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  const isFreehandStroke = stroke?.tool === "pen" || stroke?.tool === "highlighter" || stroke?.mode === "highlighter";
  ctx.beginPath();
  ctx.moveTo(Number(points[0]?.x || 0), Number(points[0]?.y || 0));
  if (isFreehandStroke && points.length >= 3) {
    for (let i = 1; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const midX = (Number(current?.x || 0) + Number(next?.x || 0)) / 2;
      const midY = (Number(current?.y || 0) + Number(next?.y || 0)) / 2;
      ctx.quadraticCurveTo(Number(current?.x || 0), Number(current?.y || 0), midX, midY);
    }
    const last = points[points.length - 1];
    ctx.lineTo(Number(last?.x || 0), Number(last?.y || 0));
  } else {
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(Number(points[i]?.x || 0), Number(points[i]?.y || 0));
    }
  }
  ctx.stroke();
  ctx.restore();
}

function createLinePoints(start, end) {
  return [
    { x: Number(start?.x || 0), y: Number(start?.y || 0) },
    { x: Number(end?.x || 0), y: Number(end?.y || 0) },
  ];
}

function createCirclePoints(center, edge, segments = 48) {
  const cx = Number(center?.x || 0);
  const cy = Number(center?.y || 0);
  const ex = Number(edge?.x || cx);
  const ey = Number(edge?.y || cy);
  const radius = Math.max(1, Math.hypot(ex - cx, ey - cy));
  const count = Math.max(24, Number(segments || 48));
  const points = [];
  for (let i = 0; i <= count; i += 1) {
    const angle = (Math.PI * 2 * i) / count;
    points.push({
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    });
  }
  return points;
}

function createPolygonPoints(points = [], closeShape = false) {
  const normalized = (Array.isArray(points) ? points : [])
    .map((point) => ({
      x: Number(point?.x || 0),
      y: Number(point?.y || 0),
    }));
  if (!normalized.length) return [];
  if (!closeShape || normalized.length < 3) return normalized;
  const first = normalized[0];
  const last = normalized[normalized.length - 1];
  if (Math.hypot(first.x - last.x, first.y - last.y) < 0.5) return normalized;
  return [...normalized, { ...first }];
}

function isPointNear(pointA, pointB, tolerance = POLYGON_CLOSE_TOLERANCE) {
  if (!pointA || !pointB) return false;
  return Math.hypot(Number(pointA.x || 0) - Number(pointB.x || 0), Number(pointA.y || 0) - Number(pointB.y || 0)) <= Number(tolerance || 0);
}

function resolveStrokeColor(color, themeMode, boardSurface = "default") {
  const normalized = String(color || "").trim().toLowerCase();
  if (!normalized) return themeMode === "light" ? "#0f172a" : "#f8fafc";

  const parseHexColor = (hex) => {
    const value = String(hex || "").trim().toLowerCase();
    const full = value.startsWith("#") ? value.slice(1) : value;
    if (full.length === 3) {
      const r = Number.parseInt(full[0] + full[0], 16);
      const g = Number.parseInt(full[1] + full[1], 16);
      const b = Number.parseInt(full[2] + full[2], 16);
      if ([r, g, b].some((n) => Number.isNaN(n))) return null;
      return { r, g, b };
    }
    if (full.length === 6) {
      const r = Number.parseInt(full.slice(0, 2), 16);
      const g = Number.parseInt(full.slice(2, 4), 16);
      const b = Number.parseInt(full.slice(4, 6), 16);
      if ([r, g, b].some((n) => Number.isNaN(n))) return null;
      return { r, g, b };
    }
    return null;
  };

  const isNearBlack = (hex) => {
    const rgb = parseHexColor(hex);
    if (!rgb) return false;
    const max = Math.max(rgb.r, rgb.g, rgb.b);
    const min = Math.min(rgb.r, rgb.g, rgb.b);
    // "Prawie czarny": bardzo ciemny, o małej różnicy kanałów.
    return max <= 48 && max - min <= 28;
  };

  const parseRgbColor = (value) => {
    const m = String(value || "")
      .trim()
      .toLowerCase()
      .match(/^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})(?:\s*[,/]\s*[\d.]+)?\s*\)$/);
    if (!m) return null;
    const r = Math.max(0, Math.min(255, Number(m[1])));
    const g = Math.max(0, Math.min(255, Number(m[2])));
    const b = Math.max(0, Math.min(255, Number(m[3])));
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return { r, g, b };
  };

  const isRgbNearBlack = (value) => {
    const rgb = parseRgbColor(value);
    if (!rgb) return false;
    const max = Math.max(rgb.r, rgb.g, rgb.b);
    const min = Math.min(rgb.r, rgb.g, rgb.b);
    return max <= 48 && max - min <= 28;
  };

  const isRgbNearWhite = (value) => {
    const rgb = parseRgbColor(value);
    if (!rgb) return false;
    const min = Math.min(rgb.r, rgb.g, rgb.b);
    const max = Math.max(rgb.r, rgb.g, rgb.b);
    return min >= 232 && max - min <= 24;
  };

  const isWhiteStroke = ["#f8fafc", "#ffffff", "#fff", "white", "rgb(255,255,255)", "rgb(248,250,252)"].includes(normalized) || isRgbNearWhite(normalized);
  const isBlackStroke =
    ["#111827", "#0f172a", "#020617", "#000000", "#000", "black", "rgb(0,0,0)"].includes(normalized) ||
    isNearBlack(normalized) ||
    isRgbNearBlack(normalized);
  const surface = String(boardSurface || "default").toLowerCase();

  // Lokalna podmiana tylko na renderze:
  // - black surface: zamiana black <-> white
  // - white surface: white -> black
  if (surface === "black") {
    if (isBlackStroke) return "#f8fafc";
  }
  if (surface === "white" && isWhiteStroke) {
    return "#000000";
  }

  if (themeMode === "light" && isWhiteStroke) {
    return "#0f172a";
  }
  return color;
}

function getBoardTheme(themeMode, boardSurface = "default") {
  const isLight = themeMode === "light";
  const surface = String(boardSurface || "default");

  if (surface === "white") {
    return {
      pageFill: "rgba(255, 255, 255, 0.995)",
      pageBorder: "rgba(148, 163, 184, 0.38)",
      pageSeparator: "rgba(148, 163, 184, 0.42)",
      pageLabel: "rgba(15, 23, 42, 0.34)",
      canvasBorder: "1px solid rgba(148, 163, 184, 0.28)",
      pageBadgeBg: "linear-gradient(180deg, #FF9900 0%, #FFBE46 100%)",
      pageBadgeText: "#1a1a1a",
      helperBg: "rgba(255,255,255,0.96)",
      helperBorder: "1px solid rgba(15,23,42,0.12)",
    };
  }

  if (surface === "black") {
    return {
      pageFill: "rgba(11, 15, 18, 0.995)",
      pageBorder: "rgba(71, 85, 105, 0.52)",
      pageSeparator: "rgba(148, 163, 184, 0.26)",
      pageLabel: "rgba(241, 245, 249, 0.52)",
      canvasBorder: "1px solid rgba(51,65,85,0.96)",
      pageBadgeBg: "linear-gradient(180deg, #FF9900 0%, #FFBE46 100%)",
      pageBadgeText: "#1a1a1a",
      helperBg: "rgba(12,16,40,0.82)",
      helperBorder: "1px solid rgba(255,255,255,0.12)",
    };
  }

  return {
    pageFill: isLight ? "rgba(175, 236, 196, 0.98)" : "rgba(16, 70, 48, 0.98)",
    pageBorder: isLight ? "rgba(86, 186, 118, 0.46)" : "rgba(6, 42, 28, 0.92)",
    pageSeparator: isLight ? "rgba(86, 186, 118, 0.5)" : "rgba(175, 236, 196, 0.18)",
    pageLabel: isLight ? "rgba(15, 71, 48, 0.42)" : "rgba(240,255,244,0.54)",
    canvasBorder: isLight ? "1px solid rgba(86, 186, 118, 0.28)" : "1px solid rgba(6, 42, 28, 0.96)",
    pageBadgeBg: "linear-gradient(180deg, #FF9900 0%, #FFBE46 100%)",
    pageBadgeText: "#1a1a1a",
    helperBg: isLight ? "rgba(255,255,255,0.96)" : "rgba(12,16,40,0.82)",
    helperBorder: isLight ? "1px solid rgba(15,23,42,0.12)" : "1px solid rgba(255,255,255,0.12)",
  };
}

function drawGrid(ctx, top, width, height, step = 64) {
  const safeStep = Math.max(24, Number(step || 64));
  const lineColor = "rgba(245, 158, 11, 0.16)";
  const bottom = top + height;
  const centerX = width / 2;
  const firstVerticalIndex = Math.ceil((0 - centerX) / safeStep);
  const lastVerticalIndex = Math.floor((width - centerX) / safeStep);
  const firstHorizontalIndex = Math.ceil(top / safeStep);
  const lastHorizontalIndex = Math.floor(bottom / safeStep);

  ctx.save();
  for (let index = firstVerticalIndex; index <= lastVerticalIndex; index += 1) {
    const x = centerX + index * safeStep;
    ctx.beginPath();
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 1;
    ctx.moveTo(x + 0.5, top);
    ctx.lineTo(x + 0.5, bottom);
    ctx.stroke();
  }

  for (let index = firstHorizontalIndex; index <= lastHorizontalIndex; index += 1) {
    const y = index * safeStep;
    ctx.beginPath();
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 1;
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(width, y + 0.5);
    ctx.stroke();
  }
  ctx.restore();
}

function drawPageGuides(ctx, pageCount, themeMode, boardSurface, gridEnabled, viewport = null, options = {}) {
  const lowPower = !!options?.lowPower;
  const gridMode = String(options?.gridMode || "full");
  const pagePaddingBefore = Math.max(0, Number(options?.pagePaddingBefore ?? options?.pagePadding ?? 1));
  const pagePaddingAfter = Math.max(0, Number(options?.pagePaddingAfter ?? options?.pagePadding ?? 1));
  const boardTheme = getBoardTheme(themeMode, boardSurface);
  const hasViewport = viewport && Number.isFinite(Number(viewport.top)) && Number.isFinite(Number(viewport.bottom));
  const viewportTop = hasViewport ? Math.max(0, Number(viewport.top || 0)) : 0;
  const viewportBottom = hasViewport ? Math.max(viewportTop, Number(viewport.bottom || 0)) : pageCount * PAGE_HEIGHT;
  const pageStart = hasViewport ? Math.max(0, Math.floor(viewportTop / PAGE_HEIGHT) - pagePaddingBefore) : 0;
  const pageEnd = hasViewport ? Math.min(pageCount - 1, Math.ceil(viewportBottom / PAGE_HEIGHT) + pagePaddingAfter) : (pageCount - 1);
  for (let page = pageStart; page <= pageEnd; page += 1) {
    const top = page * PAGE_HEIGHT;
    const bottom = top + PAGE_HEIGHT;
    if (hasViewport && (bottom < viewportTop || top > viewportBottom)) continue;

    ctx.save();
    ctx.fillStyle = boardTheme.pageFill;
    ctx.fillRect(0, top, WIDTH, PAGE_HEIGHT);

    ctx.strokeStyle = boardTheme.pageBorder;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, top + 0.5, WIDTH - 1, PAGE_HEIGHT - 1);

    if (gridEnabled && gridMode !== "off") {
      // Grid must stay visually constant across pages/LOD levels.
      drawGrid(ctx, top, WIDTH, PAGE_HEIGHT, 64);
    }

    if (page > 0) {
      if (!lowPower) ctx.setLineDash([12, 10]);
      ctx.strokeStyle = boardTheme.pageSeparator;
      ctx.beginPath();
      ctx.moveTo(24, top);
      ctx.lineTo(WIDTH - 24, top);
      ctx.stroke();
      if (!lowPower) ctx.setLineDash([]);
    }

    ctx.fillStyle = boardTheme.pageLabel;
    ctx.font = lowPower ? "600 22px sans-serif" : "600 28px sans-serif";
    ctx.fillText(`Strona ${page + 1}`, 28, top + 42);
    ctx.restore();
  }
}

function distancePointToSegment(point, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) {
    return Math.hypot(point.x - a.x, point.y - a.y);
  }

  const t = Math.max(
    0,
    Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / (dx * dx + dy * dy))
  );
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(point.x - projX, point.y - projY);
}

function strokeHitByPoint(stroke, point, radius) {
  const points = Array.isArray(stroke?.points) ? stroke.points : [];
  if (!points.length) return false;

  const strokeRadius = radius + Number(stroke?.width || 3) / 2;
  if (points.length === 1) {
    return Math.hypot(point.x - points[0].x, point.y - points[0].y) <= strokeRadius;
  }

  for (let i = 1; i < points.length; i++) {
    if (distancePointToSegment(point, points[i - 1], points[i]) <= strokeRadius) {
      return true;
    }
  }
  return false;
}

function getStrokeBounds(stroke) {
  const points = Array.isArray(stroke?.points) ? stroke.points : [];
  if (!points.length) return null;
  let minX = points[0].x;
  let maxX = points[0].x;
  let minY = points[0].y;
  let maxY = points[0].y;
  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  const pad = Math.max(3, Number(stroke?.width || 3) / 2);
  return {
    x: minX - pad,
    y: minY - pad,
    width: Math.max(1, maxX - minX + pad * 2),
    height: Math.max(1, maxY - minY + pad * 2),
  };
}

function getStrokeId(stroke, fallbackIndex) {
  return String(stroke?.id || `stroke_${fallbackIndex}`);
}

function isTouchCapableDevice() {
  if (typeof navigator === "undefined" && typeof window === "undefined") return false;
  const maxTouchPoints = Number(navigator?.maxTouchPoints || navigator?.msMaxTouchPoints || 0);
  if (maxTouchPoints > 0) return true;
  return typeof window !== "undefined" && "ontouchstart" in window;
}

function getImageId(element, fallbackIndex) {
  return String(element?.id || `image_${fallbackIndex}`);
}

function getElementSelectionKey(element, index) {
  if (element?.type === "image") return `image:${getImageId(element, index)}`;
  if (element?.type === "stroke") return `stroke:${getStrokeId(element, index)}`;
  return null;
}

function extractImageIdFromSelectionKey(key) {
  const value = String(key || "");
  if (!value.startsWith("image:")) return null;
  return value.slice("image:".length) || null;
}

function getElementBounds(element) {
  if (element?.type === "image") {
    const rect = getDisplayedImageRect(element, element?.crop);
    return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
  }
  if (element?.type === "stroke") return getStrokeBounds(element);
  return null;
}

function rectsIntersect(a, b) {
  if (!a || !b) return false;
  return (
    a.x <= b.x + b.width &&
    a.x + a.width >= b.x &&
    a.y <= b.y + b.height &&
    a.y + a.height >= b.y
  );
}

function expandRect(rect, paddingX = 0, paddingY = 0) {
  if (!rect) return null;
  return {
    x: Number(rect.x || 0) - Number(paddingX || 0),
    y: Number(rect.y || 0) - Number(paddingY || 0),
    width: Number(rect.width || 0) + Number(paddingX || 0) * 2,
    height: Number(rect.height || 0) + Number(paddingY || 0) * 2,
  };
}

function translateElement(element, dx, dy) {
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || (!dx && !dy)) return element;
  if (element?.type === "image") {
    return {
      ...element,
      x: Number(element.x || 0) + dx,
      y: Number(element.y || 0) + dy,
    };
  }
  if (element?.type === "stroke") {
    const points = Array.isArray(element.points) ? element.points : [];
    return {
      ...element,
      points: points.map((point) => ({
        ...point,
        x: Number(point.x || 0) + dx,
        y: Number(point.y || 0) + dy,
      })),
    };
  }
  return element;
}

function scaleElementUniform(element, scale, originX, originY) {
  const safeScale = Number.isFinite(scale) ? Math.max(0.05, Number(scale)) : 1;
  const ox = Number(originX || 0);
  const oy = Number(originY || 0);
  if (Math.abs(safeScale - 1) < 1e-6) return element;

  if (element?.type === "image") {
    const x = Number(element.x || 0);
    const y = Number(element.y || 0);
    const width = Math.max(1, Number(element.width || 1));
    const height = Math.max(1, Number(element.height || 1));
    return {
      ...element,
      x: ox + (x - ox) * safeScale,
      y: oy + (y - oy) * safeScale,
      width: Math.max(20, width * safeScale),
      height: Math.max(20, height * safeScale),
    };
  }

  if (element?.type === "stroke") {
    const points = Array.isArray(element.points) ? element.points : [];
    const originalWidth = Math.max(1, Number(element.width || 1));
    return {
      ...element,
      // Skalujemy geometrię (pozycje punktów), ale zachowujemy grubość linii.
      width: originalWidth,
      points: points.map((point) => ({
        ...point,
        x: ox + (Number(point?.x || 0) - ox) * safeScale,
        y: oy + (Number(point?.y || 0) - oy) * safeScale,
      })),
    };
  }

  return element;
}

function mergeBounds(boundsList) {
  const list = (boundsList || []).filter(Boolean);
  if (!list.length) return null;
  let minX = Number(list[0].x || 0);
  let minY = Number(list[0].y || 0);
  let maxX = minX + Math.max(1, Number(list[0].width || 0));
  let maxY = minY + Math.max(1, Number(list[0].height || 0));
  for (let i = 1; i < list.length; i += 1) {
    const b = list[i];
    const x = Number(b.x || 0);
    const y = Number(b.y || 0);
    const w = Math.max(1, Number(b.width || 0));
    const h = Math.max(1, Number(b.height || 0));
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  }
  return {
    x: minX,
    y: minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

function getSelectionResizeHandle(point, rect) {
  if (!point || !rect) return false;
  const handleSize = 18;
  const hx = Number(rect.x || 0) + Number(rect.width || 0);
  const hy = Number(rect.y || 0) + Number(rect.height || 0);
  return (
    point.x >= hx - handleSize &&
    point.x <= hx + handleSize &&
    point.y >= hy - handleSize &&
    point.y <= hy + handleSize
  );
}

function pointsAreClose(a, b, epsilon = 1.25) {
  if (!a || !b) return false;
  return (
    Math.abs(Number(a.x || 0) - Number(b.x || 0)) <= epsilon &&
    Math.abs(Number(a.y || 0) - Number(b.y || 0)) <= epsilon
  );
}

function getStrokeEditableVertexIndexes(stroke) {
  const points = Array.isArray(stroke?.points) ? stroke.points : [];
  if (!points.length) return [];
  const toolType = String(stroke?.tool || "");
  if (toolType === "line") {
    if (points.length === 1) return [0];
    const first = 0;
    const last = points.length - 1;
    return first === last ? [first] : [first, last];
  }
  if (toolType === "polygon") {
    if (points.length < 2) return [0];
    const isClosed = points.length >= 3 && pointsAreClose(points[0], points[points.length - 1], 1.5);
    const lastEditable = isClosed ? points.length - 2 : points.length - 1;
    const out = [];
    for (let i = 0; i <= lastEditable; i += 1) out.push(i);
    return out;
  }
  return [];
}

function moveStrokeVertexPoints(stroke, pointIndex, dx, dy) {
  const points = Array.isArray(stroke?.points) ? stroke.points : [];
  if (!points.length || !Number.isInteger(pointIndex)) return stroke;
  const safeDx = Number(dx || 0);
  const safeDy = Number(dy || 0);
  if (!safeDx && !safeDy) return stroke;
  if (pointIndex < 0 || pointIndex >= points.length) return stroke;

  const nextPoints = points.map((point, idx) => {
    if (idx !== pointIndex) return { ...point };
    return {
      ...point,
      x: Number(point?.x || 0) + safeDx,
      y: Number(point?.y || 0) + safeDy,
    };
  });

  const toolType = String(stroke?.tool || "");
  if (toolType === "polygon" && nextPoints.length >= 3) {
    const wasClosed = pointsAreClose(points[0], points[points.length - 1], 1.5);
    if (wasClosed) {
      // Zamknięty wielokąt ma pozostać zamknięty po edycji wierzchołków.
      nextPoints[nextPoints.length - 1] = { ...nextPoints[0] };
    }
  }

  return {
    ...stroke,
    points: nextPoints,
  };
}

function getStrokeVertexHandleAtPoint(stroke, point, tolerance = 16) {
  if (!stroke || !point) return null;
  const points = Array.isArray(stroke?.points) ? stroke.points : [];
  if (!points.length) return null;
  const indexes = getStrokeEditableVertexIndexes(stroke);
  for (const index of indexes) {
    const p = points[index];
    if (!p) continue;
    const distance = Math.hypot(Number(point.x || 0) - Number(p.x || 0), Number(point.y || 0) - Number(p.y || 0));
    if (distance <= tolerance) {
      return { index, point: p };
    }
  }
  return null;
}

function normalizeRotation(value) {
  const raw = Number(value || 0);
  if (!Number.isFinite(raw)) return 0;
  let next = raw % 360;
  if (next < 0) next += 360;
  return next;
}

function normalizeCrop(crop) {
  const raw = crop || {};
  const left = Math.max(0, Math.min(0.95, Number(raw.left ?? 0)));
  const top = Math.max(0, Math.min(0.95, Number(raw.top ?? 0)));
  const right = Math.max(left + 0.05, Math.min(1, Number(raw.right ?? 1)));
  const bottom = Math.max(top + 0.05, Math.min(1, Number(raw.bottom ?? 1)));
  return { left, top, right, bottom };
}

function getDisplayedImageRect(image, cropOverride) {
  const x = Number(image?.x || 0);
  const y = Number(image?.y || 0);
  const width = Number(image?.width || 0);
  const height = Number(image?.height || 0);
  const crop = normalizeCrop(cropOverride ?? image?.crop);
  const visibleX = x + width * crop.left;
  const visibleY = y + height * crop.top;
  const visibleWidth = width * (crop.right - crop.left);
  const visibleHeight = height * (crop.bottom - crop.top);
  return { x: visibleX, y: visibleY, width: visibleWidth, height: visibleHeight, fullX: x, fullY: y, fullWidth: width, fullHeight: height, crop };
}

function getRotatedRectBounds(rect, rotationDegrees = 0) {
  if (!rect) return null;
  const rotation = normalizeRotation(rotationDegrees);
  if (Math.abs(rotation) < 1e-6) return rect;
  const x = Number(rect.x || 0);
  const y = Number(rect.y || 0);
  const width = Number(rect.width || 0);
  const height = Number(rect.height || 0);
  const cx = x + width / 2;
  const cy = y + height / 2;
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const corners = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ].map((point) => {
    const dx = point.x - cx;
    const dy = point.y - cy;
    return {
      x: cx + dx * cos - dy * sin,
      y: cy + dx * sin + dy * cos,
    };
  });
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    ...rect,
    x: minX,
    y: minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

function isPointInsideImage(point, image, cropOverride) {
  const rect = getDisplayedImageRect(image, cropOverride);
  return point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height;
}

function getImageHandle(point, image, cropOverride) {
  const rect = getDisplayedImageRect(image, cropOverride);
  const size = 18;
  return point.x >= rect.x + rect.width - size && point.x <= rect.x + rect.width + 6 && point.y >= rect.y + rect.height - size && point.y <= rect.y + rect.height + 6;
}

function getCropHandle(point, image, cropOverride) {
  const rect = getDisplayedImageRect(image, cropOverride);
  const tolerance = 18;
  const midX = rect.x + rect.width / 2;
  const midY = rect.y + rect.height / 2;
  if (Math.abs(point.x - rect.x) <= tolerance && point.y >= rect.y && point.y <= rect.y + rect.height) return "left";
  if (Math.abs(point.x - (rect.x + rect.width)) <= tolerance && point.y >= rect.y && point.y <= rect.y + rect.height) return "right";
  if (Math.abs(point.y - rect.y) <= tolerance && point.x >= rect.x && point.x <= rect.x + rect.width) return "top";
  if (Math.abs(point.y - (rect.y + rect.height)) <= tolerance && point.x >= rect.x && point.x <= rect.x + rect.width) return "bottom";
  if (Math.abs(point.x - midX) <= tolerance && Math.abs(point.y - rect.y) <= tolerance) return "top";
  if (Math.abs(point.x - midX) <= tolerance && Math.abs(point.y - (rect.y + rect.height)) <= tolerance) return "bottom";
  if (Math.abs(point.y - midY) <= tolerance && Math.abs(point.x - rect.x) <= tolerance) return "left";
  if (Math.abs(point.y - midY) <= tolerance && Math.abs(point.x - (rect.x + rect.width)) <= tolerance) return "right";
  return null;
}

export default function WhiteboardCanvas({
  value,
  onChange,
  onEditGraph,
  onEditText,
  onAddPage,
  onPageChange,
  onDraftChange,
  tool = "pen",
  color,
  brushSize,
  disabled,
  overlay = null,
  topLeftControls = null,
  topRightControls = null,
  sideControls = null,
  bottomLeftTopControls = null,
  bottomCenterControls = null,
  bottomRightControls = null,
  bottomRightTopControls = null,
  controlsSide = "right",
  overlayInsets = null,
  overlayTopOffset = null,
  overlayZIndex = 3,
  forceTopControlsDetached = false,
  keepTopControlsOverlayOnMobile = false,
  fullscreenActive = false,
  fullscreenExtraOffset = 0,
  floatingControlsBottomOffset = 0,
  allowAddPage = true,
  maxDisplayWidth = null,
  viewportHeightOverride = null,
  preferFixedWidth = false,
  remoteDrafts = [],
  remoteCursors = [],
  onCursorMove,
  zoomPercent = 100,
  onZoomPercentChange = null,
  themeMode = "dark",
  boardSurface = "default",
  gridEnabled = false,
  enableElementSelection = false,
  readOnly = false,
  stylusOnly = false,
  holdToolSwitchEnabled = true,
  showStylusCursor = true,
  onToolChange = null,
}) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const scrollerRef = useRef(null);
  // Ostatnia "stabilna" szerokosc uzyta do wymiarowania plotna. Sluzy do
  // ignorowania wahan szerokosci rzedu szerokosci paska przewijania, ktore w
  // Chrome (klasyczne paski) potrafia wywolac petle: pasek -> wezsze plotno ->
  // nizsza strona -> pasek znika -> szersze -> wyzsza -> pasek wraca (miganie).
  const lastMeasuredWidthRef = useRef(0);
  const overlayRef = useRef(null);
  const draftOverlayRef = useRef(null);
  const stylusHoverCursorRef = useRef(null);
  const topControlsRef = useRef(null);
  const topLeftControlsRef = useRef(null);
  const activePointerIdRef = useRef(null);
  const activePointerTypeRef = useRef(null);
  const activePointerIsStylusLikeTouchRef = useRef(false);
  const activePointerStartedAtRef = useRef(0);
  const activePointerMaxPressureRef = useRef(0);
  const lastPenAtRef = useRef(0);
  const panSessionRef = useRef(null);
  const touchPointsRef = useRef(new Map());
  const touchGestureRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const isDrawingRef = useRef(false);
  const [draftStroke, setDraftStroke] = useState(null);
  const [displayWidth, setDisplayWidth] = useState(0);
  const [visibleCanvasRect, setVisibleCanvasRect] = useState({
    x: 0,
    y: 0,
    width: WIDTH,
    height: PAGE_HEIGHT,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canPageUp, setCanPageUp] = useState(false);
  const [isAtBoardBottom, setIsAtBoardBottom] = useState(false);
  const [overlayHeight, setOverlayHeight] = useState(0);
  const [topControlsHeight, setTopControlsHeight] = useState(0);
  const [topLeftControlsHeight, setTopLeftControlsHeight] = useState(0);
  const [erasePoint, setErasePoint] = useState(null);
  const [eraseCandidateIds, setEraseCandidateIds] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [selectedElementIds, setSelectedElementIds] = useState([]);
  const [selectionTransform, setSelectionTransform] = useState(null);
  const [marqueeSelection, setMarqueeSelection] = useState(null);
  const [imageTransform, setImageTransform] = useState(null);
  const [cropSession, setCropSession] = useState(null);
  const [rotateSession, setRotateSession] = useState(null);
  const [shapeSession, setShapeSession] = useState(null);
  const [shapeVertexEditEnabled, setShapeVertexEditEnabled] = useState(false);
  const [shapeVertexDraft, setShapeVertexDraft] = useState(null);
  const [appearanceEditorOpen, setAppearanceEditorOpen] = useState(false);
  const [lastAppearanceCustomColor, setLastAppearanceCustomColor] = useState("");
  const [imageRefreshTick, setImageRefreshTick] = useState(0);
  const [canvasRecoveryTick, setCanvasRecoveryTick] = useState(0);
  const [penPointerActive, setPenPointerActive] = useState(false);
  const [floatingControlsViewportLift, setFloatingControlsViewportLift] = useState(0);
  const [touchGestureActive, setTouchGestureActive] = useState(false);
  const [pageScrollStepRatio, setPageScrollStepRatio] = useState(1);
  const [pageScrollStepMenuOpen, setPageScrollStepMenuOpen] = useState(false);
  const draftStrokeRef = useRef(null);
  const draftPointsRef = useRef(null);
  const lastFreehandDraftRef = useRef(null);
  const committedFreehandIdsRef = useRef(new Set());
  const draftFrameRef = useRef({ rafId: null });
  const draftBroadcastRef = useRef({ lastAt: 0 });
  const eraserScanFrameRef = useRef({ rafId: null, point: null });
  const eraserLastPointRef = useRef(null);
  const eraseCandidateIdsRef = useRef([]);

  function normalizeEraseCandidateIds(ids) {
    return Array.from(new Set((Array.isArray(ids) ? ids : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean)));
  }

  function updateEraseCandidateIds(nextValue) {
    const previous = Array.isArray(eraseCandidateIdsRef.current) ? eraseCandidateIdsRef.current : [];
    const resolved = typeof nextValue === "function" ? nextValue(previous) : nextValue;
    const normalized = normalizeEraseCandidateIds(resolved);
    eraseCandidateIdsRef.current = normalized;
    setEraseCandidateIds(normalized);
    return normalized;
  }
  const scrollFrameRef = useRef(null);
  const programmaticScrollSyncTimerRef = useRef(null);
  const programmaticScrollSyncFrameRef = useRef(null);
  const canvasRectRef = useRef(null);
  const canvasRectMeasuredAtRef = useRef(0);
  const pendingMarqueeRef = useRef(null);
  const holdSwitchTimerRef = useRef(null);
  const holdSwitchSessionRef = useRef(null);
  const suppressPointerCommitRef = useRef(null);
  const shiftAxisLockRef = useRef(null);
  const firefoxFreehandModeRef = useRef(isFirefoxRuntime());

  const pageCount = Math.max(1, Math.min(MAX_PAGE_COUNT, Number(value?.pageCount || DEFAULT_PAGE_COUNT)));
  const boardVersion = Number(value?.version || 0);
  const totalHeight = PAGE_HEIGHT * pageCount;
  // Keep screen height in sync with pageCount in the same render.
  // A delayed resize measurement during an active stroke shifts its coordinates.
  const displayHeight = displayWidth > 0 ? Math.round((displayWidth * totalHeight) / WIDTH) : 0;
  const isLargeBoard = pageCount > 12;
  const deviceMemoryGb = useMemo(() => {
    if (typeof navigator === "undefined") return 4;
    const value = Number(navigator.deviceMemory || 0);
    return Number.isFinite(value) && value > 0 ? value : 4;
  }, [stylusOnly, tool]);
  const isLowMemoryDevice = deviceMemoryGb <= 4;
  const isVeryLowMemoryDevice = deviceMemoryGb <= 2;
  const pageBufferBefore = pageCount >= 24 ? 0 : pageCount >= 10 ? 1 : 2;
  const pageBufferAfter = pageCount >= 16 ? 0 : pageCount >= 10 ? 0 : 1;
  const effectivePageBufferBefore = isLowMemoryDevice ? Math.max(0, pageBufferBefore - 1) : pageBufferBefore;
  const effectivePageBufferAfter = isLowMemoryDevice ? 0 : pageBufferAfter;
  const maxPageCacheCanvases = isVeryLowMemoryDevice
    ? LOW_MEMORY_PAGE_CACHE_CANVASES
    : isLowMemoryDevice
      ? MEDIUM_MEMORY_PAGE_CACHE_CANVASES
      : MAX_PAGE_CACHE_CANVASES;
  const maxImageCacheEntries = isVeryLowMemoryDevice
    ? LOW_MEMORY_IMAGE_CACHE_ENTRIES
    : isLowMemoryDevice
      ? MEDIUM_MEMORY_IMAGE_CACHE_ENTRIES
      : DEFAULT_IMAGE_CACHE_ENTRIES;
  const canAddPage = allowAddPage && pageCount < MAX_PAGE_COUNT;
  const controlsOnLeft = controlsSide === "left";
  const sideAnchorStyle = controlsOnLeft ? { left: 14 } : { right: 14 };
  const bottomUtilityAnchorStyle = controlsOnLeft ? { right: 14 } : { left: 14 };
  const sideAlign = controlsOnLeft ? "flex-start" : "flex-end";
  const bottomUtilityAlign = controlsOnLeft ? "flex-end" : "flex-start";

  const LONG_PRESS_SWITCH_MS = 700;
  const LONG_PRESS_MOVE_TOLERANCE_PX = 6;
  const previousPageCountRef = useRef(pageCount);
  const preserveViewportAfterPageAddRef = useRef(false);
  const boardTheme = useMemo(() => getBoardTheme(themeMode, boardSurface), [boardSurface, themeMode]);
  const elements = useMemo(() => (Array.isArray(value?.elements) ? value.elements : []), [value]);
  const latestValueRef = useRef(value);
  const eraseCandidateIdSet = useMemo(() => new Set(eraseCandidateIds), [eraseCandidateIds]);
  const selectedElementIdSet = useMemo(() => new Set(selectedElementIds), [selectedElementIds]);
  const onChangeRef = useRef(onChange);
  const onDraftChangeRef = useRef(onDraftChange);
  const imageCacheRef = useRef(new Map());
  const pageRenderCacheRef = useRef(new Map());
  const pageRenderCacheTokenRef = useRef("");
  const viewportBaseSnapshotRef = useRef(null);
  const canvasHasRenderedRef = useRef(false);
  const localClipboardRef = useRef(null);
  const pasteCounterRef = useRef(0);
  const lastPointerBoardPointRef = useRef(null);
  const lastPointerAtRef = useRef(0);
  const selectedImageElement = useMemo(() => {
    if (!selectedImageId) return null;
    for (let i = elements.length - 1; i >= 0; i--) {
      const element = elements[i];
      if (element?.type !== "image") continue;
      if (getImageId(element, i) === selectedImageId) return element;
    }
    return null;
  }, [elements, selectedImageId]);
  const selectedStrokeElement = useMemo(() => {
    if (!enableElementSelection || selectedElementIds.length !== 1) return null;
    const onlyKey = String(selectedElementIds[0] || "");
    if (!onlyKey.startsWith("stroke:")) return null;
    const selectedStrokeId = onlyKey.slice("stroke:".length);
    if (!selectedStrokeId) return null;
    for (let i = elements.length - 1; i >= 0; i -= 1) {
      const element = elements[i];
      if (element?.type !== "stroke") continue;
      if (getStrokeId(element, i) !== selectedStrokeId) continue;
      return element;
    }
    return null;
  }, [elements, enableElementSelection, selectedElementIds]);
  const selectedShapeElement = useMemo(() => {
    const toolType = String(selectedStrokeElement?.tool || "");
    return toolType === "line" || toolType === "circle" || toolType === "polygon"
      ? selectedStrokeElement
      : null;
  }, [selectedStrokeElement]);
  const selectedShapeRect = useMemo(() => {
    if (!selectedShapeElement) return null;
    const shapeId = String(selectedShapeElement?.id || "");
    if (shapeVertexDraft?.elementId && shapeVertexDraft.elementId === shapeId && Array.isArray(shapeVertexDraft.points)) {
      return getStrokeBounds({ ...selectedShapeElement, points: shapeVertexDraft.points });
    }
    return getStrokeBounds(selectedShapeElement);
  }, [selectedShapeElement, shapeVertexDraft]);
  const transformedElementMap = useMemo(() => {
    if (!selectionTransform || !selectedElementIds.length) return null;
    const transformed = new Map();
    elements.forEach((element, index) => {
      const key = getElementSelectionKey(element, index);
      if (!key || !selectedElementIdSet.has(key)) return;
      const draftMatches =
        String(element?.type || "") === "stroke" &&
        shapeVertexDraft?.elementId &&
        (
          String(element?.id || "") === String(shapeVertexDraft.elementId) ||
          String(getStrokeId(element, index) || "") === String(shapeVertexDraft.elementId)
        ) &&
        Array.isArray(shapeVertexDraft.points);
      const baseElement = draftMatches
        ? { ...element, points: shapeVertexDraft.points.map((point) => ({ x: Number(point?.x || 0), y: Number(point?.y || 0) })) }
        : element;
      if (selectionTransform.mode === "vertex") {
        // Podgląd wierzchołków jest aktualizowany bezpośrednio przez shapeVertexDraft
        // podczas ruchu wskaźnika, żeby cała geometria podążała płynnie.
        return;
      }
      if (selectionTransform.mode === "resize") {
        const scale = Number(selectionTransform.scale || 1);
        const originX = Number(selectionTransform.originX || 0);
        const originY = Number(selectionTransform.originY || 0);
        if (Math.abs(scale - 1) < 1e-6) return;
        transformed.set(key, scaleElementUniform(baseElement, scale, originX, originY));
        return;
      }
      const dx = Number(selectionTransform.dx || 0);
      const dy = Number(selectionTransform.dy || 0);
      if (!dx && !dy) return;
      transformed.set(key, translateElement(baseElement, dx, dy));
    });
    return transformed.size ? transformed : null;
  }, [elements, selectedElementIdSet, selectedElementIds.length, selectionTransform, shapeVertexDraft]);
  const selectedImageRect = useMemo(() => {
    if (!selectedImageElement) return null;
    return getDisplayedImageRect(
      selectedImageElement,
      cropSession?.id === selectedImageId ? cropSession.crop : undefined
    );
  }, [cropSession, selectedImageElement, selectedImageId]);
  const selectedImageToolbarRect = useMemo(() => {
    if (!selectedImageElement || !selectedImageRect) return selectedImageRect;
    const rotation = rotateSession?.id === selectedImageId
      ? normalizeRotation(rotateSession.rotation)
      : normalizeRotation(selectedImageElement.rotation);
    return getRotatedRectBounds(selectedImageRect, rotation);
  }, [rotateSession, selectedImageElement, selectedImageId, selectedImageRect]);
  const selectedElementsRect = useMemo(() => {
    if (!enableElementSelection || !selectedElementIds.length) return null;
    const selectedSet = new Set(selectedElementIds.map((id) => String(id || "")).filter(Boolean));
    const bounds = [];
    elements.forEach((rawElement, index) => {
      const key = getElementSelectionKey(rawElement, index);
      if (!key || !selectedSet.has(key)) return;
      const element = transformedElementMap?.get(key) || rawElement;
      const box = getElementBounds(element);
      if (box) bounds.push(box);
    });
    return mergeBounds(bounds);
  }, [elements, enableElementSelection, selectedElementIds, transformedElementMap]);
  const selectedAppearanceElements = useMemo(() => {
    if (!enableElementSelection || selectedElementIds.length !== 1) return [];
    const selectedSet = new Set(selectedElementIds.map((id) => String(id || "")).filter(Boolean));
    return elements.filter((element, index) => {
      const key = getElementSelectionKey(element, index);
      if (!key || !selectedSet.has(key)) return false;
      return element?.type === "stroke" || (element?.type === "image" && element?.textWidget);
    });
  }, [elements, enableElementSelection, selectedElementIds]);
  const appearanceIsTextOnly = selectedAppearanceElements.length > 0 && selectedAppearanceElements.every(
    (element) => element?.type === "image" && element?.textWidget
  );
  const firstAppearanceElement = selectedAppearanceElements[0] || null;
  const selectedAppearanceColor = String(
    firstAppearanceElement?.type === "image"
      ? firstAppearanceElement?.textWidget?.color || "#f8fafc"
      : firstAppearanceElement?.color || "#f8fafc"
  );
  const selectedAppearanceThickness = appearanceIsTextOnly
    ? Number(firstAppearanceElement?.textWidget?.fontWeight || 400)
    : Number(firstAppearanceElement?.width || 3);
  const selectedElementIdsKey = selectedElementIds.join("|");

  useEffect(() => {
    setAppearanceEditorOpen(false);
  }, [selectedElementIdsKey]);
  const selectedShapeVertexHandles = useMemo(() => {
    if (!shapeVertexEditEnabled || !selectedShapeElement || selectedElementIds.length !== 1) return [];
    const selectedKey = String(selectedElementIds[0] || "");
    const shapeId = String(selectedShapeElement?.id || "");
    const draftShape =
      shapeVertexDraft?.elementId && shapeVertexDraft.elementId === shapeId && Array.isArray(shapeVertexDraft.points)
        ? { ...selectedShapeElement, points: shapeVertexDraft.points }
        : null;
    const previewShape = transformedElementMap?.get(selectedKey) || draftShape || selectedShapeElement;
    const points = Array.isArray(previewShape?.points) ? previewShape.points : [];
    const indexes = getStrokeEditableVertexIndexes(previewShape);
    return indexes
      .map((index) => ({ index, point: points[index] }))
      .filter((entry) => entry?.point);
  }, [shapeVertexEditEnabled, selectedShapeElement, selectedElementIds, transformedElementMap, shapeVertexDraft]);

  useEffect(() => {
    if (!selectedShapeElement || selectedElementIds.length !== 1) {
      setShapeVertexEditEnabled(false);
      setShapeVertexDraft(null);
    }
  }, [selectedShapeElement, selectedElementIds.length]);

  useEffect(() => {
    if (!selectedShapeElement || !shapeVertexDraft?.elementId) return;
    const selectedId = String(selectedShapeElement?.id || "");
    if (!selectedId || selectedId !== String(shapeVertexDraft.elementId || "")) {
      setShapeVertexDraft(null);
      setShapeVertexEditEnabled(false);
    }
  }, [selectedShapeElement, shapeVertexDraft]);
  const elementBoundsByKey = useMemo(() => {
    const map = new Map();
    elements.forEach((element, index) => {
      const key = getElementSelectionKey(element, index);
      if (!key) return;
      const bounds = getElementBounds(element);
      if (bounds) map.set(key, bounds);
    });
    return map;
  }, [elements]);
  const elementsByPageBucket = useMemo(() => {
    const buckets = new Map();
    const pushToBucket = (page, entry) => {
      const existing = buckets.get(page);
      if (existing) {
        existing.push(entry);
      } else {
        buckets.set(page, [entry]);
      }
    };

    elements.forEach((element, index) => {
      const key = getElementSelectionKey(element, index);
      if (!key) return;
      const bounds = elementBoundsByKey.get(key) || getElementBounds(element);
      if (!bounds) {
        pushToBucket(0, { index, key });
        return;
      }
      const rawStart = Math.floor(Number(bounds.y || 0) / PAGE_HEIGHT);
      const rawEnd = Math.floor((Number(bounds.y || 0) + Number(bounds.height || 0)) / PAGE_HEIGHT);
      const pageStart = Math.max(0, Math.min(pageCount - 1, rawStart));
      const pageEnd = Math.max(pageStart, Math.min(pageCount - 1, rawEnd));
      for (let page = pageStart; page <= pageEnd; page += 1) {
        pushToBucket(page, { index, key });
      }
    });
    return buckets;
  }, [elements, elementBoundsByKey, pageCount]);
  const renderRect = useMemo(() => {
    const paddingX = isLargeBoard ? 80 : 160;
    const basePaddingY = isLargeBoard ? 120 : 320;
    const touchPanPaddingY = touchGestureActive
      ? Math.max(basePaddingY, PAGE_HEIGHT * 1.5, Number(visibleCanvasRect?.height || PAGE_HEIGHT) * 2)
      : basePaddingY;
    const nextRect = expandRect(visibleCanvasRect, paddingX, touchPanPaddingY) || {
      x: 0,
      y: 0,
      width: WIDTH,
      height: totalHeight,
    };
    nextRect.x = Math.max(0, nextRect.x);
    nextRect.y = Math.max(0, nextRect.y);
    nextRect.width = Math.min(WIDTH - nextRect.x, Math.max(1, nextRect.width));
    nextRect.height = Math.min(totalHeight - nextRect.y, Math.max(1, nextRect.height));
    return nextRect;
  }, [visibleCanvasRect, totalHeight, isLargeBoard, touchGestureActive]);
  const canvasRaster = useMemo(
    () => getCanvasRasterSize(renderRect.width, renderRect.height, deviceMemoryGb),
    [renderRect, deviceMemoryGb]
  );
  const canvasRenderScale = canvasRaster.scale;
  const canvasPixelWidth = canvasRaster.width;
  const canvasPixelHeight = canvasRaster.height;
  const renderViewport = useMemo(
    () => ({
      top: renderRect.y,
      bottom: renderRect.y + renderRect.height,
    }),
    [renderRect]
  );
  const visibleElements = useMemo(() => {
    const visiblePageStart = Math.max(0, Math.floor(renderViewport.top / PAGE_HEIGHT) - effectivePageBufferBefore);
    const visiblePageEnd = Math.min(pageCount - 1, Math.ceil(renderViewport.bottom / PAGE_HEIGHT) + effectivePageBufferAfter);
    const candidateIndexes = new Set();

    for (let page = visiblePageStart; page <= visiblePageEnd; page += 1) {
      const pageEntries = elementsByPageBucket.get(page);
      if (!Array.isArray(pageEntries) || !pageEntries.length) continue;
      pageEntries.forEach((entry) => {
        if (Number.isInteger(entry?.index)) candidateIndexes.add(entry.index);
      });
    }

    if (selectedElementIds.length) {
      const selectedSet = new Set(selectedElementIds.map((id) => String(id || "")).filter(Boolean));
      elements.forEach((element, index) => {
        const key = getElementSelectionKey(element, index);
        if (key && selectedSet.has(key)) candidateIndexes.add(index);
      });
    }

    const isElementInRenderRect = (selectionKey, element) => {
      const hasMoveTransform = transformedElementMap?.has(selectionKey);
      const bounds = hasMoveTransform ? getElementBounds(element) : elementBoundsByKey.get(selectionKey);
      if (!bounds) return true;
      return rectsIntersect(bounds, renderRect);
    };
    const list = [];
    const orderedCandidateIndexes = Array.from(candidateIndexes).sort((a, b) => a - b);
    for (const index of orderedCandidateIndexes) {
      const rawElement = elements[index];
      if (!rawElement) continue;
      const selectionKey = getElementSelectionKey(rawElement, index);
      const draftMatches =
        String(rawElement?.type || "") === "stroke" &&
        shapeVertexDraft?.elementId &&
        (
          String(rawElement?.id || "") === String(shapeVertexDraft.elementId) ||
          String(getStrokeId(rawElement, index) || "") === String(shapeVertexDraft.elementId)
        ) &&
        Array.isArray(shapeVertexDraft.points);
      const element =
        transformedElementMap?.get(selectionKey) ||
        (draftMatches
          ? {
              ...rawElement,
              points: shapeVertexDraft.points.map((point) => ({
                x: Number(point?.x || 0),
                y: Number(point?.y || 0),
              })),
            }
          : rawElement);
      if (!isElementInRenderRect(selectionKey, element)) continue;
      list.push({ rawElement, element, index, selectionKey });
    }
    return list;
  }, [elements, elementsByPageBucket, transformedElementMap, elementBoundsByKey, renderRect, renderViewport, pageCount, selectedElementIds, effectivePageBufferBefore, effectivePageBufferAfter, shapeVertexDraft]);
  const isMobileNarrow = typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  const shouldDetachTopControls = (isMobileNarrow && !keepTopControlsOverlayOnMobile) || forceTopControlsDetached;
  const topControlsDetached = shouldDetachTopControls && !!topRightControls;
  const topLeftControlsDetached = shouldDetachTopControls && !!topLeftControls;
  const sideControlsDetached = isMobileNarrow && !!sideControls;
  const overlayDetached = isMobileNarrow && !!overlay;
  const hasDesktopTopControls =
    (!topControlsDetached && !!topRightControls) || (!topLeftControlsDetached && !!topLeftControls);
  const topControlsMaxHeight = Math.max(topControlsHeight, topLeftControlsHeight);
  const desktopOverlayTopOffset = hasDesktopTopControls ? Math.max(68, 14 + topControlsMaxHeight + 8) : 14;
  const sideControlsTopOffset = sideControls && !sideControlsDetached
    ? (
      controlsOnLeft
        ? (topLeftControls && !topLeftControlsDetached ? Math.max(68, 14 + topLeftControlsHeight + 8) : 14)
        : (topRightControls && !topControlsDetached ? Math.max(68, 14 + topControlsHeight + 8) : 14)
    )
    : 14;

  const resolvedOverlayTopOffset = Number.isFinite(Number(overlayTopOffset))
    ? Number(overlayTopOffset)
    : desktopOverlayTopOffset;
  const resolvedOverlayZIndex = Number.isFinite(Number(overlayZIndex))
    ? Number(overlayZIndex)
    : 3;
  const topOverlaySpace = overlay && !overlayDetached ? resolvedOverlayTopOffset + overlayHeight : 0;
  const sideControlsInset = sideControls && !sideControlsDetached ? 110 : 14;
  const overlayLeftInset = Number.isFinite(Number(overlayInsets?.left))
    ? Number(overlayInsets.left)
    : (controlsOnLeft ? sideControlsInset : 14);
  const overlayRightInset = Number.isFinite(Number(overlayInsets?.right))
    ? Number(overlayInsets.right)
    : (controlsOnLeft ? 14 : sideControlsInset);
  const fullscreenOffset = 24 + Math.max(0, Number(fullscreenExtraOffset || 0));
  const viewportBaseHeight = typeof window !== "undefined"
    ? Math.round(window.visualViewport?.height || window.innerHeight || 900)
    : 900;
  const detachedTopControlsReservedPx = 58;
  const detachedTopControlsSpace =
    (overlayDetached && overlay ? overlayHeight + 12 : 0);
  const fixedViewportHeight = Number.isFinite(Number(viewportHeightOverride))
    ? Math.max(180, Math.round(Number(viewportHeightOverride)))
    : 0;
  const viewportHeightPx = fullscreenActive
    ? Math.max(220, viewportBaseHeight - fullscreenOffset - detachedTopControlsSpace)
    : fixedViewportHeight > 0
      ? fixedViewportHeight
      : Math.min(Math.round(viewportBaseHeight * 0.72), 760);
  const viewportHeight = `${viewportHeightPx}px`;
  const needsVerticalBoardScroll = pageCount > 1 || Number(zoomPercent || 100) > 100;
  const floatingControlsExtraBottomOffset =
    Math.max(0, Number(floatingControlsBottomOffset || 0)) +
    Math.max(0, Number(floatingControlsViewportLift || 0));
  const floatingControlsBottomInset = fullscreenActive
    ? `calc(14px + env(safe-area-inset-bottom, 0px) + ${floatingControlsExtraBottomOffset}px)`
    : 14;
  const displayHeightPx = Number(displayHeight || totalHeight || PAGE_HEIGHT);
  const displayWidthPx = Number(displayWidth || WIDTH);
  const renderCanvasLeftPx = (Number(renderRect?.x || 0) / WIDTH) * displayWidthPx;
  const renderCanvasWidthPx = Math.max(
    1,
    (Number(renderRect?.width || WIDTH) / WIDTH) * displayWidthPx
  );
  const renderCanvasTopPx = (Number(renderRect?.y || 0) / Math.max(1, totalHeight)) * displayHeightPx;
  const renderCanvasHeightPx = Math.max(
    1,
    (Number(renderRect?.height || PAGE_HEIGHT) / Math.max(1, totalHeight)) * displayHeightPx
  );

  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (needsVerticalBoardScroll) return;
    const el = scrollerRef.current;
    if (!el) return;
    if (Number(el.scrollTop || 0) !== 0) {
      el.scrollTop = 0;
    }
  }, [needsVerticalBoardScroll, pageCount, zoomPercent]);

  function refreshCanvasRectCache() {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect || !Number.isFinite(rect.width) || !Number.isFinite(rect.height) || rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    canvasRectRef.current = rect;
    canvasRectMeasuredAtRef.current = (typeof performance !== "undefined" ? performance.now() : Date.now());
    return rect;
  }

  function clearHoldSwitchTimer() {
    if (holdSwitchTimerRef.current) {
      clearTimeout(holdSwitchTimerRef.current);
      holdSwitchTimerRef.current = null;
    }
  }

  function cancelHoldSwitchSession() {
    clearHoldSwitchTimer();
    holdSwitchSessionRef.current = null;
  }

  function canUseHoldSwitch(e) {
    if (!holdToolSwitchEnabled || !(tool === "pen" || tool === "eraser")) return false;
    const pointerType = String(e?.pointerType || "mouse");
    if (pointerType === "pen") return true;
    if (pointerType === "mouse") {
      const buttons = Number(e?.buttons || 0);
      return (buttons & 1) === 1;
    }
    return false;
  }

  function triggerHoldToolSwitch(pointerId) {
    if (!onToolChange) return;
    if (!(tool === "pen" || tool === "eraser")) return;
    if (activePointerIdRef.current === null || Number(activePointerIdRef.current) !== Number(pointerId)) return;
    const nextTool = tool === "pen" ? "eraser" : "pen";
    suppressPointerCommitRef.current = Number(pointerId);
    isDrawingRef.current = false;
    setIsDrawing(false);
    setDraftStroke(null);
    draftStrokeRef.current = null;
    draftPointsRef.current = null;
    lastFreehandDraftRef.current = null;
    setErasePoint(null);
    updateEraseCandidateIds([]);
    onDraftChange?.(null);
    cancelDraftFrame();
    cancelEraserScanFrame();
    onToolChange(nextTool);
  }

  function startHoldSwitchSession(e, point) {
    if (!canUseHoldSwitch(e) || !point) return;
    cancelHoldSwitchSession();
    const pointerId = Number(e?.pointerId);
    holdSwitchSessionRef.current = {
      pointerId,
      startX: Number(point.x || 0),
      startY: Number(point.y || 0),
      cancelled: false,
    };
    holdSwitchTimerRef.current = setTimeout(() => {
      const session = holdSwitchSessionRef.current;
      if (!session || session.cancelled) return;
      if (Number(session.pointerId) !== Number(pointerId)) return;
      triggerHoldToolSwitch(pointerId);
      cancelHoldSwitchSession();
    }, LONG_PRESS_SWITCH_MS);
  }

  function updateHoldSwitchSession(point) {
    const session = holdSwitchSessionRef.current;
    if (!session || !point) return;
    const dx = Number(point.x || 0) - Number(session.startX || 0);
    const dy = Number(point.y || 0) - Number(session.startY || 0);
    if (dx * dx + dy * dy > LONG_PRESS_MOVE_TOLERANCE_PX * LONG_PRESS_MOVE_TOLERANCE_PX) {
      cancelHoldSwitchSession();
    }
  }

  useEffect(() => {
    if (!holdToolSwitchEnabled) cancelHoldSwitchSession();
  }, [holdToolSwitchEnabled]);

  function getCanvasRectForEvent(forceRefresh = false) {
    const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
    const cached = canvasRectRef.current;
    if (
      !forceRefresh &&
      cached &&
      Number.isFinite(cached.width) &&
      Number.isFinite(cached.height) &&
      cached.width > 0 &&
      cached.height > 0 &&
      (now - Number(canvasRectMeasuredAtRef.current || 0)) <= 120
    ) {
      return cached;
    }
    return refreshCanvasRectCache() || cached || null;
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    refreshCanvasRectCache();

    const handleViewportChange = () => {
      refreshCanvasRectCache();
    };

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        refreshCanvasRectCache();
      });
      observer.observe(canvas);
    }

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportChange);
      window.visualViewport.addEventListener("scroll", handleViewportChange);
    }

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleViewportChange);
        window.visualViewport.removeEventListener("scroll", handleViewportChange);
      }
    };
  }, [displayWidth, displayHeight, zoomPercent, fullscreenActive, totalHeight]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!fullscreenActive) {
      setFloatingControlsViewportLift(0);
      return undefined;
    }

    let frameId = null;

    const readVisibleViewportBottom = () => {
      const visualViewport = window.visualViewport;
      if (visualViewport && Number.isFinite(visualViewport.height)) {
        return Math.max(0, Number(visualViewport.height || 0));
      }
      return Math.max(0, Number(window.innerHeight || 0));
    };

    const updateLift = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        frameId = null;
        const wrapper = wrapRef.current;
        if (!wrapper) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const viewportBottom = readVisibleViewportBottom();
        const baseInset = 14 + Math.max(0, Number(floatingControlsBottomOffset || 0));
        const breathingRoom = 10;
        const nextLift = Math.max(
          0,
          Math.ceil(wrapperRect.bottom - baseInset - viewportBottom + breathingRoom)
        );

        setFloatingControlsViewportLift((previousLift) => (
          Math.abs(previousLift - nextLift) < 1 ? previousLift : nextLift
        ));
      });
    };

    updateLift();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined" && wrapRef.current) {
      resizeObserver = new ResizeObserver(updateLift);
      resizeObserver.observe(wrapRef.current);
    }

    window.addEventListener("resize", updateLift);
    window.addEventListener("scroll", updateLift, true);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateLift);
      window.visualViewport.addEventListener("scroll", updateLift);
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateLift);
      window.removeEventListener("scroll", updateLift, true);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateLift);
        window.visualViewport.removeEventListener("scroll", updateLift);
      }
    };
  }, [
    displayHeight,
    displayWidth,
    floatingControlsBottomOffset,
    fullscreenActive,
    pageCount,
    viewportHeightPx,
  ]);

  useEffect(() => () => {
    clearCanvasCache(pageRenderCacheRef.current);
    releaseCanvas(viewportBaseSnapshotRef.current?.canvas);
    viewportBaseSnapshotRef.current = null;
    cancelHoldSwitchSession();
    if (scrollFrameRef.current) {
      cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    }
    if (programmaticScrollSyncTimerRef.current) {
      clearTimeout(programmaticScrollSyncTimerRef.current);
      programmaticScrollSyncTimerRef.current = null;
    }
    if (programmaticScrollSyncFrameRef.current) {
      cancelAnimationFrame(programmaticScrollSyncFrameRef.current);
      programmaticScrollSyncFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    while (imageCacheRef.current.size > maxImageCacheEntries) {
      const oldestKey = imageCacheRef.current.keys().next().value;
      if (!oldestKey) break;
      imageCacheRef.current.delete(oldestKey);
    }
  }, [maxImageCacheEntries, imageRefreshTick]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = draftOverlayRef.current;
    if (!canvas) return undefined;

    return installCanvasRecovery({
      canvas,
      overlay,
      invalidate: () => {
        clearCanvasCache(pageRenderCacheRef.current);
        releaseCanvas(viewportBaseSnapshotRef.current?.canvas);
        viewportBaseSnapshotRef.current = null;
      },
      redraw: () => setCanvasRecoveryTick((tick) => tick + 1),
      hasRendered: () => canvasHasRenderedRef.current,
    });
  }, []);

  useEffect(() => {
    if (showStylusCursor) return;
    if (stylusHoverCursorRef.current) stylusHoverCursorRef.current.style.display = "none";
    setPenPointerActive(false);
  }, [showStylusCursor]);

  useEffect(() => {
    if (isDrawingRef.current) return;
    draftStrokeRef.current = draftStroke;
  }, [draftStroke]);

  useEffect(() => {
    const hasActiveFreehandDraft = () => (
      (tool === "pen" || tool === "highlighter") &&
      (isDrawingRef.current || !!draftStrokeRef.current || !!lastFreehandDraftRef.current)
    );

    const finishFromPointer = (event) => {
      if (!hasActiveFreehandDraft()) return;
      finishStrokeSafely(event, "window_pointer_finish");
    };

    const finishWithoutPointer = () => {
      if (!hasActiveFreehandDraft()) return;
      finishStrokeSafely(null, "window_mouse_finish");
    };

    window.addEventListener("pointerup", finishFromPointer, true);
    window.addEventListener("pointercancel", finishFromPointer, true);
    window.addEventListener("mouseup", finishWithoutPointer, true);
    window.addEventListener("blur", finishWithoutPointer, true);

    return () => {
      window.removeEventListener("pointerup", finishFromPointer, true);
      window.removeEventListener("pointercancel", finishFromPointer, true);
      window.removeEventListener("mouseup", finishWithoutPointer, true);
      window.removeEventListener("blur", finishWithoutPointer, true);
    };
  });

  function cancelDraftFrame() {
    const rafId = draftFrameRef.current?.rafId;
    if (rafId) {
      cancelAnimationFrame(rafId);
      draftFrameRef.current.rafId = null;
    }
  }

  function scheduleDraftFrame() {
    if (draftFrameRef.current.rafId) return;
    draftFrameRef.current.rafId = requestAnimationFrame(() => {
      draftFrameRef.current.rafId = null;
      const snapshot = draftStrokeRef.current;
      if (!snapshot) return;
      const frameDraft = {
        ...snapshot,
        points: Array.isArray(snapshot.points) ? snapshot.points : [],
      };
      if (frameDraft.tool === "pen" || frameDraft.tool === "highlighter") {
        lastFreehandDraftRef.current = {
          ...frameDraft,
          points: frameDraft.points.map((point) => ({
            x: Number(point?.x || 0),
            y: Number(point?.y || 0),
          })),
        };
      }
      setDraftStroke(frameDraft);
      onDraftChangeRef.current?.(frameDraft);
    });
  }

  function flushDraftFrameNow() {
    cancelDraftFrame();
    const snapshot = draftStrokeRef.current;
    if (!snapshot) return null;
    const frameDraft = {
      ...snapshot,
      points: Array.isArray(snapshot.points) ? snapshot.points : [],
    };
    if (frameDraft.tool === "pen" || frameDraft.tool === "highlighter") {
      lastFreehandDraftRef.current = {
        ...frameDraft,
        points: frameDraft.points.map((point) => ({
          x: Number(point?.x || 0),
          y: Number(point?.y || 0),
        })),
      };
    }
    setDraftStroke(frameDraft);
    draftBroadcastRef.current.lastAt = Date.now();
    onDraftChangeRef.current?.(frameDraft);
    return frameDraft;
  }

  function cancelEraserScanFrame() {
    const rafId = eraserScanFrameRef.current?.rafId;
    if (rafId) {
      cancelAnimationFrame(rafId);
      eraserScanFrameRef.current.rafId = null;
    }
    eraserScanFrameRef.current.point = null;
    eraserScanFrameRef.current.points = [];
    eraserScanFrameRef.current.pointerType = "";
    eraserLastPointRef.current = null;
  }

  function getEraserRadius(pointerType = activePointerTypeRef.current || "mouse") {
    if (String(pointerType || "") === "touch") return ERASER_TOUCH_RADIUS;
    if (String(pointerType || "") === "pen") return ERASER_PEN_RADIUS;
    if (String(pointerType || "") === "mouse" && isTouchCapableDevice()) return ERASER_TOUCH_DEVICE_MOUSE_RADIUS;
    return ERASER_RADIUS;
  }

  function buildEraserScanPoints(point, pointerType = activePointerTypeRef.current || "mouse") {
    const currentPoint = {
      x: Number(point?.x || 0),
      y: Number(point?.y || 0),
    };
    const previousPoint = eraserLastPointRef.current;
    eraserLastPointRef.current = currentPoint;
    if (!previousPoint) return [currentPoint];

    const radius = getEraserRadius(pointerType);
    const distance = Math.hypot(currentPoint.x - previousPoint.x, currentPoint.y - previousPoint.y);
    const steps = Math.max(1, Math.min(12, Math.ceil(distance / Math.max(2, radius * 0.75))));
    const points = [];
    for (let i = 1; i <= steps; i += 1) {
      const t = i / steps;
      points.push({
        x: previousPoint.x + (currentPoint.x - previousPoint.x) * t,
        y: previousPoint.y + (currentPoint.y - previousPoint.y) * t,
      });
    }
    return points;
  }

  function collectEraserScanPointsNow(point, sourceElements, pointerType = activePointerTypeRef.current || "mouse") {
    const scanPoints = buildEraserScanPoints(point, pointerType);
    const scanRadius = getEraserRadius(pointerType);
    const nextIds = new Set(eraseCandidateIdsRef.current);
    (sourceElements || []).forEach((element, index) => {
      if (element?.type !== "stroke") return;
      if (scanPoints.some((scanPoint) => strokeHitByPoint(element, scanPoint, scanRadius))) {
        nextIds.add(getStrokeId(element, index));
      }
    });
    return updateEraseCandidateIds(Array.from(nextIds));
  }

  function deleteSelectedImage() {
    if (!selectedImageId) return;
    const currentState = latestValueRef.current || {};
    const nextElements = (Array.isArray(currentState.elements) ? currentState.elements : []).filter((element, index) => {
      if (element?.type !== "image") return true;
      return getImageId(element, index) !== selectedImageId;
    });

    setSelectedImageId(null);
    setImageTransform(null);
    setCropSession(null);
    setRotateSession(null);
    onChange?.(
      {
        ...currentState,
        version: Number(currentState.version || 0) + 1,
        elements: nextElements,
      },
      { kind: "image_delete" }
    );
  }

  function deleteSelectedElements() {
    if (!enableElementSelection || !selectedElementIds.length) return;
    const selectedSet = new Set(selectedElementIds.map((key) => String(key || "")).filter(Boolean));
    const currentState = latestValueRef.current || {};
    const currentElements = Array.isArray(currentState.elements) ? currentState.elements : [];
    const removedIds = [];
    const nextElements = currentElements.filter((element, index) => {
      const key = getElementSelectionKey(element, index);
      if (!key || !selectedSet.has(key)) return true;
      const elementId = String(element?.id || "").trim();
      if (elementId) removedIds.push(elementId);
      return false;
    });

    if (nextElements.length === currentElements.length) return;

    setSelectedElementIds([]);
    setSelectedImageId(null);
    setImageTransform(null);
    setCropSession(null);
    setRotateSession(null);
    setSelectionTransform(null);
    setMarqueeSelection(null);
    onChange?.(
      {
        ...currentState,
        version: Number(currentState.version || 0) + 1,
        elements: nextElements,
      },
      { kind: "selection_delete", removedIds }
    );
  }

  function getSelectedElementsSnapshot() {
    if (enableElementSelection && selectedElementIds.length) {
      const selectedSet = new Set(selectedElementIds.map((key) => String(key || "")).filter(Boolean));
      return elements
        .filter((element, index) => {
          const key = getElementSelectionKey(element, index);
          return !!key && selectedSet.has(key);
        })
        .map((element) => JSON.parse(JSON.stringify(element)));
    }

    if (selectedImageId) {
      for (let i = elements.length - 1; i >= 0; i -= 1) {
        const element = elements[i];
        if (String(element?.type || "") !== "image") continue;
        if (getImageId(element, i) !== selectedImageId) continue;
        return [JSON.parse(JSON.stringify(element))];
      }
    }

    return [];
  }

  function createPastedElement(element, offset) {
    const base = JSON.parse(JSON.stringify(element || {}));
    const kind = String(base?.type || "");
    const nextId = `${kind || "element"}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const deltaX = Number(offset?.dx || 0);
    const deltaY = Number(offset?.dy || 0);
    if (kind === "image") {
      return {
        ...base,
        id: nextId,
        x: Number(base.x || 0) + deltaX,
        y: Number(base.y || 0) + deltaY,
      };
    }
    if (kind === "stroke") {
      const points = Array.isArray(base.points) ? base.points : [];
      return {
        ...base,
        id: nextId,
        points: points.map((point) => ({
          ...point,
          x: Number(point?.x || 0) + deltaX,
          y: Number(point?.y || 0) + deltaY,
        })),
      };
    }
    return {
      ...base,
      id: nextId,
    };
  }

  function copySelectedElements() {
    const selected = getSelectedElementsSnapshot();
    if (!selected.length) return false;
    const payload = {
      kind: "matematron:whiteboard-elements",
      createdAt: Date.now(),
      elements: selected,
    };
    localClipboardRef.current = payload;
    try {
      navigator.clipboard?.writeText?.(JSON.stringify(payload));
    } catch {
      // ignore system clipboard failures; local clipboard is enough
    }
    return true;
  }

  function getElementsBoundsForPaste(items) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let hasBounds = false;

    (items || []).forEach((item) => {
      const bounds = getElementBounds(item);
      if (!bounds) return;
      hasBounds = true;
      minX = Math.min(minX, Number(bounds.x || 0));
      minY = Math.min(minY, Number(bounds.y || 0));
      maxX = Math.max(maxX, Number(bounds.x || 0) + Number(bounds.width || 0));
      maxY = Math.max(maxY, Number(bounds.y || 0) + Number(bounds.height || 0));
    });

    if (!hasBounds) return null;
    return {
      x: minX,
      y: minY,
      width: Math.max(1, maxX - minX),
      height: Math.max(1, maxY - minY),
    };
  }

  function getPasteAnchorPoint() {
    const recentMs = Date.now() - Number(lastPointerAtRef.current || 0);
    const recentPoint = lastPointerBoardPointRef.current;
    if (
      recentPoint &&
      Number.isFinite(recentPoint.x) &&
      Number.isFinite(recentPoint.y) &&
      recentMs <= 30000
    ) {
      return {
        x: Math.max(0, Math.min(WIDTH, Number(recentPoint.x || 0))),
        y: Math.max(0, Math.min(totalHeight, Number(recentPoint.y || 0))),
      };
    }

    return {
      x: Math.max(0, Math.min(WIDTH, Number(visibleCanvasRect.x || 0) + Number(visibleCanvasRect.width || WIDTH) / 2)),
      y: Math.max(0, Math.min(totalHeight, Number(visibleCanvasRect.y || 0) + Number(visibleCanvasRect.height || PAGE_HEIGHT) / 2)),
    };
  }

  function pasteElementsFromPayload(payload) {
    const source = payload && typeof payload === "object" ? payload : null;
    const sourceElements = Array.isArray(source?.elements) ? source.elements : [];
    if (!sourceElements.length) return false;

    pasteCounterRef.current = Number(pasteCounterRef.current || 0) + 1;
    const sourceBounds = getElementsBoundsForPaste(sourceElements);
    const anchor = getPasteAnchorPoint();
    const nudge = 14 * Math.max(0, Math.min(6, Number(pasteCounterRef.current || 1) - 1));
    const targetX = Number(anchor.x || 0) + nudge;
    const targetY = Number(anchor.y || 0) + nudge;
    const offset = {
      dx: targetX - Number(sourceBounds?.x || 0),
      dy: targetY - Number(sourceBounds?.y || 0),
    };
    const pasted = sourceElements.map((element) => createPastedElement(element, offset));
    const currentState = latestValueRef.current || {};
    const currentElements = Array.isArray(currentState.elements) ? currentState.elements : [];
    const nextElements = currentElements.concat(pasted);

    onChange?.(
      {
        ...currentState,
        version: Number(currentState.version || 0) + 1,
        elements: nextElements,
      },
      { kind: "draw" }
    );

    const nextSelectionKeys = pasted
      .map((element, index) => getElementSelectionKey(element, currentElements.length + index))
      .filter(Boolean);
    updateSelectedElements(nextSelectionKeys);
    return true;
  }

  function tryPasteFromClipboardData(clipboardData) {
    const text = String(clipboardData?.getData?.("text/plain") || "").trim();
    if (!text) return false;
    try {
      const parsed = JSON.parse(text);
      if (String(parsed?.kind || "") !== "matematron:whiteboard-elements") return false;
      return pasteElementsFromPayload(parsed);
    } catch {
      return false;
    }
  }

  function toggleSelectedShapeStyle() {
    if (!selectedShapeElement) return;
    const targetId = String(selectedShapeElement.id || "").trim();
    if (!targetId) return;
    const currentState = latestValueRef.current || {};
    const currentElements = Array.isArray(currentState.elements) ? currentState.elements : [];
    let didChange = false;
    const nextElements = currentElements.map((element) => {
      if (String(element?.type || "") !== "stroke") return element;
      if (String(element?.id || "") !== targetId) return element;
      const nextLineStyle = String(element?.lineStyle || "") === "dashed" ? "solid" : "dashed";
      didChange = true;
      return {
        ...element,
        lineStyle: nextLineStyle,
      };
    });
    if (!didChange) return;
    onChange?.(
      {
        ...currentState,
        version: Number(currentState.version || 0) + 1,
        elements: nextElements,
      },
      { kind: "line_style", elementId: targetId }
    );
  }

  function updateSelectedElementAppearance({ color: nextColor, thickness: nextThickness } = {}) {
    if (!enableElementSelection || !selectedElementIds.length) return;
    const selectedSet = new Set(selectedElementIds.map((key) => String(key || "")).filter(Boolean));
    const currentState = latestValueRef.current || {};
    const currentElements = Array.isArray(currentState.elements) ? currentState.elements : [];
    let didChange = false;

    const nextElements = currentElements.map((element, index) => {
      const key = getElementSelectionKey(element, index);
      if (!key || !selectedSet.has(key)) return element;

      if (element?.type === "stroke") {
        const nextElement = { ...element };
        if (nextColor) nextElement.color = String(nextColor);
        if (Number.isFinite(Number(nextThickness))) {
          nextElement.width = Math.max(1, Math.min(48, Number(nextThickness)));
        }
        didChange = true;
        return nextElement;
      }

      if (element?.type === "image" && element?.textWidget) {
        const textWidget = element.textWidget || {};
        const color = String(nextColor || textWidget.color || "#f8fafc");
        const fontWeight = Number.isFinite(Number(nextThickness))
          ? Math.max(300, Math.min(900, Math.round(Number(nextThickness) / 100) * 100))
          : Number(textWidget.fontWeight || 400);
        const fontSize = Number(textWidget.fontSize || 36);
        const text = String(textWidget.text || "");
        const { src, layout } = createTextWidgetSvgDataUrl({ text, color, fontSize, fontWeight });
        const width = Math.max(1, Number(element.width || layout.svgWidth));
        didChange = true;
        return {
          ...element,
          src,
          width,
          height: Math.max(1, Math.round(width * (layout.svgHeight / layout.svgWidth))),
          naturalWidth: layout.svgWidth,
          naturalHeight: layout.svgHeight,
          textWidget: {
            ...textWidget,
            color,
            fontWeight,
          },
        };
      }

      return element;
    });

    if (!didChange) return;
    const changedElement = selectedAppearanceElements[0] || null;
    onChange?.(
      {
        ...currentState,
        version: Number(currentState.version || 0) + 1,
        elements: nextElements,
      },
      {
        kind: changedElement?.type === "image" ? "image_transform" : "stroke_patch",
        elementId: String(changedElement?.id || ""),
      }
    );
  }

  useEffect(() => {
    function handleKeyDown(e) {
      const target = e.target;
      const tagName = String(target?.tagName || "").toLowerCase();
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        target?.isContentEditable;
      if (isEditable) return;
      const key = String(e.key || "").toLowerCase();
      const ctrlOrMeta = e.ctrlKey || e.metaKey;

      if (ctrlOrMeta && key === "c") {
        if (copySelectedElements()) {
          e.preventDefault();
        }
        return;
      }

      if (ctrlOrMeta && key === "v") {
        // Let native paste event flow so image paste handlers can run.
        return;
      }

      if (!selectedImageId && !(enableElementSelection && selectedElementIds.length)) return;
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (enableElementSelection && selectedElementIds.length) {
        deleteSelectedElements();
      } else {
        deleteSelectedImage();
      }
      e.preventDefault();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deleteSelectedElements, deleteSelectedImage, enableElementSelection, selectedElementIds, selectedImageId]);

  useEffect(() => {
    function handlePaste(e) {
      const target = e.target;
      const tagName = String(target?.tagName || "").toLowerCase();
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        target?.isContentEditable;
      if (isEditable) return;

      const items = Array.from(e.clipboardData?.items || []);
      const hasImage = items.some((item) => String(item?.type || "").startsWith("image/"));
      if (hasImage) {
        // Image paste is handled in page-level whiteboard paste handler.
        return;
      }

      if (tryPasteFromClipboardData(e.clipboardData)) {
        e.preventDefault();
        return;
      }

      // Fallback for environments where clipboard text write was blocked.
      if (pasteElementsFromPayload(localClipboardRef.current)) {
        e.preventDefault();
      }
    }

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [pasteElementsFromPayload, tryPasteFromClipboardData]);

  useEffect(() => {
    onDraftChangeRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    isDrawingRef.current = false;
    setIsDrawing(false);
    setDraftStroke(null);
    draftStrokeRef.current = null;
    draftPointsRef.current = null;
    lastFreehandDraftRef.current = null;
    draftPointsRef.current = null;
    cancelDraftFrame();
    cancelEraserScanFrame();
    setShapeSession(null);
    setErasePoint(null);
    eraseCandidateIdsRef.current = [];
    setEraseCandidateIds([]);
    setSelectionTransform(null);
    setMarqueeSelection(null);
    setImageTransform(null);
    setCropSession(null);
    setRotateSession(null);
    activePointerIdRef.current = null;
    activePointerTypeRef.current = null;
    panSessionRef.current = null;
    touchPointsRef.current.clear();
    touchGestureRef.current = null;
    onDraftChangeRef.current?.(null);
  }, [tool]);

  useEffect(() => () => {
    cancelDraftFrame();
    cancelEraserScanFrame();
    draftPointsRef.current = null;
    canvasRectRef.current = null;
    touchPointsRef.current.clear();
    touchGestureRef.current = null;
  }, []);

  useEffect(() => {
    if (!enableElementSelection) return;
    if (selectedElementIds.length !== 1) {
      setSelectedImageId(null);
      return;
    }
    const imageId = extractImageIdFromSelectionKey(selectedElementIds[0]);
    setSelectedImageId(imageId || null);
  }, [enableElementSelection, selectedElementIds]);

  useEffect(() => {
    if (!enableElementSelection && selectedElementIds.length) {
      setSelectedElementIds([]);
    }
  }, [enableElementSelection, selectedElementIds.length]);

  function recoverStaleActivePointerBeforeStart(e) {
    if (activePointerIdRef.current === null) return;
    const incomingPointerId = Number(e?.pointerId);
    if (Number.isFinite(incomingPointerId) && incomingPointerId === Number(activePointerIdRef.current)) return;

    const now = Date.now();
    const activeAgeMs = now - Number(activePointerStartedAtRef.current || 0);
    const lastInputAgeMs = now - Number(lastPointerAtRef.current || 0);
    const hasFreehandDraft =
      (tool === "pen" || tool === "highlighter") &&
      !!(draftStrokeRef.current || lastFreehandDraftRef.current || draftStroke);
    const incomingPointerType = String(e?.pointerType || "mouse");
    const activePointerType = String(activePointerTypeRef.current || "");
    const incomingSoftPalmTouch = incomingPointerType === "touch" && isPalmLikeTouchEvent(e);
    const incomingLooksDrawable =
      incomingPointerType === "pen" ||
      incomingPointerType === "mouse" ||
      (
        incomingPointerType === "touch" &&
        !isHardPalmTouchEvent(e) &&
        !(activePointerType === "pen" && incomingSoftPalmTouch)
      );

    // Some tablet browsers miss pointerup/cancel. If the previous freehand pointer is stale,
    // commit it before accepting a new stroke instead of silently ignoring the next line.
    if (
      hasFreehandDraft &&
      incomingLooksDrawable &&
      activeAgeMs > 80 &&
      (incomingPointerType !== "touch" || lastInputAgeMs > 55 || activePointerType === "touch")
    ) {
      logFreehandDebug({
        phase: "recover_stale_active_pointer",
        reason: "new_drawable_pointer",
        activeAgeMs,
        lastInputAgeMs,
        incomingPointerType,
        activePointerType,
        incomingPointerId,
        activePointer: activePointerIdRef.current,
      });
      finishStrokeSafely(null, "new_pointer_recovered_previous_freehand");
      return;
    }

    if (tool === "select" && activeAgeMs > 90) {
      setSelectionTransform(null);
      setImageTransform(null);
      setCropSession(null);
      setRotateSession(null);
      setMarqueeSelection(null);
      pendingMarqueeRef.current = null;
      releaseActivePointer();
      return;
    }

    if (!isDrawingRef.current || activeAgeMs > 1800) {
      releaseActivePointer();
    }
  }

  function getTouchContactMetrics(e) {
    const pointerWidth = Number(e?.width || 0);
    const pointerHeight = Number(e?.height || 0);
    const pointerPressure = Number(e?.pressure || 0);
    const hasContactSize = pointerWidth > 0 && pointerHeight > 0;
    return {
      pointerWidth,
      pointerHeight,
      pointerPressure,
      hasContactSize,
      largestContactSide: Math.max(pointerWidth, pointerHeight),
      contactArea: hasContactSize ? pointerWidth * pointerHeight : 0,
    };
  }

  function isPalmLikeTouchEvent(e) {
    if (String(e?.pointerType || "") !== "touch") return false;
    const metrics = getTouchContactMetrics(e);
    if (!metrics.hasContactSize) return false;
    return isHardPalmContactMetrics(metrics) || isSoftPalmContactMetrics(metrics);
  }

  function isHardPalmContactMetrics(metrics) {
    return metrics.largestContactSide >= 58 || metrics.contactArea >= 2200;
  }

  function isSoftPalmContactMetrics(metrics) {
    return metrics.largestContactSide >= 46 || (
      metrics.largestContactSide >= 34 &&
      metrics.contactArea >= 1100
    );
  }

  function isHardPalmTouchEvent(e) {
    if (String(e?.pointerType || "") !== "touch") return false;
    const metrics = getTouchContactMetrics(e);
    if (!metrics.hasContactSize) return false;
    return isHardPalmContactMetrics(metrics);
  }

  function isStylusLikeTouchEvent(e) {
    if (String(e?.pointerType || "") !== "touch") return false;
    const metrics = getTouchContactMetrics(e);
    return (
      (metrics.hasContactSize && metrics.pointerWidth <= 26 && metrics.pointerHeight <= 26) ||
      (!metrics.hasContactSize) ||
      (metrics.pointerPressure > 0 && metrics.pointerWidth <= 34 && metrics.pointerHeight <= 34)
    );
  }

  function isStylusLikeTouchEventStrict(e) {
    if (String(e?.pointerType || "") !== "touch") return false;
    const metrics = getTouchContactMetrics(e);
    return (
      (metrics.hasContactSize && metrics.pointerWidth <= 26 && metrics.pointerHeight <= 26) ||
      (metrics.pointerPressure > 0 && (!metrics.hasContactSize || (metrics.pointerWidth <= 34 && metrics.pointerHeight <= 34)))
    );
  }

  function isPreciseStylusLikeTouchEvent(e) {
    if (String(e?.pointerType || "") !== "touch") return false;
    const metrics = getTouchContactMetrics(e);
    return (
      (metrics.hasContactSize && metrics.largestContactSide <= 14) ||
      (!metrics.hasContactSize && metrics.pointerPressure > 0) ||
      (metrics.pointerPressure > 0 && metrics.largestContactSide <= 22)
    );
  }

  function shouldIgnorePointerStart(e) {
    const pointerType = String(e?.pointerType || "mouse");
    const pointerWidth = Number(e?.width || 0);
    const pointerHeight = Number(e?.height || 0);
    const pointerPressure = Number(e?.pressure || 0);
    const hasContactSize = pointerWidth > 0 && pointerHeight > 0;
    const metrics = getTouchContactMetrics(e);
    const isFreehandTool = tool === "pen" || tool === "highlighter";
    const isEraserTool = tool === "eraser";
    const isDrawingTouchTool = isFreehandTool || isEraserTool;
    const isHardPalmContact = pointerType === "touch" && hasContactSize && isHardPalmContactMetrics(metrics);
    const isSoftPalmContact = pointerType === "touch" && hasContactSize && isSoftPalmContactMetrics(metrics);
    const stylusLikeTouch =
      pointerType === "touch" && (
        // Część tabletów raportuje rysik jako touch o małym polu kontaktu.
        (hasContactSize && pointerWidth <= 26 && pointerHeight <= 26) ||
        // Inne zwracają touch z zerowym contact size.
        (!hasContactSize) ||
        // Dodatkowy fallback dla urządzeń, które raportują pressure przy rysiku.
        (pointerPressure > 0 && pointerWidth <= 34 && pointerHeight <= 34)
      );
    // Wersja rygorystyczna dla trybu rysika: brak rozmiaru kontaktu bez pressure
    // nie jest traktowany jako rysik — dłoń bez danych geometrycznych jest taka sama.
    const stylusLikeTouchStrict =
      pointerType === "touch" && (
        (hasContactSize && pointerWidth <= 26 && pointerHeight <= 26) ||
        (pointerPressure > 0 && (!hasContactSize || (pointerWidth <= 34 && pointerHeight <= 34)))
      );

    if (pointerType === "pen") {
      lastPenAtRef.current = Date.now();
    }
    const penRecentlyActive = Date.now() - Number(lastPenAtRef.current || 0) < 2000;

    const shouldRejectHardPalmContact =
      isHardPalmContact &&
      (
        !isDrawingTouchTool ||
        stylusOnly ||
        penRecentlyActive ||
        activePointerIdRef.current !== null ||
        e?.isPrimary === false
      );

    if (shouldRejectHardPalmContact) {
      logFreehandDebug({
        phase: "ignore_start",
        reason: "hard_palm_contact",
        tool,
        pointerType,
        pointerWidth,
        pointerHeight,
        pointerPressure,
        isPrimary: e?.isPrimary,
      });
      return true;
    }
    if (!isDrawingTouchTool && tool !== "select" && isSoftPalmContact) return true;
    // W trybie rysika używamy rygorystycznego filtru: touch bez rozmiaru kontaktu i bez pressure
    // traktujemy jako dłoń, nie rysik.
    if (stylusOnly && pointerType === "touch" && !(stylusLikeTouchStrict || isStylusLikeTouchEventStrict(e))) return true;
    // Palm-rejection oparta o "pen recently active" bywa zbyt agresywna na części urządzeń
    // raportujących rysik jako touch. Zostawiamy ją tylko dla trybu rysika.
    if (stylusOnly && pointerType === "touch" && penRecentlyActive && !(stylusLikeTouchStrict || isStylusLikeTouchEventStrict(e))) return true;
    // Opcja C: dłoń oparcia tworzy drugi jednoczesny dotyk — odrzucamy go dla narzędzi rysujących.
    if (isDrawingTouchTool && pointerType === "touch" && touchPointsRef.current.size > 1) return true;
    if (pointerType === "touch" && e?.isPrimary === false && !stylusLikeTouch && activePointerIdRef.current !== null) return true;
    if (activePointerIdRef.current !== null) {
      const activeType = String(activePointerTypeRef.current || "");
      const activeAgeMs = Date.now() - Number(activePointerStartedAtRef.current || 0);
      if (
        isDrawingTouchTool &&
        pointerType === "touch" &&
        e?.isPrimary !== false &&
        activeType === "touch" &&
        (!isDrawingRef.current || activeAgeMs > 70)
      ) {
        if (draftStrokeRef.current || lastFreehandDraftRef.current) {
          finishStrokeSafely(null, "new_primary_touch_recovered_previous_touch");
        } else {
          releaseActivePointer();
        }
        return false;
      }
      // Jeżeli aktywny pointer to touch (np. dłoń), a wpada rysik,
      // to oddajemy priorytet rysikowi zamiast blokować start kreski.
      if (
        activeType === "touch" &&
        (pointerType === "pen" || stylusLikeTouch)
      ) {
        releaseActivePointer();
        return false;
      }
      logFreehandDebug({
        phase: "ignore_start",
        reason: "active_pointer_exists",
        tool,
        pointerType,
        activeType,
        activePointer: activePointerIdRef.current,
        incomingPointer: e?.pointerId,
        pointerWidth,
        pointerHeight,
        isPrimary: e?.isPrimary,
      });
      return true;
    }
    return false;
  }

  function isActivePointerEvent(e) {
    if (activePointerIdRef.current === null) return false;
    return Number(e?.pointerId) === Number(activePointerIdRef.current);
  }

  function claimActivePointer(e) {
    activePointerIdRef.current = Number(e?.pointerId);
    activePointerTypeRef.current = String(e?.pointerType || "mouse");
    activePointerIsStylusLikeTouchRef.current = isPreciseStylusLikeTouchEvent(e);
    activePointerStartedAtRef.current = Date.now();
    activePointerMaxPressureRef.current = Math.max(0, Number(e?.pressure || 0));
  }

  function releaseActivePointer() {
    cancelHoldSwitchSession();
    suppressPointerCommitRef.current = null;
    activePointerIdRef.current = null;
    activePointerTypeRef.current = null;
    activePointerIsStylusLikeTouchRef.current = false;
    activePointerStartedAtRef.current = 0;
    activePointerMaxPressureRef.current = 0;
  }

  function capturePointerOnCanvas(e) {
    const canvas = e?.currentTarget;
    const pointerId = Number(e?.pointerId);
    if (!canvas || !Number.isFinite(pointerId)) return;
    try {
      if (typeof canvas.setPointerCapture === "function") {
        canvas.setPointerCapture(pointerId);
      }
    } catch {
      // ignore unsupported capture environments
    }
  }

  function releasePointerOnCanvas(e) {
    const canvas = e?.currentTarget;
    const pointerId = Number(e?.pointerId);
    if (!canvas || !Number.isFinite(pointerId)) return;
    try {
      if (typeof canvas.releasePointerCapture === "function" && canvas.hasPointerCapture?.(pointerId)) {
        canvas.releasePointerCapture(pointerId);
      }
    } catch {
      // ignore unsupported capture environments
    }
  }

  function hideStylusHoverCursor() {
    if (stylusHoverCursorRef.current) stylusHoverCursorRef.current.style.display = "none";
  }

  function updateStylusHoverCursor(point, e) {
    const pointerType = String(e?.pointerType || "");
    setPenPointerActive(pointerType === "pen" && showStylusCursor);
    if (
      pointerType !== "pen" ||
      !showStylusCursor ||
      !point ||
      Number(e?.buttons || 0) !== 0 ||
      disabled ||
      readOnly
    ) {
      hideStylusHoverCursor();
      return;
    }
    const cursor = stylusHoverCursorRef.current;
    if (!cursor) return;
    cursor.style.left = `${(Number(point.x || 0) / WIDTH) * 100}%`;
    cursor.style.top = `${(Number(point.y || 0) / Math.max(1, totalHeight)) * 100}%`;
    cursor.style.display = "flex";
  }

  function emitCursor(point, e = null) {
    if (!onCursorMove) return;
    const pointerType = String(e?.pointerType || activePointerTypeRef.current || "mouse");
    if (pointerType !== "mouse" && pointerType !== "pen") return;
    if (pointerType === "pen" && !showStylusCursor) return;
    onCursorMove(point);
  }

  function isTemporaryMousePan(e) {
    return String(e?.pointerType || "mouse") === "mouse" && (e?.button === 1 || e?.button === 2);
  }

  function registerTouchPoint(e) {
    if (String(e?.pointerType || "") !== "touch") return;
    if (isHardPalmTouchEvent(e)) {
      touchPointsRef.current.delete(Number(e.pointerId));
      return;
    }
    touchPointsRef.current.set(Number(e.pointerId), {
      x: Number(e.clientX || 0),
      y: Number(e.clientY || 0),
    });
  }

  function removeTouchPoint(e) {
    if (String(e?.pointerType || "") !== "touch") return;
    touchPointsRef.current.delete(Number(e.pointerId));
  }

  function getTouchPair() {
    const values = Array.from(touchPointsRef.current.values());
    if (values.length < 2) return null;
    return [values[0], values[1]];
  }

  function getNativeTouchPair(event) {
    const touches = Array.from(event?.touches || []);
    if (touches.length < 2) return null;
    return touches.slice(0, 2).map((touch) => ({
      x: Number(touch.clientX || 0),
      y: Number(touch.clientY || 0),
    }));
  }

  function resetForTouchGesture() {
    isDrawingRef.current = false;
    setIsDrawing(false);
    cancelDraftFrame();
    setDraftStroke(null);
    draftStrokeRef.current = null;
    draftPointsRef.current = null;
    lastFreehandDraftRef.current = null;
    onDraftChangeRef.current?.(null);
    setErasePoint(null);
    updateEraseCandidateIds([]);
    panSessionRef.current = null;
    setSelectionTransform(null);
    setImageTransform(null);
    setCropSession(null);
    setRotateSession(null);
    setMarqueeSelection(null);
    pendingMarqueeRef.current = null;
    releaseActivePointer();
  }

  function startTouchGestureFromPair(pair) {
    const el = scrollerRef.current;
    if (!pair || !el) return false;
    const [a, b] = pair;
    const centerX = (a.x + b.x) / 2;
    const centerY = (a.y + b.y) / 2;

    resetForTouchGesture();

    touchGestureRef.current = {
      startCenterX: centerX,
      startCenterY: centerY,
      startScrollLeft: Number(el.scrollLeft || 0),
      startScrollTop: Number(el.scrollTop || 0),
    };
    setTouchGestureActive(true);
    return true;
  }

  function startTouchGesture() {
    const hasActiveFreehandDraft =
      (tool === "pen" || tool === "highlighter") &&
      !!(draftStrokeRef.current || lastFreehandDraftRef.current || draftStroke);
    if (hasActiveFreehandDraft) {
      const activeType = String(activePointerTypeRef.current || "");
      const activeAgeMs = Date.now() - Number(activePointerStartedAtRef.current || 0);
      const activeDraftPoints = Array.isArray(draftPointsRef.current)
        ? draftPointsRef.current.length
        : (Array.isArray(draftStrokeRef.current?.points) ? draftStrokeRef.current.points.length : 0);
      const activeLooksLikeStylus =
        activeType === "pen" ||
        !!activePointerIsStylusLikeTouchRef.current;
      if (activeLooksLikeStylus || activeDraftPoints > 1 || activeAgeMs > 140) return false;
    }

    const pair = getTouchPair();
    return startTouchGestureFromPair(pair);
  }

  function updateTouchGestureFromPair(pair) {
    const gesture = touchGestureRef.current;
    if (!gesture) return false;
    const el = scrollerRef.current;
    if (!pair || !el) return false;

    const [a, b] = pair;
    const centerX = (a.x + b.x) / 2;
    const centerY = (a.y + b.y) / 2;
    const dx = centerX - Number(gesture.startCenterX || 0);
    const dy = centerY - Number(gesture.startCenterY || 0);
    el.scrollLeft = Number(gesture.startScrollLeft || 0) - dx;
    el.scrollTop = Number(gesture.startScrollTop || 0) - dy;
    refreshCanvasRectCache();
    updateCurrentPage();
    return true;
  }

  function updateTouchGesture(e) {
    if (String(e?.pointerType || "") !== "touch") return false;
    return updateTouchGestureFromPair(getTouchPair());
  }

  function maybeStartOrUpdateNativeTouchGesture(event) {
    if (stylusOnly || tool !== "select") return false;
    const pair = getNativeTouchPair(event);
    if (!pair) return false;
    if (!touchGestureRef.current) {
      return startTouchGestureFromPair(pair);
    }
    return updateTouchGestureFromPair(pair);
  }

  function maybeStartOrUpdateTouchGesture(e) {
    if (String(e?.pointerType || "") !== "touch") return false;
    if (stylusOnly) return false;
    if (isHardPalmTouchEvent(e)) return false;
    if (tool !== "select" && !touchGestureRef.current) {
      return false;
    }
    if (tool === "select" && touchPointsRef.current.size >= 2) {
      pendingMarqueeRef.current = null;
      setMarqueeSelection(null);
      if (!touchGestureRef.current) {
        return startTouchGesture();
      }
      return updateTouchGesture(e);
    }
    if (activePointerIdRef.current !== null) {
      const activeType = String(activePointerTypeRef.current || "");
      const activeLooksLikeStylus =
        activeType === "pen" ||
        !!activePointerIsStylusLikeTouchRef.current;
      const activeAgeMs = Date.now() - Number(activePointerStartedAtRef.current || 0);
      const activeDraftPoints = Array.isArray(draftPointsRef.current)
        ? draftPointsRef.current.length
        : (Array.isArray(draftStrokeRef.current?.points) ? draftStrokeRef.current.points.length : 0);
      if (
        activeLooksLikeStylus ||
        (isDrawingRef.current && (activeDraftPoints > 1 || activeAgeMs > 140))
      ) {
        return false;
      }
    }
    if (touchPointsRef.current.size >= 2) {
      pendingMarqueeRef.current = null;
      setMarqueeSelection(null);
      if (!touchGestureRef.current) {
        return startTouchGesture();
      }
      return updateTouchGesture(e);
    }
    return false;
  }

  function stopTouchGestureIfNeeded() {
    if (touchPointsRef.current.size >= 2) return;
    if (touchGestureRef.current) {
      updateCurrentPageNow();
    }
    touchGestureRef.current = null;
    setTouchGestureActive(false);
  }

  function clearTouchGestureTracking(syncViewport = false) {
    touchPointsRef.current.clear();
    touchGestureRef.current = null;
    setTouchGestureActive(false);
    if (syncViewport) updateCurrentPageNow();
  }

  function readVisibleCanvasRectFromScrollerAt(el, scrollTopOverride = null) {
    if (!el || !displayHeight) return null;
    const scaleX = WIDTH / Math.max(1, displayWidth || WIDTH);
    const renderedPageHeightPx = Math.max(1, getRenderedPageHeightPx());
    const fallbackScaleY = totalHeight / Math.max(1, displayHeight || totalHeight);
    const scaleY = renderedPageHeightPx > 0 ? PAGE_HEIGHT / renderedPageHeightPx : fallbackScaleY;
    const scrollTop = scrollTopOverride === null || scrollTopOverride === undefined
      ? Number(el.scrollTop || 0)
      : Number(scrollTopOverride || 0);
    return {
      x: Math.max(0, Math.min(WIDTH, Number(el.scrollLeft || 0) * scaleX)),
      y: Math.max(0, Math.min(totalHeight, scrollTop * scaleY)),
      width: Math.max(1, Math.min(WIDTH, Number(el.clientWidth || 0) * scaleX)),
      height: Math.max(1, Math.min(totalHeight, Number(el.clientHeight || 0) * scaleY)),
    };
  }

  function readVisibleCanvasRectFromScroller(el) {
    return readVisibleCanvasRectFromScrollerAt(el);
  }

  function applyVisibleCanvasRect(nextRect) {
    if (!nextRect) return false;
    setVisibleCanvasRect((prev) => {
      const dx = Math.abs(Number(prev?.x || 0) - nextRect.x);
      const dy = Math.abs(Number(prev?.y || 0) - nextRect.y);
      const dw = Math.abs(Number(prev?.width || 0) - nextRect.width);
      const dh = Math.abs(Number(prev?.height || 0) - nextRect.height);
      if (dx < 0.5 && dy < 0.5 && dw < 0.5 && dh < 0.5) return prev;
      return nextRect;
    });
    return true;
  }

  function forceRenderWindowForScrollTop(scrollTop) {
    return applyVisibleCanvasRect(readVisibleCanvasRectFromScrollerAt(scrollerRef.current, scrollTop));
  }

  function getRenderedPageHeightPx() {
    const widthBasedHeight = Number(displayWidth || 0) > 0
      ? (Number(displayWidth || WIDTH) * PAGE_HEIGHT) / WIDTH
      : 0;
    if (Number.isFinite(widthBasedHeight) && widthBasedHeight > 0) return widthBasedHeight;
    return Number(displayHeight || 0) / Math.max(1, pageCount);
  }

  function scrollToPageTop(targetPage, behavior = "smooth") {
    const el = scrollerRef.current;
    if (!el || !displayHeight) return false;
    const page = Math.max(0, Math.min(pageCount - 1, Number(targetPage || 0)));
    const pageHeightPx = Math.max(1, getRenderedPageHeightPx());
    const maxTop = Math.max(0, Number(el.scrollHeight || 0) - Number(el.clientHeight || 0));
    const targetTop = Math.max(0, Math.min(maxTop, page * pageHeightPx));
    forceRenderWindowForScrollTop(targetTop);
    el.scrollTo({ top: targetTop, behavior });
    scheduleProgrammaticScrollSync();
    return Math.abs(Number(el.scrollTop || 0) - targetTop) < 2 || behavior === "smooth";
  }



  function scheduleProgrammaticScrollSync() {
    const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    const syncDurationMs = 980;
    updateCurrentPageNow();

    if (programmaticScrollSyncFrameRef.current) {
      cancelAnimationFrame(programmaticScrollSyncFrameRef.current);
      programmaticScrollSyncFrameRef.current = null;
    }
    if (programmaticScrollSyncTimerRef.current) {
      clearTimeout(programmaticScrollSyncTimerRef.current);
      programmaticScrollSyncTimerRef.current = null;
    }

    const tick = () => {
      updateCurrentPageNow();
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now - startedAt < syncDurationMs) {
        programmaticScrollSyncFrameRef.current = requestAnimationFrame(tick);
      } else {
        programmaticScrollSyncFrameRef.current = null;
      }
    };

    programmaticScrollSyncFrameRef.current = requestAnimationFrame(tick);
    programmaticScrollSyncTimerRef.current = setTimeout(() => {
      programmaticScrollSyncTimerRef.current = null;
      if (programmaticScrollSyncFrameRef.current) {
        cancelAnimationFrame(programmaticScrollSyncFrameRef.current);
        programmaticScrollSyncFrameRef.current = null;
      }
      updateCurrentPageNow();
    }, syncDurationMs + 120);
  }

  function prepareForProgrammaticScroll() {
    clearTouchGestureTracking(false);
    panSessionRef.current = null;
    releaseActivePointer();
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    const scroller = scrollerRef.current;
    if (!canvas && !scroller) return undefined;

    const blockNativeCanvasTouch = (event) => {
      if (!event?.cancelable) return;
      const target = event.target;
      if (
        target &&
        canvas &&
        scroller &&
        !canvas.contains(target) &&
        !scroller.contains(target)
      ) {
        return;
      }
      if (maybeStartOrUpdateNativeTouchGesture(event)) {
        event.preventDefault();
        return;
      }
      if (event.type === "touchstart") {
        return;
      }
      event.preventDefault();
    };
    const endNativeCanvasTouch = () => {
      if (tool === "select" && activePointerIdRef.current !== null) {
        finishStrokeSafely(null, "native_touch_end_select");
      }
      clearTouchGestureTracking(true);
    };

    canvas?.addEventListener("touchstart", blockNativeCanvasTouch, { passive: false, capture: true });
    canvas?.addEventListener("touchmove", blockNativeCanvasTouch, { passive: false, capture: true });
    canvas?.addEventListener("touchend", endNativeCanvasTouch, { passive: true, capture: true });
    canvas?.addEventListener("touchcancel", endNativeCanvasTouch, { passive: true, capture: true });
    scroller?.addEventListener("touchstart", blockNativeCanvasTouch, { passive: false, capture: true });
    scroller?.addEventListener("touchmove", blockNativeCanvasTouch, { passive: false, capture: true });
    scroller?.addEventListener("touchend", endNativeCanvasTouch, { passive: true, capture: true });
    scroller?.addEventListener("touchcancel", endNativeCanvasTouch, { passive: true, capture: true });
    if (typeof document !== "undefined") {
      document.addEventListener("touchmove", blockNativeCanvasTouch, { passive: false, capture: true });
      document.addEventListener("touchend", endNativeCanvasTouch, { passive: true, capture: true });
      document.addEventListener("touchcancel", endNativeCanvasTouch, { passive: true, capture: true });
    }
    return () => {
      canvas?.removeEventListener("touchstart", blockNativeCanvasTouch, { capture: true });
      canvas?.removeEventListener("touchmove", blockNativeCanvasTouch, { capture: true });
      canvas?.removeEventListener("touchend", endNativeCanvasTouch, { capture: true });
      canvas?.removeEventListener("touchcancel", endNativeCanvasTouch, { capture: true });
      scroller?.removeEventListener("touchstart", blockNativeCanvasTouch, { capture: true });
      scroller?.removeEventListener("touchmove", blockNativeCanvasTouch, { capture: true });
      scroller?.removeEventListener("touchend", endNativeCanvasTouch, { capture: true });
      scroller?.removeEventListener("touchcancel", endNativeCanvasTouch, { capture: true });
      if (typeof document !== "undefined") {
        document.removeEventListener("touchmove", blockNativeCanvasTouch, { capture: true });
        document.removeEventListener("touchend", endNativeCanvasTouch, { capture: true });
        document.removeEventListener("touchcancel", endNativeCanvasTouch, { capture: true });
      }
    };
  }, [stylusOnly, tool]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const measure = (rawWidth) => {
      if (!rawWidth) return;

      // Tlumimy wahania szerokosci rzedu szerokosci paska przewijania (~<=20px).
      // Snapujemy do ostatniej stabilnej szerokosci zamiast reagowac na kazda
      // mikro-zmiane, ale wieksze (realne) zmiany layoutu nadal przepuszczamy, a
      // pozostale wejscia (wysokosc/zoom) liczymy normalnie - wiec to nie blokuje
      // zwyklego responsywnego przeliczania.
      const SCROLLBAR_JITTER_PX = 20;
      const prevWidth = lastMeasuredWidthRef.current;
      let width = rawWidth;
      if (prevWidth > 0 && Math.abs(rawWidth - prevWidth) <= SCROLLBAR_JITTER_PX) {
        width = prevWidth;
      } else {
        lastMeasuredWidthRef.current = rawWidth;
      }

      const availableWidth = Math.max(280, Math.round(width - 8));
      const cappedAvailableWidth =
        typeof maxDisplayWidth === "number" && Number.isFinite(maxDisplayWidth)
          ? Math.min(availableWidth, Math.max(280, Math.round(maxDisplayWidth)))
          : availableWidth;

      const controlsOffset = topControlsDetached ? topControlsHeight + 12 : 0;
      const visibleViewportHeight = Math.max(
        220,
        viewportHeightPx - Math.max(0, topOverlaySpace) - Math.max(0, controlsOffset)
      );
      const viewportAspect = cappedAvailableWidth / Math.max(1, visibleViewportHeight);
      // On wide fullscreen displays, fit the entire page in both dimensions.
      const shouldMaximizeWidth = (!fullscreenActive && preferFixedWidth) || isMobileNarrow || viewportAspect < 1.15;
      // Margines bezpieczenstwa przy dopasowaniu strony do wysokosci. Bez niego
      // wysokosc dopasowanej strony wychodzi co do piksela rowna wysokosci
      // viewportu (maxHeight scrollera). Przy ulamkowym devicePixelRatio (np.
      // skalowanie monitora 125%/150%) zaokraglenie sub-pikselowe sprawia, ze
      // scrollHeight raz jest rowny clientHeight, a raz o ~0.5px wiekszy - przez
      // co pionowy pasek przewijania miga ~kilka razy na sekunde (tylko w Chrome,
      // bo Edge ma paski nakladkowe). Kilka px zapasu trzyma strone pewnie ponizej
      // progu przewijania i eliminuje miganie.
      const SCROLL_FIT_SAFETY_PX = 6;
      const fitPageWidthAt100 = Math.max(
        280,
        Math.floor(((visibleViewportHeight - SCROLL_FIT_SAFETY_PX) * WIDTH) / PAGE_HEIGHT)
      );
      const preferredBaseWidth = cappedAvailableWidth;
      const baseWidthAt100 = shouldMaximizeWidth
        ? preferredBaseWidth
        : Math.min(preferredBaseWidth, fitPageWidthAt100);
      const baseWidth = baseWidthAt100;
      const zoomScale = Math.max(1, Number(zoomPercent || 100) / 100);
      const nextWidth = Math.round(baseWidth * zoomScale);

      setDisplayWidth(nextWidth);
    };

    const ro = new ResizeObserver(([entry]) => {
      measure(entry.contentRect.width || 0);
    });
    ro.observe(el);
    measure(el.getBoundingClientRect().width || 0);

    return () => ro.disconnect();
  }, [
    fullscreenActive,
    totalHeight,
    zoomPercent,
    maxDisplayWidth,
    topOverlaySpace,
    topControlsDetached,
    topControlsHeight,
    viewportHeightPx,
    viewportHeightOverride,
    preferFixedWidth,
  ]);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx || ctx.isContextLost?.()) return;
    const heavyBoardLoad =
      elements.length >= 520 ||
      visibleElements.length >= 260;
    const lowPowerRender =
      heavyBoardLoad ||
      (isMobileNarrow && elements.length >= 260) ||
      (isLowMemoryDevice && (elements.length >= 220 || pageCount >= 10));

    const drawBaseElement = (drawCtx, rawElement, index, element) => {
      if (element?.type === "image") {
        const imageId = getImageId(rawElement, index);
        const rotation = normalizeRotation(element.rotation);
        const x = Number(element.x ?? 0);
        const y = Number(element.y ?? 0);
        const width = Number(element.width ?? 0);
        const height = Number(element.height ?? 0);
        const crop = element.crop;
        const rect = getDisplayedImageRect({ ...element, x, y, width, height }, crop);
        const src = String(element.src || "");
        if (!src) return;
        let cached = imageCacheRef.current.get(src);
        if (!cached) {
          const img = new Image();
          cached = { img, loaded: false };
          img.onload = () => {
            cached.loaded = true;
            setImageRefreshTick((tick) => tick + 1);
          };
          img.src = src;
          imageCacheRef.current.set(src, cached);
        } else {
          imageCacheRef.current.delete(src);
          imageCacheRef.current.set(src, cached);
        }
        if (cached.loaded) {
          const naturalW = Math.max(1, Number(element.naturalWidth || cached.img.naturalWidth || width || 1));
          const naturalH = Math.max(1, Number(element.naturalHeight || cached.img.naturalHeight || height || 1));
          const normalizedCrop = normalizeCrop(crop);
          const sx = naturalW * normalizedCrop.left;
          const sy = naturalH * normalizedCrop.top;
          const sw = Math.max(1, naturalW * (normalizedCrop.right - normalizedCrop.left));
          const sh = Math.max(1, naturalH * (normalizedCrop.bottom - normalizedCrop.top));
          const centerX = rect.x + rect.width / 2;
          const centerY = rect.y + rect.height / 2;
          const rad = (rotation * Math.PI) / 180;
          drawCtx.save();
          drawCtx.translate(centerX, centerY);
          if (rotation !== 0) drawCtx.rotate(rad);
          drawCtx.drawImage(
            cached.img,
            sx,
            sy,
            sw,
            sh,
            -rect.width / 2,
            -rect.height / 2,
            rect.width,
            rect.height
          );
          drawCtx.restore();
        } else {
          drawCtx.save();
          drawCtx.fillStyle = themeMode === "light" ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)";
          drawCtx.fillRect(rect.x, rect.y, rect.width, rect.height);
          drawCtx.restore();
        }
        return;
      }
      if (element?.type === "stroke") {
        drawStroke(drawCtx, element, { strokeStyle: resolveStrokeColor(element.color, themeMode, boardSurface) });
      }
    };

    const canUsePageCache =
      !selectionTransform &&
      !imageTransform &&
      !cropSession &&
      !rotateSession &&
      !shapeSession &&
      !marqueeSelection &&
      !selectedElementIds.length &&
      !selectedImageId &&
      eraseCandidateIdSet.size === 0 &&
      tool !== "eraser" &&
      pageCount <= 40 &&
      elements.length <= 2000;

    const cacheToken = [
      boardVersion,
      pageCount,
      themeMode,
      boardSurface,
      gridEnabled ? 1 : 0,
      elements.length,
      imageRefreshTick,
      lowPowerRender ? 1 : 0,
    ].join(":");
    if (pageRenderCacheTokenRef.current !== cacheToken) {
      pageRenderCacheTokenRef.current = cacheToken;
      clearCanvasCache(pageRenderCacheRef.current);
    }

    const getPageCacheCanvas = (page) => {
      const pageDistance = Math.abs(page - currentPage);
      const pageLodBucket = pageDistance <= 1 ? "near" : pageDistance <= 3 ? "mid" : "far";
      const key = `${page}:${lowPowerRender ? 1 : 0}:${pageLodBucket}`;
      const existing = pageRenderCacheRef.current.get(key);
      if (existing && isCanvasUsable(existing)) {
        // Simple LRU refresh.
        pageRenderCacheRef.current.delete(key);
        pageRenderCacheRef.current.set(key, existing);
        return existing;
      }

      if (existing) {
        releaseCanvas(existing);
        pageRenderCacheRef.current.delete(key);
      }
      // Evict before allocation to avoid transient memory spikes during scrolling.
      while (pageRenderCacheRef.current.size >= maxPageCacheCanvases) {
        const oldestKey = pageRenderCacheRef.current.keys().next().value;
        releaseCanvas(pageRenderCacheRef.current.get(oldestKey));
        pageRenderCacheRef.current.delete(oldestKey);
      }
      const pageTop = page * PAGE_HEIGHT;
      const pageCanvas = createRenderCanvas();
      pageCanvas.width = WIDTH;
      pageCanvas.height = PAGE_HEIGHT;
      const pageCtx = pageCanvas.getContext("2d");
      if (!pageCtx || pageCtx.isContextLost?.()) {
        releaseCanvas(pageCanvas);
        return null;
      }
      pageCtx.clearRect(0, 0, WIDTH, PAGE_HEIGHT);
      pageCtx.save();
      pageCtx.translate(0, -pageTop);
      drawPageGuides(
        pageCtx,
        pageCount,
        themeMode,
        boardSurface,
        gridEnabled,
        { top: pageTop, bottom: pageTop + PAGE_HEIGHT },
        {
          lowPower: lowPowerRender,
          gridMode: "full",
          currentPage,
          pagePaddingBefore: effectivePageBufferBefore,
          pagePaddingAfter: effectivePageBufferAfter,
        }
      );
      const pageEntries = elementsByPageBucket.get(page) || [];
      pageEntries.forEach((entry) => {
        const index = Number(entry?.index);
        if (!Number.isInteger(index)) return;
        const rawElement = elements[index];
        if (!rawElement) return;
        drawBaseElement(pageCtx, rawElement, index, rawElement);
      });
      pageCtx.restore();
      pageRenderCacheRef.current.set(key, pageCanvas);
      while (pageRenderCacheRef.current.size > maxPageCacheCanvases) {
        const oldestKey = pageRenderCacheRef.current.keys().next().value;
        if (!oldestKey) break;
        releaseCanvas(pageRenderCacheRef.current.get(oldestKey));
        pageRenderCacheRef.current.delete(oldestKey);
      }
      return pageCanvas;
    };

    const drawDynamicOverlays = (drawCtx) => {
      for (const remoteDraft of remoteDrafts || []) {
        const draftBounds = getElementBounds(remoteDraft);
        if (draftBounds && !rectsIntersect(draftBounds, renderRect)) continue;
        drawStroke(drawCtx, remoteDraft, { strokeStyle: resolveStrokeColor(remoteDraft.color, themeMode, boardSurface) });
      }
    };

    const baseSnapshotToken = `${cacheToken}:${canUsePageCache ? 1 : 0}`;
    const snapshot = viewportBaseSnapshotRef.current;
    const canUseFastDraftSnapshot =
      !!snapshot &&
      isCanvasUsable(snapshot.canvas) &&
      snapshot.token === baseSnapshotToken &&
      snapshot.x === renderRect.x &&
      snapshot.y === renderRect.y &&
      snapshot.width === renderRect.width &&
      snapshot.height === renderRect.height &&
      isDrawing &&
      (tool === "pen" || tool === "highlighter") &&
      !erasePoint &&
      (!remoteDrafts || remoteDrafts.length === 0) &&
      !shapeSession;

    const scaleX = canvasPixelWidth / Math.max(1, Number(renderRect.width || WIDTH));
    const scaleY = canvasPixelHeight / Math.max(1, Number(renderRect.height || PAGE_HEIGHT));

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scaleX, 0, 0, scaleY, -Number(renderRect.x || 0) * scaleX, -Number(renderRect.y || 0) * scaleY);

    if (canUseFastDraftSnapshot) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(renderRect.x, renderRect.y, renderRect.width, renderRect.height);
      ctx.clip();
      ctx.clearRect(renderRect.x, renderRect.y, renderRect.width, renderRect.height);
      ctx.drawImage(
        snapshot.canvas,
        0,
        0,
        snapshot.width,
        snapshot.height,
        snapshot.x,
        snapshot.y,
        snapshot.width,
        snapshot.height
      );
      drawDynamicOverlays(ctx);
      ctx.restore();
      canvasHasRenderedRef.current = true;
      return;
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(renderRect.x, renderRect.y, renderRect.width, renderRect.height);
    ctx.clip();
    ctx.clearRect(renderRect.x, renderRect.y, renderRect.width, renderRect.height);

    if (canUsePageCache) {
      const visiblePageStart = Math.max(0, Math.floor(renderViewport.top / PAGE_HEIGHT) - effectivePageBufferBefore);
      const visiblePageEnd = Math.min(pageCount - 1, Math.ceil(renderViewport.bottom / PAGE_HEIGHT) + effectivePageBufferAfter);
      for (let page = visiblePageStart; page <= visiblePageEnd; page += 1) {
        const pageTop = page * PAGE_HEIGHT;
        const pageBottom = pageTop + PAGE_HEIGHT;
        const drawTop = Math.max(pageTop, renderRect.y);
        const drawBottom = Math.min(pageBottom, renderRect.y + renderRect.height);
        const drawHeight = Math.max(0, drawBottom - drawTop);
        if (drawHeight <= 0) continue;
        const sourceY = drawTop - pageTop;
        const cacheCanvas = getPageCacheCanvas(page);
        if (cacheCanvas) {
          ctx.drawImage(
            cacheCanvas,
            0,
            sourceY,
            WIDTH,
            drawHeight,
            0,
            drawTop,
            WIDTH,
            drawHeight
          );
        } else {
          drawPageGuides(ctx, pageCount, themeMode, boardSurface, gridEnabled, {
            top: drawTop,
            bottom: drawBottom,
          });
          (elementsByPageBucket.get(page) || []).forEach(({ index }) => {
            const element = elements[index];
            if (element) drawBaseElement(ctx, element, index, element);
          });
        }
      }
    } else {
      drawPageGuides(ctx, pageCount, themeMode, boardSurface, gridEnabled, renderViewport, {
        lowPower: lowPowerRender,
        gridMode: "full",
        currentPage,
        pagePaddingBefore: effectivePageBufferBefore,
        pagePaddingAfter: effectivePageBufferAfter,
      });

      visibleElements.forEach(({ rawElement, index, selectionKey, element }) => {
        if (element?.type === "image") {
          const imageId = getImageId(rawElement, index);
          const transform = imageTransform?.id === imageId ? imageTransform : null;
          const rotation = rotateSession?.id === imageId
            ? normalizeRotation(rotateSession.rotation)
            : normalizeRotation(element.rotation);
          const x = Number(transform?.x ?? element.x ?? 0);
          const y = Number(transform?.y ?? element.y ?? 0);
          const width = Number(transform?.width ?? element.width ?? 0);
          const height = Number(transform?.height ?? element.height ?? 0);
          const crop = cropSession?.id === imageId ? cropSession.crop : element.crop;
          const rect = getDisplayedImageRect({ ...element, x, y, width, height }, crop);
          const src = String(element.src || "");
          if (src) {
            let cached = imageCacheRef.current.get(src);
            if (!cached) {
              const img = new Image();
              cached = { img, loaded: false };
              img.onload = () => {
                cached.loaded = true;
                setImageRefreshTick((tick) => tick + 1);
              };
              img.src = src;
              imageCacheRef.current.set(src, cached);
            } else {
              imageCacheRef.current.delete(src);
              imageCacheRef.current.set(src, cached);
            }
            if (cached.loaded) {
              const naturalW = Math.max(1, Number(element.naturalWidth || cached.img.naturalWidth || width || 1));
              const naturalH = Math.max(1, Number(element.naturalHeight || cached.img.naturalHeight || height || 1));
              const normalizedCrop = normalizeCrop(crop);
              const sx = naturalW * normalizedCrop.left;
              const sy = naturalH * normalizedCrop.top;
              const sw = Math.max(1, naturalW * (normalizedCrop.right - normalizedCrop.left));
              const sh = Math.max(1, naturalH * (normalizedCrop.bottom - normalizedCrop.top));
              const drawImageInRect = (sourceX, sourceY, sourceW, sourceH, targetRect) => {
                const centerX = targetRect.x + targetRect.width / 2;
                const centerY = targetRect.y + targetRect.height / 2;
                const rad = (rotation * Math.PI) / 180;
                ctx.save();
                ctx.translate(centerX, centerY);
                if (rotation !== 0) ctx.rotate(rad);
                ctx.drawImage(
                  cached.img,
                  sourceX,
                  sourceY,
                  sourceW,
                  sourceH,
                  -targetRect.width / 2,
                  -targetRect.height / 2,
                  targetRect.width,
                  targetRect.height
                );
                ctx.restore();
              };

              if (cropSession?.id === imageId) {
                ctx.save();
                ctx.globalAlpha = 0.22;
                drawImageInRect(0, 0, naturalW, naturalH, { x, y, width, height });
                ctx.restore();

                ctx.save();
                ctx.fillStyle = themeMode === "light" ? "rgba(15,23,42,0.16)" : "rgba(2,6,23,0.42)";
                ctx.beginPath();
                ctx.rect(x, y, width, height);
                ctx.rect(rect.x, rect.y, rect.width, rect.height);
                ctx.fill("evenodd");
                ctx.restore();
              }

              drawImageInRect(sx, sy, sw, sh, rect);
            } else {
              ctx.save();
              ctx.fillStyle = themeMode === "light" ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)";
              ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
              ctx.restore();
            }
          }

          if (selectedImageId === imageId || selectedElementIdSet.has(selectionKey)) {
            ctx.save();
            ctx.strokeStyle = "#f59e0b";
            ctx.lineWidth = 2;
            if (!lowPowerRender) ctx.setLineDash([10, 8]);
            if (rotation !== 0) {
              const centerX = rect.x + rect.width / 2;
              const centerY = rect.y + rect.height / 2;
              const rad = (rotation * Math.PI) / 180;
              ctx.translate(centerX, centerY);
              ctx.rotate(rad);
              ctx.strokeRect(-rect.width / 2, -rect.height / 2, rect.width, rect.height);
            } else {
              ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
            }
            if (!lowPowerRender) ctx.setLineDash([]);
            if (cropSession?.id === imageId) {
              const handles = [
                [rect.x, rect.y + rect.height / 2],
                [rect.x + rect.width, rect.y + rect.height / 2],
                [rect.x + rect.width / 2, rect.y],
                [rect.x + rect.width / 2, rect.y + rect.height],
              ];
              ctx.fillStyle = "#f59e0b";
              handles.forEach(([handleX, handleY]) => {
                ctx.fillRect(handleX - 7, handleY - 7, 14, 14);
              });
            } else {
              ctx.fillStyle = "#f59e0b";
              ctx.fillRect(rect.x + rect.width - 12, rect.y + rect.height - 12, 14, 14);
            }
            ctx.restore();
          }
          return;
        }

        if (element?.type !== "stroke") return;

        const strokeId = getStrokeId(rawElement, index);
        if (eraseCandidateIdSet.has(strokeId)) {
          drawStroke(ctx, element, {
            alpha: 0.25,
            strokeStyle: "#f97316",
            lineWidth: Math.max(Number(element.width || 3), ERASER_RADIUS),
            dashed: !lowPowerRender,
          });
          return;
        }

        drawStroke(ctx, element, { strokeStyle: resolveStrokeColor(element.color, themeMode, boardSurface) });

        if (selectedElementIdSet.has(selectionKey)) {
          const bounds = getStrokeBounds(element);
          if (bounds) {
            ctx.save();
            ctx.strokeStyle = "#f59e0b";
            ctx.lineWidth = 2;
            if (!lowPowerRender) ctx.setLineDash([10, 8]);
            ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
            if (!lowPowerRender) ctx.setLineDash([]);
            ctx.restore();
          }
        }
      });
    }

    const shouldCaptureBaseSnapshot =
      !touchGestureActive &&
      !isDrawing &&
      !erasePoint &&
      (!remoteDrafts || remoteDrafts.length === 0) &&
      !shapeSession &&
      !marqueeSelection &&
      Math.min(scaleX, scaleY) >= 0.999;

    if (shouldCaptureBaseSnapshot) {
      let snapshotCanvas = viewportBaseSnapshotRef.current?.canvas || null;
      if (!snapshotCanvas || !isCanvasUsable(snapshotCanvas) || snapshotCanvas.width !== renderRect.width || snapshotCanvas.height !== renderRect.height) {
        releaseCanvas(snapshotCanvas);
        snapshotCanvas = createRenderCanvas();
        snapshotCanvas.width = renderRect.width;
        snapshotCanvas.height = renderRect.height;
      }
      const snapshotCtx = snapshotCanvas.getContext("2d");
      if (!snapshotCtx || snapshotCtx.isContextLost?.()) {
        releaseCanvas(snapshotCanvas);
        viewportBaseSnapshotRef.current = null;
        drawDynamicOverlays(ctx);
        ctx.restore();
        canvasHasRenderedRef.current = true;
        return;
      }
      snapshotCtx.clearRect(0, 0, renderRect.width, renderRect.height);
      snapshotCtx.drawImage(
        canvas,
        0,
        0,
        canvas.width,
        canvas.height,
        0,
        0,
        renderRect.width,
        renderRect.height
      );
      viewportBaseSnapshotRef.current = {
        token: baseSnapshotToken,
        x: renderRect.x,
        y: renderRect.y,
        width: renderRect.width,
        height: renderRect.height,
        canvas: snapshotCanvas,
      };
    } else {
      releaseCanvas(viewportBaseSnapshotRef.current?.canvas);
      viewportBaseSnapshotRef.current = null;
    }

    drawDynamicOverlays(ctx);
    ctx.restore();
    canvasHasRenderedRef.current = true;
  }, [
    elements,
    elementsByPageBucket,
    visibleElements,
    renderRect,
    renderViewport,
    draftStroke,
    remoteDrafts,
    tool,
    erasePoint,
    eraseCandidateIdSet,
    pageCount,
    selectedImageId,
    selectedElementIds,
    imageTransform,
    selectionTransform,
    marqueeSelection,
    shapeSession,
    cropSession,
    rotateSession,
    themeMode,
    boardSurface,
    gridEnabled,
    totalHeight,
    selectedElementIdSet,
    isMobileNarrow,
    imageRefreshTick,
    isDrawing,
    canvasRenderScale,
    canvasPixelWidth,
    canvasPixelHeight,
    boardVersion,
    canvasRecoveryTick,
    touchGestureActive,
    currentPage,
    effectivePageBufferBefore,
    effectivePageBufferAfter,
    isLowMemoryDevice,
    maxPageCacheCanvases,
  ]);

  useLayoutEffect(() => {
    const overlayCanvas = draftOverlayRef.current;
    if (!overlayCanvas) return;
    const ctx = overlayCanvas.getContext("2d");
    if (!ctx) return;

    const scaleX = canvasPixelWidth / Math.max(1, Number(renderRect.width || WIDTH));
    const scaleY = canvasPixelHeight / Math.max(1, Number(renderRect.height || PAGE_HEIGHT));

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    ctx.setTransform(scaleX, 0, 0, scaleY, -Number(renderRect.x || 0) * scaleX, -Number(renderRect.y || 0) * scaleY);

    ctx.save();
    ctx.beginPath();
    ctx.rect(renderRect.x, renderRect.y, renderRect.width, renderRect.height);
    ctx.clip();

    const localDraftBounds = draftStroke ? getElementBounds(draftStroke) : null;
    if (draftStroke && (!localDraftBounds || rectsIntersect(localDraftBounds, renderRect))) {
      drawStroke(ctx, draftStroke, { strokeStyle: resolveStrokeColor(draftStroke.color, themeMode, boardSurface) });
    }

    const eraserPreviewRadius = getEraserRadius(activePointerTypeRef.current || "mouse");
    if (tool === "eraser" && erasePoint && rectsIntersect({ x: erasePoint.x - eraserPreviewRadius - 4, y: erasePoint.y - eraserPreviewRadius - 4, width: (eraserPreviewRadius + 4) * 2, height: (eraserPreviewRadius + 4) * 2 }, renderRect)) {
      ctx.save();
      ctx.strokeStyle = "rgba(249, 115, 22, 0.8)";
      ctx.fillStyle = "rgba(249, 115, 22, 0.12)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(erasePoint.x, erasePoint.y, eraserPreviewRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore();
  }, [
    draftStroke,
    erasePoint,
    tool,
    renderRect,
    canvasRenderScale,
    canvasPixelWidth,
    canvasPixelHeight,
    themeMode,
    boardVersion,
    canvasRecoveryTick,
  ]);

  function pointFromEvent(e) {
    const pointerType = String(e?.pointerType || activePointerTypeRef.current || "");
    const rect = getCanvasRectForEvent(pointerType === "touch" || pointerType === "pen");
    if (!rect) return null;
    const ratioX = (Number(e?.clientX || 0) - rect.left) / rect.width;
    const ratioY = (Number(e?.clientY || 0) - rect.top) / rect.height;
    const x = Number(renderRect?.x || 0) + ratioX * Number(renderRect?.width || WIDTH);
    const y = Number(renderRect?.y || 0) + ratioY * Number(renderRect?.height || PAGE_HEIGHT);
    const nextPoint = {
      x: Math.max(0, Math.min(WIDTH, x)),
      y: Math.max(0, Math.min(totalHeight, y)),
    };
    lastPointerBoardPointRef.current = nextPoint;
    lastPointerAtRef.current = Date.now();
    return nextPoint;
  }

  function pointsFromPointerEvent(e) {
    const pointerType = String(e?.pointerType || activePointerTypeRef.current || "");
    const rect = getCanvasRectForEvent(pointerType === "touch" || pointerType === "pen");
    if (!rect) return [];
    const convert = (clientX, clientY) => {
      const ratioX = (Number(clientX || 0) - rect.left) / rect.width;
      const ratioY = (Number(clientY || 0) - rect.top) / rect.height;
      const x = Number(renderRect?.x || 0) + ratioX * Number(renderRect?.width || WIDTH);
      const y = Number(renderRect?.y || 0) + ratioY * Number(renderRect?.height || PAGE_HEIGHT);
      return {
        x: Math.max(0, Math.min(WIDTH, x)),
        y: Math.max(0, Math.min(totalHeight, y)),
      };
    };

    const collected = [];
    if (!firefoxFreehandModeRef.current && typeof e?.getCoalescedEvents === "function") {
      try {
        const coalesced = e.getCoalescedEvents();
        if (Array.isArray(coalesced) && coalesced.length) {
          coalesced.forEach((ev) => {
            collected.push(convert(ev.clientX, ev.clientY));
          });
        }
      } catch {
        // Firefox / older tablet browsers can expose the method but throw for some events.
      }
    }

    const eventPoint = convert(e?.clientX, e?.clientY);
    const lastCollected = collected[collected.length - 1];
    if (
      !lastCollected ||
      Math.abs(Number(lastCollected.x || 0) - Number(eventPoint.x || 0)) > 0.01 ||
      Math.abs(Number(lastCollected.y || 0) - Number(eventPoint.y || 0)) > 0.01
    ) {
      collected.push(eventPoint);
    }

    const last = collected[collected.length - 1];
    if (last) {
      lastPointerBoardPointRef.current = last;
      lastPointerAtRef.current = Date.now();
    }
    return collected;
  }

  function appendPointIfNeeded(points, point, minDistanceSq = 1.2 * 1.2) {
    if (!point || !Array.isArray(points)) return false;
    if (!points.length) {
      points.push(point);
      return true;
    }
    const prev = points[points.length - 1];
    const dx = Number(point.x || 0) - Number(prev.x || 0);
    const dy = Number(point.y || 0) - Number(prev.y || 0);
    if (dx * dx + dy * dy < minDistanceSq) return false;
    points.push(point);
    return true;
  }

  function getAccidentalTapMetrics(points) {
    const safePoints = Array.isArray(points) ? points : [];
    if (!safePoints.length) return { span: 0, travel: 0 };

    const first = safePoints[0];
    let minX = Number(first?.x || 0);
    let maxX = minX;
    let minY = Number(first?.y || 0);
    let maxY = minY;
    let travel = 0;
    let previous = first;

    for (let index = 1; index < safePoints.length; index += 1) {
      const point = safePoints[index];
      const x = Number(point?.x || 0);
      const y = Number(point?.y || 0);
      const previousX = Number(previous?.x || 0);
      const previousY = Number(previous?.y || 0);
      travel += Math.hypot(x - previousX, y - previousY);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      previous = point;
    }

    return {
      span: Math.hypot(maxX - minX, maxY - minY),
      travel,
    };
  }

  function shouldSuppressAccidentalFreehandTap(points, e = null) {
    const pointerType = String(e?.pointerType || activePointerTypeRef.current || "mouse");
    if (pointerType !== "pen" && pointerType !== "touch") return false;

    const durationMs = Math.max(0, Date.now() - Number(activePointerStartedAtRef.current || 0));
    const maxPressure = Math.max(
      Number(activePointerMaxPressureRef.current || 0),
      Number(e?.pressure || 0)
    );
    const canvasRect = canvasRectRef.current || canvasRef.current?.getBoundingClientRect?.();
    const boardUnitsPerCssPixel = canvasRect?.width > 0
      ? WIDTH / Number(canvasRect.width)
      : 1;
    const { span, travel } = getAccidentalTapMetrics(points);
    const spanCssPx = span / Math.max(0.01, boardUnitsPerCssPixel);
    const travelCssPx = travel / Math.max(0.01, boardUnitsPerCssPixel);
    const holdThresholdMs = pointerType === "pen" ? 115 : 150;
    const deliberatePressure = pointerType === "pen" && maxPressure >= 0.55;

    return (
      durationMs < holdThresholdMs &&
      spanCssPx < 3.5 &&
      travelCssPx < 5.5 &&
      !deliberatePressure
    );
  }

  function logFreehandDebug(details) {
    if (typeof console === "undefined" || typeof console.warn !== "function") return;
    if (!isWhiteboardDebugEnabled()) return;
    const ua = typeof navigator === "undefined" ? "" : String(navigator.userAgent || "");
    console.warn("[whiteboard:freehand]", {
      ...details,
      userAgent: ua,
    });
  }

  function rememberCommittedFreehandId(id) {
    const safeId = String(id || "");
    if (!safeId) return;
    const ids = committedFreehandIdsRef.current;
    ids.add(safeId);
    if (ids.size > 800) {
      const first = ids.values().next().value;
      if (first) ids.delete(first);
    }
  }

  function clearFreehandDraft(e = null) {
    isDrawingRef.current = false;
    setIsDrawing(false);
    cancelDraftFrame();
    draftStrokeRef.current = null;
    draftPointsRef.current = null;
    lastFreehandDraftRef.current = null;
    setDraftStroke(null);
    onDraftChangeRef.current?.(null);
    emitCursor(null, e);
    releaseActivePointer();
  }

  function getActiveFreehandDraftSnapshot() {
    return draftStrokeRef.current || lastFreehandDraftRef.current || draftStroke;
  }

  function commitFreehandDraftSnapshot(snapshot, e = null, source = "freehand_commit") {
    const draft = snapshot && typeof snapshot === "object" ? snapshot : null;
    const draftTool = String(draft?.tool || tool || "");
    if (!draft || (draftTool !== "pen" && draftTool !== "highlighter")) return false;
    const draftId = String(draft.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`);
    if (committedFreehandIdsRef.current.has(draftId)) return true;

    let rawCommittedPoints = (Array.isArray(draft.points) ? draft.points : []).map((point) => ({
      x: Number(point?.x || 0),
      y: Number(point?.y || 0),
    }));

    if (e && Number.isFinite(Number(e?.clientX)) && Number.isFinite(Number(e?.clientY))) {
      let endSampled = pointsFromPointerEvent(e);
      if (e?.shiftKey) {
        const originPoint = rawCommittedPoints[0] || endSampled[0];
        ensureShiftAxisLock(originPoint);
        endSampled = endSampled.map((point) => applyShiftAxisLock(point));
      }
      const pointerType = String(e?.pointerType || activePointerTypeRef.current || "mouse");
      const adaptiveInputLoad =
        pageCount >= 10 ||
        elements.length >= 320 ||
        isLowMemoryDevice;
      const veryHeavyInputLoad =
        pageCount >= 20 ||
        elements.length >= 1200 ||
        isVeryLowMemoryDevice;
      const isShortCommittedStroke = rawCommittedPoints.length < 10;
      const minDistance =
        pointerType === "touch"
          ? (isShortCommittedStroke ? 0.35 : veryHeavyInputLoad ? 1.2 : adaptiveInputLoad ? 0.85 : 0.55)
          : pointerType === "pen"
            ? (isShortCommittedStroke ? 0.3 : veryHeavyInputLoad ? 1.2 : adaptiveInputLoad ? 0.95 : 0.65)
            : (isShortCommittedStroke ? 0.35 : 0.9);
      const minDistanceSq = minDistance * minDistance;
      endSampled.forEach((point) => {
        appendPointIfNeeded(rawCommittedPoints, point, minDistanceSq);
      });
      if (endSampled.length) {
        appendPointIfNeeded(rawCommittedPoints, endSampled[endSampled.length - 1], 0.01);
      }
    }

    if (!rawCommittedPoints.length) return false;

    if (shouldSuppressAccidentalFreehandTap(rawCommittedPoints, e)) {
      const pointerType = String(e?.pointerType || activePointerTypeRef.current || "mouse");
      const durationMs = Math.max(0, Date.now() - Number(activePointerStartedAtRef.current || 0));
      const { span, travel } = getAccidentalTapMetrics(rawCommittedPoints);
      logFreehandDebug({
        phase: "suppress_accidental_tap",
        source,
        pointerType,
        durationMs,
        maxPressure: Number(activePointerMaxPressureRef.current || 0),
        points: rawCommittedPoints.length,
        span,
        travel,
      });
      return true;
    }

    const committedPoints = optimizeFreehandCommittedPoints(rawCommittedPoints, draftTool, {
      isLowMemoryDevice,
      isVeryLowMemoryDevice,
    });
    if (!committedPoints.length) return false;

    const committedDraft = {
      ...draft,
      id: draftId,
      type: "stroke",
      tool: draftTool,
      color: draft.color || color,
      width: Number(draft.width || brushSize || 3),
      points: committedPoints,
    };
    const currentState = latestValueRef.current || {};
    const currentElements = Array.isArray(currentState.elements) ? currentState.elements : [];
    if (currentElements.some((element) => String(element?.id || "") === draftId)) {
      rememberCommittedFreehandId(draftId);
      return true;
    }

    logFreehandDebug({
      phase: "direct_commit",
      source,
      tool: draftTool,
      rawPoints: rawCommittedPoints.length,
      committedPoints: committedPoints.length,
      hasEvent: !!e,
      pointerType: String(e?.pointerType || activePointerTypeRef.current || ""),
      activePointer: activePointerIdRef.current,
    });

    rememberCommittedFreehandId(draftId);
    onChangeRef.current?.(
      {
        ...currentState,
        version: Number(currentState.version || 0) + 1,
        elements: [...currentElements, committedDraft],
      },
      { kind: "draw", appendedElement: committedDraft }
    );
    return true;
  }

  function finishStrokeSafely(e = null, source = "finish_stroke") {
    const isFreehandTool = tool === "pen" || tool === "highlighter";
    if (isFreehandTool) {
      const snapshot = getActiveFreehandDraftSnapshot();
      if (snapshot || isDrawingRef.current) {
        const committed = commitFreehandDraftSnapshot(snapshot, e, source);
        if (!committed) {
          logFreehandDebug({
            phase: "commit_failed",
            source,
            hasSnapshot: !!snapshot,
            points: Array.isArray(snapshot?.points) ? snapshot.points.length : 0,
            hasEvent: !!e,
            activePointer: activePointerIdRef.current,
          });
        }
        clearFreehandDraft(e);
        return;
      }
    }
    endStroke(e);
  }

  function clearShiftAxisLock() {
    shiftAxisLockRef.current = null;
  }

  function ensureShiftAxisLock(originPoint) {
    const safeOrigin = originPoint
      ? { x: Number(originPoint.x || 0), y: Number(originPoint.y || 0) }
      : { x: 0, y: 0 };
    const current = shiftAxisLockRef.current;
    if (
      current &&
      Number.isFinite(current.origin?.x) &&
      Number.isFinite(current.origin?.y)
    ) {
      return current;
    }
    const next = {
      origin: safeOrigin,
      axis: null, // "horizontal" | "vertical"
    };
    shiftAxisLockRef.current = next;
    return next;
  }

  function applyShiftAxisLock(point) {
    const session = shiftAxisLockRef.current;
    if (!session || !point) return point;
    const originX = Number(session.origin?.x || 0);
    const originY = Number(session.origin?.y || 0);
    const pointX = Number(point.x || 0);
    const pointY = Number(point.y || 0);
    const dx = pointX - originX;
    const dy = pointY - originY;
    if (!session.axis) {
      // Minimalny ruch, aby nie blokować osi przy mikrodryfcie kursora.
      if (Math.abs(dx) < 1.5 && Math.abs(dy) < 1.5) {
        return { x: pointX, y: pointY };
      }
      session.axis = Math.abs(dx) >= Math.abs(dy) ? "horizontal" : "vertical";
    }
    if (session.axis === "horizontal") {
      return { x: pointX, y: originY };
    }
    return { x: originX, y: pointY };
  }

  function collectErasedStrokeIds(point, nextElements) {
    const radius = getEraserRadius(activePointerTypeRef.current || "mouse");
    const nextIds = new Set(eraseCandidateIdsRef.current);

    nextElements.forEach((element, index) => {
      if (element?.type !== "stroke") return;
      if (!strokeHitByPoint(element, point, radius)) return;
      nextIds.add(getStrokeId(element, index));
    });

    return Array.from(nextIds);
  }

  function findImageAtPoint(point) {
    for (let i = elements.length - 1; i >= 0; i--) {
      const element = elements[i];
      if (element?.type !== "image") continue;
      const imageId = getImageId(element, i);
      const crop = cropSession?.id === imageId ? cropSession.crop : element.crop;
      if (selectedImageId === imageId && getImageHandle(point, element, crop)) {
        return { element, index: i, id: imageId, mode: "resize" };
      }
      if (isPointInsideImage(point, element, crop)) {
        return { element, index: i, id: imageId, mode: "move" };
      }
    }
    return null;
  }

  function findStrokeAtPoint(point, event = null) {
    const pointerType = String(event?.pointerType || activePointerTypeRef.current || "mouse");
    const hitRadius = pointerType === "touch"
      ? Math.max(26, ERASER_RADIUS)
      : pointerType === "pen"
        ? Math.max(16, ERASER_RADIUS)
        : Math.max(8, ERASER_RADIUS);
    for (let i = elements.length - 1; i >= 0; i--) {
      const element = elements[i];
      if (element?.type !== "stroke") continue;
      if (!strokeHitByPoint(element, point, hitRadius)) continue;
      return {
        element,
        index: i,
        id: getStrokeId(element, i),
      };
    }
    return null;
  }

  function updateSelectedElements(keys) {
    const sanitized = Array.from(new Set((keys || []).filter(Boolean)));
    setSelectedElementIds(sanitized);
    if (sanitized.length === 1) {
      const imageId = extractImageIdFromSelectionKey(sanitized[0]);
      setSelectedImageId(imageId || null);
    } else {
      setSelectedImageId(null);
    }
  }

  function clearSelectionState() {
    updateSelectedElements([]);
    setSelectedImageId(null);
    setSelectionTransform(null);
    setImageTransform(null);
    setCropSession(null);
    setRotateSession(null);
    setMarqueeSelection(null);
    pendingMarqueeRef.current = null;
    setShapeVertexDraft(null);
    setShapeVertexEditEnabled(false);
  }

  useEffect(() => {
    if (tool === "select") return;
    if (
      selectedElementIds.length ||
      selectedImageId ||
      selectionTransform ||
      imageTransform ||
      cropSession ||
      rotateSession ||
      marqueeSelection ||
      shapeVertexEditEnabled
    ) {
      clearSelectionState();
    }
  }, [tool]);

  function startCropSelectedImage() {
    if (!selectedImageElement || !selectedImageId) return;
    setImageTransform(null);
    setRotateSession(null);
    setCropSession({
      id: selectedImageId,
      crop: normalizeCrop(selectedImageElement.crop),
      handle: null,
      startX: 0,
      startY: 0,
    });
  }

  function cancelCropSelectedImage() {
    setCropSession(null);
  }

  function startRotateSelectedImage() {
    if (!selectedImageElement || !selectedImageId) return;
    setImageTransform(null);
    setCropSession(null);
    setRotateSession({
      id: selectedImageId,
      rotation: normalizeRotation(selectedImageElement.rotation),
    });
  }

  function rotateSelectedImageBy(delta) {
    setRotateSession((prev) => {
      if (!prev || prev.id !== selectedImageId) return prev;
      return {
        ...prev,
        rotation: normalizeRotation(Number(prev.rotation || 0) + Number(delta || 0)),
      };
    });
  }

  function cancelRotateSelectedImage() {
    setRotateSession(null);
  }

  function applyRotateSelectedImage() {
    if (!rotateSession?.id) return;
    const currentState = latestValueRef.current || {};
    const nextElements = (Array.isArray(currentState.elements) ? currentState.elements : []).map((element, index) => {
      if (element?.type !== "image") return element;
      if (getImageId(element, index) !== rotateSession.id) return element;
      return {
        ...element,
        rotation: normalizeRotation(rotateSession.rotation),
      };
    });
    setRotateSession(null);
    onChange?.(
      {
        ...currentState,
        version: Number(currentState.version || 0) + 1,
        elements: nextElements,
      },
      { kind: "image_rotate" }
    );
  }

  function startShapeVertexEdit() {
    if (!selectedShapeElement) return;
    const selectedKey = String(selectedElementIds?.[0] || "");
    const keyStrokeId = selectedKey.startsWith("stroke:") ? selectedKey.slice("stroke:".length) : "";
    const targetId = String(selectedShapeElement?.id || keyStrokeId || "");
    const points = (Array.isArray(selectedShapeElement?.points) ? selectedShapeElement.points : []).map((point) => ({
      x: Number(point?.x || 0),
      y: Number(point?.y || 0),
    }));
    if (!targetId || !points.length) return;
    setSelectionTransform(null);
    setShapeVertexDraft({
      elementId: targetId,
      points,
    });
    setShapeVertexEditEnabled(true);
  }

  function cancelShapeVertexEdit() {
    setSelectionTransform(null);
    setShapeVertexDraft(null);
    setShapeVertexEditEnabled(false);
  }

  function applyShapeVertexEdit() {
    const targetId = String(shapeVertexDraft?.elementId || "");
    const draftPoints = Array.isArray(shapeVertexDraft?.points) ? shapeVertexDraft.points : [];
    if (!targetId || !draftPoints.length) {
      setShapeVertexEditEnabled(false);
      setShapeVertexDraft(null);
      return;
    }
    const normalizedDraftPoints = draftPoints.map((point) => ({
      x: Number(point?.x || 0),
      y: Number(point?.y || 0),
    }));
    const hasTarget = elements.some(
      (element, index) =>
        String(element?.type || "") === "stroke" &&
        (
          String(element?.id || "") === targetId ||
          String(getStrokeId(element, index) || "") === targetId
        )
    );
    if (!hasTarget) {
      setShapeVertexEditEnabled(false);
      setShapeVertexDraft(null);
      return;
    }
    const nextElements = elements.map((element, index) => {
      if (String(element?.type || "") !== "stroke") return element;
      if (
        String(element?.id || "") !== targetId &&
        String(getStrokeId(element, index) || "") !== targetId
      ) return element;
      return {
        ...element,
        points: normalizedDraftPoints,
      };
    });
    setSelectionTransform(null);
    setShapeVertexEditEnabled(false);
    setShapeVertexDraft(null);
    onChange?.(
      {
        ...latestValueRef.current,
        version: Number(latestValueRef.current?.version || 0) + 1,
        elements: nextElements,
      },
      {
        kind: "stroke_patch",
        elementId: targetId,
      }
    );
  }

  function applyCropSelectedImage() {
    if (!cropSession?.id) return;
    const currentState = latestValueRef.current || {};
    const nextElements = (Array.isArray(currentState.elements) ? currentState.elements : []).map((element, index) => {
      if (element?.type !== "image") return element;
      if (getImageId(element, index) !== cropSession.id) return element;
      return {
        ...element,
        crop: normalizeCrop(cropSession.crop),
      };
    });
    setCropSession(null);
    onChange?.(
      {
        ...currentState,
        version: Number(currentState.version || 0) + 1,
        elements: nextElements,
      },
      { kind: "image_crop" }
    );
  }

  function startStroke(e) {
    if (disabled) return;
    recoverStaleActivePointerBeforeStart(e);
    if (shouldIgnorePointerStart(e)) return;
    // Gesty dwoma palcami sa obslugiwane przed startStroke. W trybie
    // tylko do odczytu pojedynczy pointer nie moze rozpoczac zadnej edycji.
    if (readOnly) return;
    clearShiftAxisLock();
    // Defensive reset: pointerId (szczególnie dla myszki) bywa stały,
    // więc po long-press switchu nie chcemy, aby suppress z poprzedniej
    // interakcji kasował pierwszą poprawną kreskę.
    suppressPointerCommitRef.current = null;
    const pt = pointFromEvent(e);
    if (!pt) return;
    claimActivePointer(e);
    e.preventDefault();
    startHoldSwitchSession(e, pt);
    draftBroadcastRef.current.lastAt = 0;

    if (isTemporaryMousePan(e)) {
      panSessionRef.current = {
        pointerId: Number(e?.pointerId),
        lastClientX: Number(e?.clientX || 0),
        lastClientY: Number(e?.clientY || 0),
      };
      return;
    }

    if (tool === "select" && cropSession?.id === selectedImageId && selectedImageElement) {
      const cropHandle = getCropHandle(pt, selectedImageElement, cropSession.crop);
      if (cropHandle) {
        setCropSession((prev) => (
          prev
            ? {
                ...prev,
                handle: cropHandle,
                startX: pt.x,
                startY: pt.y,
              }
            : prev
        ));
        return;
      }
      return;
    }

    if (tool === "select" && rotateSession?.id === selectedImageId) {
      return;
    }

    if (tool === "select" && enableElementSelection) {
      if (shapeVertexEditEnabled && selectedShapeElement && selectedElementIds.length === 1) {
        const selectedKey = String(selectedElementIds[0] || "");
        const selectedStrokeId = selectedKey.startsWith("stroke:") ? selectedKey.slice("stroke:".length) : "";
        if (selectedStrokeId) {
          const editTarget =
            shapeVertexDraft?.elementId && String(shapeVertexDraft.elementId) === selectedStrokeId && Array.isArray(shapeVertexDraft.points)
              ? { ...selectedShapeElement, points: shapeVertexDraft.points }
              : selectedShapeElement;
          const vertexHandle = getStrokeVertexHandleAtPoint(editTarget, pt, 16);
          if (vertexHandle) {
            setSelectionTransform({
              mode: "vertex",
              keys: [selectedKey],
              elementId: selectedStrokeId,
              pointIndex: Number(vertexHandle.index),
              baseTool: String(editTarget?.tool || "polygon"),
              basePoints: (Array.isArray(editTarget?.points) ? editTarget.points : []).map((point) => ({
                x: Number(point?.x || 0),
                y: Number(point?.y || 0),
              })),
              startX: pt.x,
              startY: pt.y,
              dx: 0,
              dy: 0,
            });
            return;
          }
          // W trybie edycji wierzchołków ignorujemy zwykłe przesuwanie zaznaczenia.
          return;
        }
      }

      const singleImageSelected =
        selectedElementIds.length === 1 && String(selectedElementIds[0] || "").startsWith("image:");
      if (!singleImageSelected && getSelectionResizeHandle(pt, selectedElementsRect)) {
        setSelectionTransform({
          mode: "resize",
          keys: [...selectedElementIds],
          startX: pt.x,
          startY: pt.y,
          originX: Number(selectedElementsRect?.x || 0),
          originY: Number(selectedElementsRect?.y || 0),
          baseWidth: Math.max(1, Number(selectedElementsRect?.width || 1)),
          baseHeight: Math.max(1, Number(selectedElementsRect?.height || 1)),
          scale: 1,
        });
        return;
      }

      const imageHit = findImageAtPoint(pt);
      if (imageHit?.mode === "resize") {
        updateSelectedElements([`image:${imageHit.id}`]);
        setRotateSession(null);
        setImageTransform({
          id: imageHit.id,
          mode: imageHit.mode,
          startX: pt.x,
          startY: pt.y,
          x: Number(imageHit.element.x || 0),
          y: Number(imageHit.element.y || 0),
          width: Number(imageHit.element.width || 0),
          height: Number(imageHit.element.height || 0),
          aspectRatio: Math.max(0.1, Number(imageHit.element.width || 1) / Math.max(1, Number(imageHit.element.height || 1))),
        });
        return;
      }

      const strokeHit = findStrokeAtPoint(pt, e);
      const hitKey = imageHit
        ? `image:${imageHit.id}`
        : strokeHit
          ? `stroke:${strokeHit.id}`
          : null;
      if (hitKey) {
        const nextSelected = selectedElementIdSet.has(hitKey) ? selectedElementIds : [hitKey];
        updateSelectedElements(nextSelected);
        setRotateSession(null);
        setCropSession(null);
        setImageTransform(null);
        setMarqueeSelection(null);
        setSelectionTransform({
          mode: "move",
          startX: pt.x,
          startY: pt.y,
          dx: 0,
          dy: 0,
          keys: nextSelected,
        });
        return;
      }

      setImageTransform(null);
      setRotateSession(null);
      setCropSession(null);
      setSelectionTransform(null);
      setSelectedImageId(null);
      updateSelectedElements([]);
      pendingMarqueeRef.current = {
        startX: pt.x,
        startY: pt.y,
        endX: pt.x,
        endY: pt.y,
      };
      setMarqueeSelection(null);
      return;
    }

    const imageHit = findImageAtPoint(pt);
    if (tool === "select" && imageHit) {
      setSelectedImageId(imageHit.id);
      setRotateSession(null);
      setImageTransform({
        id: imageHit.id,
        mode: imageHit.mode,
        startX: pt.x,
        startY: pt.y,
        x: Number(imageHit.element.x || 0),
        y: Number(imageHit.element.y || 0),
        width: Number(imageHit.element.width || 0),
        height: Number(imageHit.element.height || 0),
        aspectRatio: Math.max(0.1, Number(imageHit.element.width || 1) / Math.max(1, Number(imageHit.element.height || 1))),
      });
      return;
    }

    if (tool === "select") {
      setSelectedImageId(null);
      setImageTransform(null);
      setCropSession(null);
      setRotateSession(null);
      return;
    }

    if (tool !== "select") {
      clearSelectionState();
    }

    if (tool === "select") return;

    if (tool === "line" || tool === "circle") {
      const shapeType = tool;
      const buildPoints = (startPoint, targetPoint) => (
        shapeType === "line"
          ? createLinePoints(startPoint, targetPoint)
          : createCirclePoints(startPoint, targetPoint)
      );
      if (shapeSession && shapeSession.tool === shapeType) {
        const startPoint = shapeSession.start || pt;
        const finalizedPoints = buildPoints(startPoint, pt);
        const finalStroke = {
          id: String(draftStrokeRef.current?.id || draftStroke?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`),
          type: "stroke",
          tool: shapeType,
          color,
          width: brushSize,
          points: finalizedPoints,
        };
        onChange?.(
          {
            version: Number(value?.version || 0) + 1,
            elements: [...elements, finalStroke],
          },
          { kind: "draw", appendedElement: finalStroke }
        );
        cancelDraftFrame();
        draftStrokeRef.current = null;
        draftPointsRef.current = null;
        setDraftStroke(null);
        onDraftChange?.(null);
        setShapeSession(null);
        isDrawingRef.current = false;
        setIsDrawing(false);
        emitCursor(null, e);
        releaseActivePointer();
        return;
      }
      const initialPoints = buildPoints(pt, pt);
      const baseDraft = {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        type: "stroke",
        tool: shapeType,
        color,
        width: brushSize,
        points: initialPoints,
      };
      setShapeSession({
        tool: shapeType,
        start: pt,
        current: pt,
      });
      isDrawingRef.current = true;
      setIsDrawing(true);
      cancelDraftFrame();
      draftStrokeRef.current = baseDraft;
      setDraftStroke(baseDraft);
      onDraftChange?.(baseDraft);
      return;
    }

    if (tool === "polygon") {
      const shapeType = "polygon";
      const draftId = String(draftStroke?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`);

      if (!shapeSession || shapeSession.tool !== shapeType) {
        const points = [{ x: Number(pt.x || 0), y: Number(pt.y || 0) }];
        const baseDraft = {
          id: draftId,
          type: "stroke",
          tool: shapeType,
          color,
          width: brushSize,
          points,
        };
        setShapeSession({ tool: shapeType, points, current: pt });
        cancelDraftFrame();
        draftStrokeRef.current = baseDraft;
        setDraftStroke(baseDraft);
        onDraftChange?.(baseDraft);
        return;
      }

      const basePoints = Array.isArray(shapeSession.points) ? shapeSession.points : [];
      if (!basePoints.length) {
        const points = [{ x: Number(pt.x || 0), y: Number(pt.y || 0) }];
        const baseDraft = {
          id: draftId,
          type: "stroke",
          tool: shapeType,
          color,
          width: brushSize,
          points,
        };
        setShapeSession({ tool: shapeType, points, current: pt });
        cancelDraftFrame();
        draftStrokeRef.current = baseDraft;
        setDraftStroke(baseDraft);
        onDraftChange?.(baseDraft);
        return;
      }

      const firstPoint = basePoints[0];
      const shouldClose = basePoints.length >= 3 && isPointNear(pt, firstPoint);
      if (shouldClose) {
        const finalStroke = {
          id: draftId,
          type: "stroke",
          tool: shapeType,
          color,
          width: brushSize,
          points: createPolygonPoints(basePoints, true),
        };
        if (finalStroke.points.length >= 4) {
          onChange?.(
            {
              version: Number(value?.version || 0) + 1,
              elements: [...elements, finalStroke],
            },
            { kind: "draw", appendedElement: finalStroke }
          );
        }
        cancelDraftFrame();
        draftStrokeRef.current = null;
        draftPointsRef.current = null;
        setDraftStroke(null);
        onDraftChange?.(null);
        setShapeSession(null);
        emitCursor(null, e);
        releaseActivePointer();
        return;
      }

      const nextPoints = [...basePoints, { x: Number(pt.x || 0), y: Number(pt.y || 0) }];
      const nextDraft = {
        id: draftId,
        type: "stroke",
        tool: shapeType,
        color,
        width: brushSize,
        points: nextPoints,
      };
      setShapeSession({ tool: shapeType, points: nextPoints, current: pt });
      draftStrokeRef.current = nextDraft;
      setDraftStroke(nextDraft);
      onDraftChange?.(nextDraft);
      return;
    }

    if (tool === "eraser") {
      isDrawingRef.current = true;
      setIsDrawing(true);
      setErasePoint(pt);
      collectEraserScanPointsNow(pt, elements, String(e?.pointerType || activePointerTypeRef.current || "mouse"));
      return;
    }

    const baseDraft = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      type: "stroke",
      tool,
      color,
      width: brushSize,
      points: [pt],
    };

    isDrawingRef.current = true;
    setIsDrawing(true);
    draftPointsRef.current = baseDraft.points;
    draftStrokeRef.current = baseDraft;
    lastFreehandDraftRef.current = {
      ...baseDraft,
      points: baseDraft.points.map((point) => ({
        x: Number(point?.x || 0),
        y: Number(point?.y || 0),
      })),
    };
    setDraftStroke(baseDraft);
    onDraftChange?.(baseDraft);
  }

  function moveStroke(e) {
    const pt = pointFromEvent(e);
    if (!pt) return;
    if (isActivePointerEvent(e)) {
      activePointerMaxPressureRef.current = Math.max(
        Number(activePointerMaxPressureRef.current || 0),
        Number(e?.pressure || 0)
      );
    }
    updateHoldSwitchSession(pt);

    if ((tool === "line" || tool === "circle") && shapeSession && shapeSession.tool === tool) {
      const startPoint = shapeSession.start || pt;
      const nextPoints = tool === "line"
        ? createLinePoints(startPoint, pt)
        : createCirclePoints(startPoint, pt);
      setShapeSession((prev) => (prev ? { ...prev, current: pt } : prev));
      setDraftStroke((prev) => {
        const nextDraft = {
          id: String(prev?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`),
          type: "stroke",
          tool,
          color,
          width: brushSize,
          points: nextPoints,
        };
        onDraftChange?.(nextDraft);
        return nextDraft;
      });
      return;
    }

    if (tool === "polygon" && shapeSession && shapeSession.tool === "polygon") {
      const basePoints = Array.isArray(shapeSession.points) ? shapeSession.points : [];
      if (!basePoints.length) return;
      const previewPoints = [...basePoints, { x: Number(pt.x || 0), y: Number(pt.y || 0) }];
      setShapeSession((prev) => (prev ? { ...prev, current: pt } : prev));
      setDraftStroke((prev) => {
        const nextDraft = {
          id: String(prev?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`),
          type: "stroke",
          tool: "polygon",
          color,
          width: brushSize,
          points: previewPoints,
        };
        onDraftChange?.(nextDraft);
        return nextDraft;
      });
      return;
    }

    if (!isActivePointerEvent(e)) return;

    const panSession = panSessionRef.current;
    if (panSession && Number(panSession.pointerId) === Number(e?.pointerId)) {
      e.preventDefault();
      const el = scrollerRef.current;
      if (el) {
        const deltaX = Number(e?.clientX || 0) - Number(panSession.lastClientX || 0);
        const deltaY = Number(e?.clientY || 0) - Number(panSession.lastClientY || 0);
        if (deltaX || deltaY) {
          el.scrollLeft -= deltaX;
          el.scrollTop -= deltaY;
          updateCurrentPage();
        }
      }
      panSession.lastClientX = Number(e?.clientX || 0);
      panSession.lastClientY = Number(e?.clientY || 0);
      return;
    }

    if (cropSession?.handle && selectedImageElement) {
      e.preventDefault();
      setCropSession((prev) => {
        if (!prev?.handle) return prev;
        const fullWidth = Math.max(1, Number(selectedImageElement.width || 1));
        const fullHeight = Math.max(1, Number(selectedImageElement.height || 1));
        const nextCrop = { ...normalizeCrop(prev.crop) };
        const deltaX = pt.x - prev.startX;
        const deltaY = pt.y - prev.startY;

        if (prev.handle === "left") nextCrop.left += deltaX / fullWidth;
        if (prev.handle === "right") nextCrop.right += deltaX / fullWidth;
        if (prev.handle === "top") nextCrop.top += deltaY / fullHeight;
        if (prev.handle === "bottom") nextCrop.bottom += deltaY / fullHeight;

        return {
          ...prev,
          crop: normalizeCrop(nextCrop),
          startX: pt.x,
          startY: pt.y,
        };
      });
      return;
    }
    if (imageTransform) {
      e.preventDefault();
      setImageTransform((prev) => {
        if (!prev) return prev;
        if (prev.mode === "move") {
          return {
            ...prev,
            x: Math.max(0, prev.x + (pt.x - prev.startX)),
            y: Math.max(0, prev.y + (pt.y - prev.startY)),
            startX: pt.x,
            startY: pt.y,
          };
        }

        const nextWidth = Math.max(120, prev.width + (pt.x - prev.startX));
        const nextHeight = Math.max(80, Math.round(nextWidth / prev.aspectRatio));
        return {
          ...prev,
          width: nextWidth,
          height: nextHeight,
          startX: pt.x,
          startY: pt.y,
        };
      });
      return;
    }

    if (selectionTransform) {
      e.preventDefault();
      setSelectionTransform((prev) => {
        if (!prev) return prev;
        if (prev.mode === "vertex") {
          const dx = pt.x - Number(prev.startX || 0);
          const dy = pt.y - Number(prev.startY || 0);
          const pointIndex = Number(prev.pointIndex);
          const basePoints = Array.isArray(prev.basePoints) ? prev.basePoints : [];
          const baseTool = String(prev.baseTool || "polygon");
          const elementId = String(prev.elementId || "");
          if (elementId && Number.isInteger(pointIndex) && basePoints.length) {
            const preview = moveStrokeVertexPoints(
              { type: "stroke", tool: baseTool, points: basePoints },
              pointIndex,
              dx,
              dy
            );
            const nextPoints = (Array.isArray(preview?.points) ? preview.points : []).map((point) => ({
              x: Number(point?.x || 0),
              y: Number(point?.y || 0),
            }));
            if (nextPoints.length) {
              setShapeVertexDraft((current) => {
                if (!current || String(current.elementId || "") !== elementId) return current;
                return { ...current, points: nextPoints };
              });
            }
          }
          return {
            ...prev,
            dx,
            dy,
          };
        }
        if (prev.mode === "resize") {
          const deltaX = pt.x - Number(prev.startX || 0);
          const deltaY = pt.y - Number(prev.startY || 0);
          const baseWidth = Math.max(1, Number(prev.baseWidth || 1));
          const baseHeight = Math.max(1, Number(prev.baseHeight || 1));
          const scaleX = (baseWidth + deltaX) / baseWidth;
          const scaleY = (baseHeight + deltaY) / baseHeight;
          const nextScale = Math.max(0.1, Math.min(8, Math.max(scaleX, scaleY)));
          return {
            ...prev,
            scale: nextScale,
          };
        }
        return {
          ...prev,
          dx: pt.x - prev.startX,
          dy: pt.y - prev.startY,
        };
      });
      return;
    }

    if (pendingMarqueeRef.current) {
      const pending = pendingMarqueeRef.current;
      const dx = pt.x - Number(pending.startX || 0);
      const dy = pt.y - Number(pending.startY || 0);
      const threshold = String(e?.pointerType || activePointerTypeRef.current || "") === "touch" ? 12 : 6;
      if ((dx * dx + dy * dy) < threshold * threshold) {
        e.preventDefault();
        return;
      }
      const nextMarquee = {
        startX: Number(pending.startX || pt.x),
        startY: Number(pending.startY || pt.y),
        endX: pt.x,
        endY: pt.y,
      };
      pendingMarqueeRef.current = null;
      setMarqueeSelection(nextMarquee);
      e.preventDefault();
      return;
    }

    if (marqueeSelection) {
      e.preventDefault();
      setMarqueeSelection((prev) => (prev ? { ...prev, endX: pt.x, endY: pt.y } : prev));
      return;
    }

    if (!isDrawingRef.current) return;
    e.preventDefault();

    if (tool === "eraser") {
      setErasePoint(pt);
      collectEraserScanPointsNow(pt, elements, String(e?.pointerType || activePointerTypeRef.current || "mouse"));
      return;
    }

    if (!draftStrokeRef.current && !draftStroke) {
      releaseActivePointer();
      return;
    }

    const isFreehand = tool === "pen" || tool === "highlighter";
    if (!isFreehand) {
      setDraftStroke((prev) => {
        if (!prev) return prev;
        const next = { ...prev, points: [...prev.points, pt] };
        draftStrokeRef.current = next;
        onDraftChange?.(next);
        return next;
      });
      return;
    }

    const currentDraft = draftStrokeRef.current || draftStroke;
    if (!currentDraft) {
      releaseActivePointer();
      return;
    }

    let sampled = pointsFromPointerEvent(e);
    let nextPoints = Array.isArray(draftPointsRef.current)
      ? draftPointsRef.current
      : (Array.isArray(currentDraft.points) ? currentDraft.points : []);
    if (!Array.isArray(nextPoints)) nextPoints = [];
    if (!draftPointsRef.current) {
      draftPointsRef.current = nextPoints;
    }
    const shiftAxisLockEnabled = !!e?.shiftKey;
    if (shiftAxisLockEnabled) {
      const originPoint = nextPoints[0] || sampled[0] || pt;
      ensureShiftAxisLock(originPoint);
      sampled = sampled.map((point) => applyShiftAxisLock(point));
    } else {
      clearShiftAxisLock();
    }
    const pointerType = String(e?.pointerType || activePointerTypeRef.current || "mouse");
    const adaptiveInputLoad =
      pageCount >= 10 ||
      elements.length >= 320 ||
      isLowMemoryDevice;
    const veryHeavyInputLoad =
      pageCount >= 20 ||
      elements.length >= 1200 ||
      isVeryLowMemoryDevice;
    const earlyStrokePointCount = nextPoints.length;
    const isEarlyFreehandStroke = earlyStrokePointCount < 10;
    if ((pointerType === "touch" || pointerType === "pen") && sampled.length > 6 && !isEarlyFreehandStroke) {
      const decimationStep =
        pointerType === "touch"
          ? (veryHeavyInputLoad ? 3 : adaptiveInputLoad ? 2 : 1)
          : (veryHeavyInputLoad ? 3 : adaptiveInputLoad ? 2 : 1);
      const decimated = [];
      for (let i = 0; i < sampled.length; i += decimationStep) decimated.push(sampled[i]);
      const last = sampled[sampled.length - 1];
      if (decimated[decimated.length - 1] !== last) decimated.push(last);
      sampled = decimated;
    }
    const minDistance =
      pointerType === "touch"
        ? (isEarlyFreehandStroke ? 0.35 : veryHeavyInputLoad ? 1.2 : adaptiveInputLoad ? 0.85 : 0.55)
        : pointerType === "pen"
          ? (isEarlyFreehandStroke ? 0.35 : veryHeavyInputLoad ? 1.2 : adaptiveInputLoad ? 0.95 : 0.65)
          : (isEarlyFreehandStroke ? 0.45 : 0.9);
    const minDistanceSq = minDistance * minDistance;
    let changed = false;
    sampled.forEach((point) => {
      const appended = appendPointIfNeeded(nextPoints, point, minDistanceSq);
      if (appended) changed = true;
    });

    if (!changed && sampled.length) {
      const fallbackPoint = sampled[sampled.length - 1];
      const appended = appendPointIfNeeded(nextPoints, fallbackPoint, 0.01);
      if (appended) changed = true;
    }
    if (!changed) return;
    if (firefoxFreehandModeRef.current) {
      const stablePoints = nextPoints.map((point) => ({
        x: Number(point?.x || 0),
        y: Number(point?.y || 0),
      }));
      const nextDraft = {
        ...currentDraft,
        points: stablePoints,
      };
      draftPointsRef.current = stablePoints;
      draftStrokeRef.current = nextDraft;
      lastFreehandDraftRef.current = nextDraft;
      setDraftStroke(nextDraft);
      onDraftChangeRef.current?.(nextDraft);
      return;
    }
    currentDraft.points = nextPoints;
    draftStrokeRef.current = currentDraft;
    lastFreehandDraftRef.current = {
      ...currentDraft,
      points: nextPoints.map((point) => ({
        x: Number(point?.x || 0),
        y: Number(point?.y || 0),
      })),
    };
    scheduleDraftFrame();
  }

  function endStroke(e) {
    const isFreehandTool = tool === "pen" || tool === "highlighter";
    const hasFreehandDraft = isFreehandTool && !!(draftStrokeRef.current || lastFreehandDraftRef.current || draftStroke);
    if (e && !isActivePointerEvent(e)) {
      if (firefoxFreehandModeRef.current && hasFreehandDraft) {
        e = null;
      } else {
        return;
      }
    }
    draftBroadcastRef.current.lastAt = 0;
    cancelHoldSwitchSession();
    clearShiftAxisLock();
    const panSession = panSessionRef.current;
    if (panSession && (!e || Number(panSession.pointerId) === Number(e?.pointerId))) {
      panSessionRef.current = null;
      releaseActivePointer();
      return;
    }
    if (cropSession?.handle) {
      setCropSession((prev) => (prev ? { ...prev, handle: null } : prev));
      releaseActivePointer();
      return;
    }

    if ((tool === "line" || tool === "circle") && shapeSession && shapeSession.tool === tool) {
      releaseActivePointer();
      return;
    }

    if (tool === "polygon" && shapeSession && (!e || isActivePointerEvent(e))) {
      releaseActivePointer();
      return;
    }

    if (imageTransform) {
      const transform = imageTransform;
      setImageTransform(null);
      const nextElements = elements.map((element, index) => {
        if (element?.type !== "image") return element;
        const imageId = getImageId(element, index);
        if (imageId !== transform.id) return element;
        return {
          ...element,
          x: Math.round(transform.x),
          y: Math.round(transform.y),
          width: Math.round(transform.width),
          height: Math.round(transform.height),
        };
      });
      onChange?.(
        {
          ...latestValueRef.current,
          version: Number(latestValueRef.current?.version || 0) + 1,
          elements: nextElements,
        },
        { kind: "image_transform" }
      );
      releaseActivePointer();
      return;
    }

    if (selectionTransform) {
      const selectedSet = new Set(selectionTransform.keys || []);
      setSelectionTransform(null);
      if (selectionTransform.mode === "vertex") {
        // Draft punktów jest już aktualizowany "na żywo" w onPointerMove.
      } else if (selectionTransform.mode === "resize") {
        const scale = Number(selectionTransform.scale || 1);
        const originX = Number(selectionTransform.originX || 0);
        const originY = Number(selectionTransform.originY || 0);
        if (Math.abs(scale - 1) > 1e-6) {
          const nextElements = elements.map((element, index) => {
            const key = getElementSelectionKey(element, index);
            if (!key || !selectedSet.has(key)) return element;
            return scaleElementUniform(element, scale, originX, originY);
          });
          onChange?.(
            {
              ...latestValueRef.current,
              version: Number(latestValueRef.current?.version || 0) + 1,
              elements: nextElements,
            },
            {
              kind: "selection_resize",
              resizedIds: Array.from(selectedSet),
              scale,
              originX,
              originY,
            }
          );
        }
      } else {
        const dx = Number(selectionTransform.dx || 0);
        const dy = Number(selectionTransform.dy || 0);
        if (dx || dy) {
          const nextElements = elements.map((element, index) => {
            const key = getElementSelectionKey(element, index);
            if (!key || !selectedSet.has(key)) return element;
            return translateElement(element, dx, dy);
          });
          onChange?.(
            {
              ...latestValueRef.current,
              version: Number(latestValueRef.current?.version || 0) + 1,
              elements: nextElements,
            },
            {
              kind: "selection_move",
              movedIds: Array.from(selectedSet),
              dx,
              dy,
            }
          );
        }
      }
      releaseActivePointer();
      return;
    }

    if (pendingMarqueeRef.current) {
      pendingMarqueeRef.current = null;
      updateSelectedElements([]);
      releaseActivePointer();
      return;
    }

    if (marqueeSelection) {
      const selectionRect = {
        x: Math.min(marqueeSelection.startX, marqueeSelection.endX),
        y: Math.min(marqueeSelection.startY, marqueeSelection.endY),
        width: Math.abs(marqueeSelection.endX - marqueeSelection.startX),
        height: Math.abs(marqueeSelection.endY - marqueeSelection.startY),
      };
      setMarqueeSelection(null);
      if (selectionRect.width < 4 && selectionRect.height < 4) {
        updateSelectedElements([]);
        releaseActivePointer();
        return;
      }
      const nextSelected = [];
      elements.forEach((element, index) => {
        const key = getElementSelectionKey(element, index);
        if (!key) return;
        const bounds = getElementBounds(element);
        if (rectsIntersect(selectionRect, bounds)) {
          nextSelected.push(key);
        }
      });
      updateSelectedElements(nextSelected);
      releaseActivePointer();
      return;
    }

    if (!isDrawingRef.current && !hasFreehandDraft) {
      if (e && Number(suppressPointerCommitRef.current) === Number(e?.pointerId)) {
        suppressPointerCommitRef.current = null;
      }
      releaseActivePointer();
      return;
    }
    isDrawingRef.current = false;
    setIsDrawing(false);

    if (tool === "eraser") {
      const nextIdSet = new Set(eraseCandidateIdsRef.current);
      if (e) {
        const finishPoint = pointFromEvent(e);
        if (finishPoint) {
          buildEraserScanPoints(finishPoint, String(e?.pointerType || activePointerTypeRef.current || "mouse")).forEach((scanPoint) => {
            collectErasedStrokeIds(scanPoint, elements).forEach((id) => nextIdSet.add(id));
          });
        }
      }
      if (nextIdSet.size > 0) {
        const nextElements = elements.filter((element, index) => !nextIdSet.has(getStrokeId(element, index)));
        onChange?.(
          {
            version: Number(value?.version || 0) + 1,
            elements: nextElements,
          },
          { kind: "erase", removedIds: Array.from(nextIdSet) }
        );
      }
      setErasePoint(null);
      updateEraseCandidateIds([]);
      cancelEraserScanFrame();
      emitCursor(null, e);
      releaseActivePointer();
      return;
    }

    const finalizedDraft = isFreehandTool
      ? (flushDraftFrameNow() || draftStrokeRef.current || lastFreehandDraftRef.current || draftStroke)
      : (draftStrokeRef.current || draftStroke);
    if (!finalizedDraft) {
      if (e && Number(suppressPointerCommitRef.current) === Number(e?.pointerId)) {
        suppressPointerCommitRef.current = null;
      }
      releaseActivePointer();
      return;
    }

    if (e && Number(suppressPointerCommitRef.current) === Number(e?.pointerId)) {
      suppressPointerCommitRef.current = null;
      setDraftStroke(null);
      draftStrokeRef.current = null;
      draftPointsRef.current = null;
      lastFreehandDraftRef.current = null;
      onDraftChange?.(null);
      releaseActivePointer();
      return;
    }

    if (firefoxFreehandModeRef.current && isFreehandTool) {
      commitFreehandDraftSnapshot(finalizedDraft, e, "end_stroke_firefox");
      clearFreehandDraft(e);
      return;
    }

    let rawCommittedPoints = (Array.isArray(finalizedDraft.points) ? finalizedDraft.points : []).map((p) => ({
      x: Number(p?.x || 0),
      y: Number(p?.y || 0),
    }));
    // Domykamy stroke punktami z pointerup (w tym coalesced), żeby nie ucinać końcówek
    // przy szybkich ruchach / słabszych urządzeniach.
    if (e && (tool === "pen" || tool === "highlighter")) {
      let endSampled = pointsFromPointerEvent(e);
      const shiftAxisLockEnabled = !!e?.shiftKey;
      if (shiftAxisLockEnabled) {
        const originPoint = rawCommittedPoints[0] || endSampled[0];
        ensureShiftAxisLock(originPoint);
        endSampled = endSampled.map((point) => applyShiftAxisLock(point));
      }
      const pointerType = String(e?.pointerType || activePointerTypeRef.current || "mouse");
      const adaptiveInputLoad =
        pageCount >= 10 ||
        elements.length >= 320 ||
        isLowMemoryDevice;
      const veryHeavyInputLoad =
        pageCount >= 20 ||
        elements.length >= 1200 ||
        isVeryLowMemoryDevice;
      const isShortCommittedStroke = rawCommittedPoints.length < 10;
      const minDistance =
        pointerType === "touch"
          ? (isShortCommittedStroke ? 0.35 : veryHeavyInputLoad ? 1.2 : adaptiveInputLoad ? 0.85 : 0.55)
          : pointerType === "pen"
            ? (isShortCommittedStroke ? 0.3 : veryHeavyInputLoad ? 1.2 : adaptiveInputLoad ? 0.95 : 0.65)
            : (isShortCommittedStroke ? 0.35 : 0.9);
      const minDistanceSq = minDistance * minDistance;
      endSampled.forEach((point) => {
        appendPointIfNeeded(rawCommittedPoints, point, minDistanceSq);
      });
      if (endSampled.length) {
        appendPointIfNeeded(rawCommittedPoints, endSampled[endSampled.length - 1], 0.01);
      }
    }
    const draftTool = String(finalizedDraft?.tool || tool || "pen");
    const committedPoints = optimizeFreehandCommittedPoints(rawCommittedPoints, draftTool, {
      isLowMemoryDevice,
      isVeryLowMemoryDevice,
    });
    const committedDraft = {
      ...finalizedDraft,
      points: committedPoints,
    };
    if (draftTool === "pen" || draftTool === "highlighter") {
      logFreehandDebug({
        phase: "commit",
        tool: draftTool,
        rawPoints: rawCommittedPoints.length,
        committedPoints: committedPoints.length,
        hasEvent: !!e,
        pointerType: String(e?.pointerType || activePointerTypeRef.current || ""),
        activePointer: activePointerIdRef.current,
      });
    }
    const nextElements = [...elements, committedDraft];
    onChange?.(
      {
        version: Number(value?.version || 0) + 1,
        elements: nextElements,
      },
      { kind: "draw", appendedElement: committedDraft }
    );
    draftStrokeRef.current = null;
    draftPointsRef.current = null;
    lastFreehandDraftRef.current = null;
    setDraftStroke(null);
    onDraftChange?.(null);
    emitCursor(null, e);
    releaseActivePointer();
  }

  function finalizePolygonSession(closeShape, triggerEvent = null) {
    if (tool !== "polygon" || !shapeSession || shapeSession.tool !== "polygon") return false;
    const basePoints = Array.isArray(shapeSession.points) ? shapeSession.points : [];
    const finalPoints = createPolygonPoints(basePoints, closeShape);
    const minPoints = closeShape ? 4 : 2;
    if (finalPoints.length >= minPoints) {
      const finalStroke = {
        id: String(draftStroke?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`),
        type: "stroke",
        tool: "polygon",
        color,
        width: brushSize,
        points: finalPoints,
      };
      onChange?.(
        {
          version: Number(value?.version || 0) + 1,
          elements: [...elements, finalStroke],
        },
        { kind: "draw", appendedElement: finalStroke }
      );
    }
    cancelDraftFrame();
    draftStrokeRef.current = null;
    draftPointsRef.current = null;
    setDraftStroke(null);
    onDraftChange?.(null);
    setShapeSession(null);
    emitCursor(null, triggerEvent);
    releaseActivePointer();
    return true;
  }

  useEffect(() => {
    if (!topRightControls) {
      setTopControlsHeight(0);
      return undefined;
    }

    const el = topControlsRef.current;
    if (!el) return undefined;

    const updateHeight = () => {
      setTopControlsHeight(Math.ceil(el.getBoundingClientRect().height || 0));
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [topRightControls, topControlsDetached]);

  useEffect(() => {
    if (!topLeftControls) {
      setTopLeftControlsHeight(0);
      return undefined;
    }

    const el = topLeftControlsRef.current;
    if (!el) return undefined;

    const updateHeight = () => {
      setTopLeftControlsHeight(Math.ceil(el.getBoundingClientRect().height || 0));
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [topLeftControls, topLeftControlsDetached]);

  useEffect(() => {
    if (!overlay || overlayDetached) {
      setOverlayHeight(0);
      return undefined;
    }

    const el = overlayRef.current;
    if (!el) return undefined;

    const updateHeight = () => {
      setOverlayHeight(Math.ceil(el.getBoundingClientRect().height || 0));
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [overlay, overlayDetached]);

  function updateCurrentPage() {
    const nextRect = readVisibleCanvasRectFromScroller(scrollerRef.current);
    const windowMargin = 12;
    const outsideRenderWindow = nextRect && (
      nextRect.x < renderRect.x - windowMargin ||
      nextRect.x + nextRect.width > renderRect.x + renderRect.width + windowMargin ||
      nextRect.y < renderRect.y - windowMargin ||
      nextRect.y + nextRect.height > renderRect.y + renderRect.height + windowMargin
    );
    if (outsideRenderWindow) {
      updateCurrentPageNow();
      return;
    }
    if (scrollFrameRef.current) return;
    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      const el = scrollerRef.current;
      if (!el || !displayHeight) return;
      refreshCanvasRectCache();
      const pageHeightPx = getRenderedPageHeightPx();
      const nextPage = Math.max(0, Math.min(pageCount - 1, Math.round(el.scrollTop / Math.max(1, pageHeightPx))));
      const nextIsAtBoardBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      setCurrentPage(nextPage);
      onPageChange?.(nextPage);
      setCanPageUp(el.scrollTop > 4);
      setIsAtBoardBottom(nextIsAtBoardBottom);
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);

      const nextRect = readVisibleCanvasRectFromScroller(el);
      if (!nextRect) return;
      applyVisibleCanvasRect(nextRect);
    });
  }

  function updateCurrentPageNow() {
    if (scrollFrameRef.current) {
      cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    }
    const el = scrollerRef.current;
    if (!el || !displayHeight) return;
    refreshCanvasRectCache();
    const pageHeightPx = getRenderedPageHeightPx();
    const nextPage = Math.max(0, Math.min(pageCount - 1, Math.round(el.scrollTop / Math.max(1, pageHeightPx))));
    const nextIsAtBoardBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    setCurrentPage(nextPage);
    onPageChange?.(nextPage);
    setCanPageUp(el.scrollTop > 4);
    setIsAtBoardBottom(nextIsAtBoardBottom);
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);

    const nextRect = readVisibleCanvasRectFromScroller(el);
    applyVisibleCanvasRect(nextRect);
  }

  function scrollToPage(targetPage) {
    prepareForProgrammaticScroll();
    scrollToPageTop(targetPage, "smooth");
  }

  function scrollHorizontally(direction) {
    const el = scrollerRef.current;
    if (!el) return;
    prepareForProgrammaticScroll();
    const step = Math.max(120, Math.round((el.clientWidth || 0) * 0.32));
    el.scrollTo({
      left: Math.max(0, el.scrollLeft + direction * step),
      behavior: "smooth",
    });
    scheduleProgrammaticScrollSync();
  }

  function scrollByPageStep(direction) {
    const el = scrollerRef.current;
    if (!el || !displayHeight) return;
    prepareForProgrammaticScroll();
    const pageHeightPx = getRenderedPageHeightPx();
    const step = Math.max(1, pageHeightPx * Math.max(0.25, Number(pageScrollStepRatio || 1)));
    const maxTop = Math.max(0, Number(el.scrollHeight || 0) - Number(el.clientHeight || 0));
    const targetTop = Math.max(0, Math.min(maxTop, Number(el.scrollTop || 0) + direction * step));
    el.scrollTo({ top: targetTop, behavior: "smooth" });
    scheduleProgrammaticScrollSync();
  }

  function handleAdvanceOrAddPage() {
    const fullPageStep = Number(pageScrollStepRatio || 1) >= 0.999;
    if (isAtBoardBottom || (fullPageStep && currentPage >= pageCount - 1)) {
      if (!canAddPage) return;
      preserveViewportAfterPageAddRef.current = true;
      onAddPage?.();
      return;
    }
    if (fullPageStep) {
      scrollToPage(currentPage + 1);
      return;
    }
    scrollByPageStep(1);
  }

  function handlePageUp() {
    const el = scrollerRef.current;
    if (!el || !displayHeight) return;
    if (Number(pageScrollStepRatio || 1) < 0.999) {
      scrollByPageStep(-1);
      return;
    }
    const pageHeightPx = getRenderedPageHeightPx();
    const scrollTop = Math.max(0, el.scrollTop);
    const pageIndex = Math.max(0, Math.min(pageCount - 1, Math.floor(scrollTop / Math.max(1, pageHeightPx))));
    const pageTop = pageIndex * pageHeightPx;
    const offsetWithinPage = scrollTop - pageTop;
    const threshold = 8;
    const targetPage = offsetWithinPage > threshold ? pageIndex : pageIndex - 1;
    const clampedPage = Math.max(0, Math.min(pageCount - 1, targetPage));
    prepareForProgrammaticScroll();
    el.scrollTo({ top: clampedPage * pageHeightPx, behavior: "smooth" });
    scheduleProgrammaticScrollSync();
  }

  useLayoutEffect(() => {
    updateCurrentPageNow();
    const frameId = requestAnimationFrame(() => updateCurrentPageNow());
    const timerId = setTimeout(() => updateCurrentPageNow(), 120);
    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timerId);
    };
  }, [displayWidth, displayHeight, pageCount, zoomPercent, viewportHeightPx, topOverlaySpace]);

  useEffect(() => {
    const previousPageCount = previousPageCountRef.current;
    previousPageCountRef.current = pageCount;
    if (pageCount <= previousPageCount) return;
    const shouldPreserveViewport = preserveViewportAfterPageAddRef.current;
    preserveViewportAfterPageAddRef.current = false;

    if (shouldPreserveViewport) {
      const el = scrollerRef.current;
      const scrollTop = Number(el?.scrollTop || 0);
      forceRenderWindowForScrollTop(scrollTop);
      updateCurrentPageNow();
      requestAnimationFrame(() => {
        forceRenderWindowForScrollTop(scrollTop);
        updateCurrentPageNow();
        requestAnimationFrame(() => {
          forceRenderWindowForScrollTop(scrollTop);
          updateCurrentPageNow();
        });
      });
      return;
    }

    if (currentPage !== previousPageCount - 1 && !isAtBoardBottom) return;
    forceRenderWindowForScrollTop(Number(scrollerRef.current?.scrollTop || 0));
    updateCurrentPageNow();
  }, [currentPage, displayHeight, isAtBoardBottom, pageCount]);


  const isAtLastPageNavigationPoint =
    isAtBoardBottom || (Number(pageScrollStepRatio || 1) >= 0.999 && currentPage >= pageCount - 1);
  const showAddPageButton = canAddPage && isAtLastPageNavigationPoint;
  const advanceOrAddDisabled = isAtLastPageNavigationPoint && !canAddPage;
  const pageScrollStepLabel =
    PAGE_SCROLL_STEP_OPTIONS.find((option) => option.value === pageScrollStepRatio)?.label || "1";

  const touchSafeButtonStyle = {
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  };
  const compactCanvasButtonSize = 34;
  const pageScrollStepMenuSide = controlsOnLeft ? "left" : "right";
  const pageScrollStepMenuStyle = {
    position: "absolute",
    [pageScrollStepMenuSide]: compactCanvasButtonSize + 6,
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: 4,
    borderRadius: 8,
    border: "1px solid rgba(148,163,184,0.55)",
    background: "rgba(2,6,23,0.94)",
    boxShadow: "0 12px 26px rgba(2,6,23,0.34)",
    zIndex: 8,
  };
  const pageScrollStepOptionStyle = {
    minWidth: 42,
    height: 28,
    padding: "0 8px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 800,
    ...touchSafeButtonStyle,
  };

  return (
    <div
      ref={wrapRef}
      className="panel-card"
      style={{
        padding: 10,
        position: "relative",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
        touchAction: "none",
        overscrollBehavior: "none",
      }}
    >
      {overlay && !overlayDetached ? (
        <div
          ref={overlayRef}
          style={{
            position: "absolute",
            top: resolvedOverlayTopOffset,
            left: overlayLeftInset,
            right: overlayRightInset,
            zIndex: resolvedOverlayZIndex,
            pointerEvents: "none",
          }}
        >
          <div style={{ pointerEvents: "auto" }}>{overlay}</div>
        </div>
      ) : null}

      {!topLeftControlsDetached && topLeftControls ? (
        <div
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <div ref={topLeftControlsRef} style={{ pointerEvents: "auto" }}>{topLeftControls}</div>
        </div>
      ) : null}

      {!topControlsDetached && topRightControls ? (
        <div
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <div
            ref={topControlsRef}
            style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end", pointerEvents: "auto" }}
          >
            {topRightControls}
            <button
              className="btn btn-secondary"
              type="button"
              onClick={handlePageUp}
              onContextMenu={(e) => e.preventDefault()}
              disabled={!canPageUp}
              style={{ minWidth: 48, paddingInline: 14, display: "none", ...touchSafeButtonStyle }}
              aria-label="Przejdz wyzej"
            >
              ↑
            </button>
          </div>
        </div>
      ) : null}

      {topControlsDetached ? (
        <div
          style={{
            height: detachedTopControlsReservedPx,
            marginBottom: -detachedTopControlsReservedPx,
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            overflow: "visible",
            position: "absolute",
            top: 14,
            left: 14,
            right: 14,
            zIndex: 6,
            pointerEvents: "none",
          }}
        >
          <div
            ref={topControlsRef}
            style={{
              flex: 1,
              minWidth: 0,
              pointerEvents: "none",
            }}
          >
            {topRightControls}
          </div>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={handlePageUp}
            onContextMenu={(e) => e.preventDefault()}
            disabled={!canPageUp}
            style={{ minWidth: 48, paddingInline: 14, display: "none", ...touchSafeButtonStyle }}
            aria-label="Przejdz wyzej"
          >
            ↑
          </button>
        </div>
      ) : null}

      {topLeftControlsDetached ? (
        <div
          ref={topLeftControlsRef}
          style={{
            height: detachedTopControlsReservedPx,
            marginBottom: -detachedTopControlsReservedPx,
            display: "flex",
            justifyContent: "flex-start",
            overflow: "visible",
            position: "absolute",
            top: 14,
            left: 14,
            right: 14,
            zIndex: 6,
            pointerEvents: "none",
          }}
        >
          <div style={{ pointerEvents: "auto" }}>{topLeftControls}</div>
        </div>
      ) : null}

      {overlayDetached ? <div style={{ marginBottom: 12 }}>{overlay}</div> : null}

      {!sideControlsDetached && sideControls ? (
        <div
          style={{
            position: "absolute",
            top: sideControlsTopOffset,
            ...sideAnchorStyle,
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              alignItems: sideAlign,
              pointerEvents: "auto",
            }}
          >
            {sideControls}
          </div>
        </div>
      ) : null}

      {sideControlsDetached ? (
        <div
          style={{
            marginBottom: 12,
            display: "flex",
            justifyContent: controlsOnLeft ? "flex-start" : "flex-end",
          }}
        >
          {sideControls}
        </div>
      ) : null}

      <div
        ref={scrollerRef}
        onScroll={updateCurrentPage}
        style={{
          maxHeight: viewportHeight,
          overflowY: needsVerticalBoardScroll ? "auto" : "hidden",
          overflowX: zoomPercent > 100 ? "auto" : "hidden",
          scrollbarGutter: needsVerticalBoardScroll ? "stable" : "auto",
          overscrollBehavior: "none",
          overscrollBehaviorY: "none",
          borderRadius: 14,
          paddingTop: topOverlaySpace,
          scrollSnapType: "y proximity",
          touchAction: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div
          style={{
            width: displayWidth || "100%",
            minHeight: displayHeight || 0,
            margin: "0 auto",
            position: "relative",
            background: boardTheme.pageFill,
            borderRadius: 14,
            touchAction: "none",
            overscrollBehavior: "none",
          }}
        >
          <canvas
            ref={canvasRef}
            width={canvasPixelWidth}
            height={canvasPixelHeight}
            onPointerDown={(e) => {
              hideStylusHoverCursor();
              registerTouchPoint(e);
              if (maybeStartOrUpdateTouchGesture(e)) {
                e.preventDefault();
                return;
              }
              capturePointerOnCanvas(e);
              const pt = pointFromEvent(e);
              if (pt && !shouldIgnorePointerStart(e)) emitCursor(pt, e);
              startStroke(e);
            }}
            onPointerEnter={(e) => {
              const pt = pointFromEvent(e);
              updateStylusHoverCursor(pt, e);
              if (pt) emitCursor(pt, e);
            }}
            onPointerMove={(e) => {
              if (String(e?.pointerType || "") === "touch") {
                registerTouchPoint(e);
                if (maybeStartOrUpdateTouchGesture(e)) {
                  e.preventDefault();
                  return;
                }
              }
              if (activePointerIdRef.current !== null && !isActivePointerEvent(e)) return;
              const pt = pointFromEvent(e);
              updateStylusHoverCursor(pt, e);
              if (pt) emitCursor(pt, e);
              moveStroke(e);
            }}
            onPointerUp={(e) => {
              finishStrokeSafely(e, "canvas_pointer_up");
              updateStylusHoverCursor(pointFromEvent(e), e);
              releasePointerOnCanvas(e);
              removeTouchPoint(e);
              stopTouchGestureIfNeeded();
            }}
            onMouseUp={() => {
              // Firefox potrafi czasem dostarczyć poprawny mouseup, ale pominąć
              // użyteczny pointerup dla naszego aktywnego pointera.
              if (activePointerIdRef.current !== null && (isDrawingRef.current || draftStrokeRef.current || lastFreehandDraftRef.current)) {
                finishStrokeSafely(null, "canvas_mouse_up");
              }
            }}
            onPointerLeave={(e) => {
              hideStylusHoverCursor();
              setPenPointerActive(false);
              removeTouchPoint(e);
              stopTouchGestureIfNeeded();
              // Do not end active drag/resize on leave; pointer capture keeps interaction stable
              // while crossing UI controls outside the canvas.
              if (activePointerIdRef.current === null) {
                if (activePointerTypeRef.current === "mouse") endStroke();
                emitCursor(null, e);
              }
            }}
            onLostPointerCapture={(e) => {
              // Firefox potrafi zakończyć sekwencję pointera przez utratę capture
              // zanim React dostarczy klasyczne pointerup. Domykamy wtedy stroke
              // po naszej stronie, zamiast zostawiać sam draft do wyczyszczenia.
              if (activePointerIdRef.current !== null && isActivePointerEvent(e)) {
                finishStrokeSafely(e, "lost_pointer_capture");
              }
            }}
            onPointerCancel={(e) => {
              hideStylusHoverCursor();
              setPenPointerActive(false);
              finishStrokeSafely(e, "canvas_pointer_cancel");
              releasePointerOnCanvas(e);
              removeTouchPoint(e);
              stopTouchGestureIfNeeded();
              emitCursor(null, e);
            }}
            onContextMenu={(e) => {
              if (tool === "polygon" && shapeSession && shapeSession.tool === "polygon") {
                e.preventDefault();
                finalizePolygonSession(false, e);
                return;
              }
              e.preventDefault();
            }}
            style={{
              position: "absolute",
              left: `${renderCanvasLeftPx}px`,
              top: `${renderCanvasTopPx}px`,
              width: `${renderCanvasWidthPx}px`,
              height: `${renderCanvasHeightPx}px`,
              borderRadius: 14,
              background: boardTheme.pageFill,
              border: boardTheme.canvasBorder,
              touchAction: "none",
              cursor:
                disabled
                  ? "not-allowed"
                  : penPointerActive
                    ? "none"
                  : tool === "eraser"
                    ? ERASER_CURSOR
                    : tool === "select"
                      ? "default"
                      : tool === "line" || tool === "circle" || tool === "polygon"
                        ? SHAPE_CURSOR
                      : tool === "highlighter"
                        ? HIGHLIGHTER_CURSOR
                        : PEN_CURSOR,
              display: "block",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
              WebkitTapHighlightColor: "transparent",
            }}
          />
          <canvas
            ref={draftOverlayRef}
            width={canvasPixelWidth}
            height={canvasPixelHeight}
            style={{
              position: "absolute",
              left: `${renderCanvasLeftPx}px`,
              top: `${renderCanvasTopPx}px`,
              width: `${renderCanvasWidthPx}px`,
              height: `${renderCanvasHeightPx}px`,
              pointerEvents: "none",
              borderRadius: 14,
              display: "block",
              touchAction: "none",
            }}
          />
          <div
            ref={stylusHoverCursorRef}
            data-whiteboard-stylus-cursor="true"
            aria-hidden="true"
            style={{
              position: "absolute",
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              width: tool === "eraser" ? 30 : tool === "highlighter" ? 24 : 18,
              height: tool === "eraser" ? 30 : tool === "highlighter" ? 24 : 18,
              transform: "translate(-50%, -50%)",
              borderRadius: "50%",
              border: tool === "eraser" ? "2px dashed #f97316" : `2px solid ${color || "#f59e0b"}`,
              background: tool === "highlighter" ? "rgba(245,158,11,0.18)" : "rgba(15,23,42,0.14)",
              boxShadow: "0 0 0 1px rgba(255,255,255,0.9), 0 2px 8px rgba(0,0,0,0.45)",
              pointerEvents: "none",
              zIndex: 6,
            }}
          >
            {tool !== "eraser" ? <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#fff" }} /> : null}
          </div>
          {(remoteCursors || []).map((cursor) => {
            const left = ((cursor.point?.x || 0) / WIDTH) * (displayWidth || WIDTH);
            const top = ((cursor.point?.y || 0) / totalHeight) * (displayHeight || totalHeight);

            return (
              <div
                key={cursor.userId}
                style={{
                  position: "absolute",
                  left,
                  top,
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  zIndex: 5,
                  transition: "left 90ms cubic-bezier(0.22, 1, 0.36, 1), top 90ms cubic-bezier(0.22, 1, 0.36, 1)",
                  willChange: "left, top",
                }}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: cursor.color,
                    border: "2px solid rgba(255,255,255,0.95)",
                    boxShadow: "0 0 0 3px rgba(0,0,0,0.12), 0 6px 18px rgba(0,0,0,0.16)",
                  }}
                />
              </div>
            );
          })}

          {marqueeSelection ? (
            <div
              style={{
                position: "absolute",
                left: (Math.min(marqueeSelection.startX, marqueeSelection.endX) / WIDTH) * (displayWidth || WIDTH),
                top: (Math.min(marqueeSelection.startY, marqueeSelection.endY) / totalHeight) * (displayHeight || totalHeight),
                width: (Math.abs(marqueeSelection.endX - marqueeSelection.startX) / WIDTH) * (displayWidth || WIDTH),
                height: (Math.abs(marqueeSelection.endY - marqueeSelection.startY) / totalHeight) * (displayHeight || totalHeight),
                border: "1.5px dashed #f59e0b",
                background: "rgba(245, 158, 11, 0.12)",
                pointerEvents: "none",
                zIndex: 5,
              }}
            />
          ) : null}

          {enableElementSelection && selectedElementsRect && (!selectedImageElement || selectedElementIds.length > 1) ? (
            <div
              style={{
                position: "absolute",
                left: (Number(selectedElementsRect.x || 0) / WIDTH) * (displayWidth || WIDTH),
                top: (Number(selectedElementsRect.y || 0) / totalHeight) * (displayHeight || totalHeight),
                width: (Number(selectedElementsRect.width || 0) / WIDTH) * (displayWidth || WIDTH),
                height: (Number(selectedElementsRect.height || 0) / totalHeight) * (displayHeight || totalHeight),
                border: "1.5px dashed #f59e0b",
                pointerEvents: "none",
                zIndex: 5,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  width: 10,
                  height: 10,
                  right: 0,
                  bottom: 0,
                  borderRadius: 0,
                  background: "#f59e0b",
                  border: "none",
                  boxShadow: "none",
                }}
              />
            </div>
          ) : null}

          {selectedAppearanceElements.length && selectedElementsRect ? (() => {
            const centerPx = ((Number(selectedElementsRect.x || 0) + Number(selectedElementsRect.width || 0) / 2) / WIDTH) * (displayWidth || WIDTH);
            const selectionTopPx = (Number(selectedElementsRect.y || 0) / totalHeight) * (displayHeight || totalHeight);
            const selectionHeightPx = (Number(selectedElementsRect.height || 0) / totalHeight) * (displayHeight || totalHeight);
            const selectionBottomPx = selectionTopPx + selectionHeightPx;
            const buttonTopPx = Math.max(8, selectionTopPx - 50);
            const panelWidth = Math.min(286, Math.max(220, Number(displayWidth || WIDTH) - 16));
            const panelLeftPx = Math.max(8, Math.min(Number(displayWidth || WIDTH) - panelWidth - 8, centerPx - panelWidth / 2));
            const panelHeightEstimate = 178;
            const availableCanvasHeight = Number(displayHeight || totalHeight);
            const panelTopPx = selectionBottomPx + panelHeightEstimate + 16 <= availableCanvasHeight
              ? selectionBottomPx + 10
              : Math.max(8, buttonTopPx - panelHeightEstimate - 8);
            const nativeColorValue = /^#[0-9a-f]{6}$/i.test(selectedAppearanceColor) ? selectedAppearanceColor : "#f59e0b";
            return (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  title="Edytuj wygląd"
                  aria-label="Edytuj wygląd"
                  onClick={() => setAppearanceEditorOpen((open) => !open)}
                  onContextMenu={(event) => event.preventDefault()}
                  style={{
                    position: "absolute",
                    left: centerPx,
                    top: buttonTopPx,
                    transform: "translateX(-50%)",
                    width: 42,
                    height: 42,
                    minWidth: 42,
                    padding: 0,
                    display: "grid",
                    placeItems: "center",
                    zIndex: 8,
                    pointerEvents: "auto",
                    borderColor: appearanceEditorOpen ? "#f59e0b" : undefined,
                    ...touchSafeButtonStyle,
                  }}
                >
                  <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5 7h14M5 12h14M5 17h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="9" cy="7" r="1.7" fill={selectedAppearanceColor} stroke="currentColor" strokeWidth="0.8" />
                    <circle cx="15" cy="12" r="1.7" fill={selectedAppearanceColor} stroke="currentColor" strokeWidth="0.8" />
                    <circle cx="11" cy="17" r="1.7" fill={selectedAppearanceColor} stroke="currentColor" strokeWidth="0.8" />
                  </svg>
                </button>
                {appearanceEditorOpen ? (
                  <div
                    style={{
                      position: "absolute",
                      left: panelLeftPx,
                      top: panelTopPx,
                      width: panelWidth,
                      boxSizing: "border-box",
                      zIndex: 9,
                      display: "grid",
                      gap: 12,
                      padding: 12,
                      borderRadius: 12,
                      background: boardTheme.helperBg,
                      border: boardTheme.helperBorder,
                      boxShadow: "0 14px 32px rgba(0,0,0,0.24)",
                      pointerEvents: "auto",
                    }}
                  >
                    <div style={{ fontFamily: "Rubik, sans-serif", fontSize: 13, fontWeight: 800 }}>Wygląd elementu</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {ELEMENT_APPEARANCE_COLORS.map((appearanceColor) => (
                        <button
                          key={`selected-appearance-${appearanceColor}`}
                          type="button"
                          title={`Kolor ${appearanceColor}`}
                          aria-label={`Kolor ${appearanceColor}`}
                          onClick={() => updateSelectedElementAppearance({ color: appearanceColor })}
                          onContextMenu={(event) => event.preventDefault()}
                          style={{
                            width: 28,
                            height: 28,
                            minWidth: 28,
                            padding: 0,
                            borderRadius: 999,
                            background: appearanceColor,
                            border: selectedAppearanceColor.toLowerCase() === appearanceColor.toLowerCase()
                              ? "2px solid #f59e0b"
                              : "1px solid rgba(148,163,184,0.72)",
                            cursor: "pointer",
                            ...touchSafeButtonStyle,
                          }}
                        />
                      ))}
                      {/^#[0-9a-f]{6}$/i.test(lastAppearanceCustomColor) && !ELEMENT_APPEARANCE_COLORS.some(
                        (paletteColor) => paletteColor.toLowerCase() === lastAppearanceCustomColor.toLowerCase()
                      ) ? (
                        <button
                          type="button"
                          title={`Ostatni własny kolor ${lastAppearanceCustomColor}`}
                          aria-label={`Ostatni własny kolor ${lastAppearanceCustomColor}`}
                          onClick={() => updateSelectedElementAppearance({ color: lastAppearanceCustomColor })}
                          onContextMenu={(event) => event.preventDefault()}
                          style={{
                            width: 28,
                            height: 28,
                            minWidth: 28,
                            padding: 0,
                            borderRadius: 999,
                            background: lastAppearanceCustomColor,
                            border: selectedAppearanceColor.toLowerCase() === lastAppearanceCustomColor.toLowerCase()
                              ? "2px solid #f59e0b"
                              : "1px solid rgba(148,163,184,0.72)",
                            cursor: "pointer",
                            ...touchSafeButtonStyle,
                          }}
                        />
                      ) : null}
                      <label
                        className="btn btn-secondary"
                        title="Własny kolor"
                        aria-label="Własny kolor"
                        style={{
                          position: "relative",
                          width: 28,
                          height: 28,
                          minWidth: 28,
                          padding: 0,
                          borderRadius: 999,
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 900,
                          cursor: "pointer",
                          overflow: "hidden",
                          ...touchSafeButtonStyle,
                        }}
                      >
                        ⋯
                        <input
                          type="color"
                          value={nativeColorValue}
                          onChange={(event) => {
                            setLastAppearanceCustomColor(event.target.value);
                            updateSelectedElementAppearance({ color: event.target.value });
                          }}
                          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
                        />
                      </label>
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "Rubik, sans-serif", fontSize: 12, fontWeight: 700 }}>
                      {appearanceIsTextOnly ? "Grubość tekstu" : "Grubość"}
                      <input
                        type="range"
                        min={appearanceIsTextOnly ? 300 : 1}
                        max={appearanceIsTextOnly ? 900 : 36}
                        step={appearanceIsTextOnly ? 100 : 1}
                        value={selectedAppearanceThickness}
                        onChange={(event) => updateSelectedElementAppearance({ thickness: Number(event.target.value) })}
                        style={{ flex: 1, minWidth: 0 }}
                      />
                      <span style={{ minWidth: 28, textAlign: "right" }}>{selectedAppearanceThickness}</span>
                    </label>
                  </div>
                ) : null}
              </>
            );
          })() : null}

          {selectedShapeVertexHandles.length ? (
            <>
              {selectedShapeVertexHandles.map((handle) => (
                <div
                  key={`shape-vertex-${handle.index}`}
                  style={{
                    position: "absolute",
                    left: ((Number(handle.point?.x || 0) / WIDTH) * (displayWidth || WIDTH)) - 6,
                    top: ((Number(handle.point?.y || 0) / totalHeight) * (displayHeight || totalHeight)) - 6,
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    background: "#f59e0b",
                    border: "1px solid rgba(255,255,255,0.95)",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
                    pointerEvents: "none",
                    zIndex: 6,
                  }}
                />
              ))}
            </>
          ) : null}

          {selectedImageElement ? (
            <div
              style={{
                position: "absolute",
                left: (((Number(selectedImageToolbarRect?.x || 0) + Number(selectedImageToolbarRect?.width || 0)) / WIDTH) * (displayWidth || WIDTH)) + 14,
                top: (((Number(selectedImageToolbarRect?.y || 0) + Number(selectedImageToolbarRect?.height || 0) / 2) / totalHeight) * (displayHeight || totalHeight)),
                transform: "translateY(-50%)",
                zIndex: 6,
                pointerEvents: "auto",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gap: 8,
                  padding: 8,
                  borderRadius: 14,
                  background: boardTheme.helperBg,
                  border: boardTheme.helperBorder,
                  boxShadow: "0 14px 32px rgba(0,0,0,0.18)",
                }}
              >
                {cropSession?.id === selectedImageId ? (
                  <>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Zastosuj"
                      aria-label="Zastosuj"
                      onClick={applyCropSelectedImage}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>✓</span>
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Anuluj"
                      aria-label="Anuluj"
                      onClick={cancelCropSelectedImage}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>✕</span>
                    </button>
                  </>
                ) : rotateSession?.id === selectedImageId ? (
                  <>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Obróć w lewo"
                      aria-label="Obróć w lewo"
                      onClick={() => rotateSelectedImageBy(-90)}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 20, lineHeight: 1 }}>↺</span>
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Obróć w prawo"
                      aria-label="Obróć w prawo"
                      onClick={() => rotateSelectedImageBy(90)}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 20, lineHeight: 1 }}>↻</span>
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Zastosuj"
                      aria-label="Zastosuj"
                      onClick={applyRotateSelectedImage}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>✓</span>
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Anuluj"
                      aria-label="Anuluj"
                      onClick={cancelRotateSelectedImage}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>✕</span>
                    </button>
                  </>
                ) : false ? (
                  <>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-secondary" type="button" onClick={() => rotateSelectedImageBy(-90)} onContextMenu={(e) => e.preventDefault()} style={touchSafeButtonStyle}>
                        ↺
                      </button>
                      <button className="btn btn-secondary" type="button" onClick={() => rotateSelectedImageBy(90)} onContextMenu={(e) => e.preventDefault()} style={touchSafeButtonStyle}>
                        ↻
                      </button>
                    </div>
                    <button className="btn btn-secondary" type="button" onClick={applyRotateSelectedImage} onContextMenu={(e) => e.preventDefault()} style={touchSafeButtonStyle}>
                      Zastosuj
                    </button>
                    <button className="btn btn-secondary" type="button" onClick={cancelRotateSelectedImage} onContextMenu={(e) => e.preventDefault()} style={touchSafeButtonStyle}>
                      Anuluj
                    </button>
                  </>
                ) : (
                  <>
                    {selectedImageElement?.graph ? (
                      <button
                        className="btn btn-secondary"
                        type="button"
                        title="Edytuj wykres"
                        aria-label="Edytuj wykres"
                        onClick={() => onEditGraph?.(selectedImageElement, selectedImageId)}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                      >
                        <svg width="23" height="23" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M4 19V5M4 19h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          <path d="M6 15c2-5 4-6.6 6-4.8 1.4 1.2 2.3 3.3 5.5-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="m15.5 18.5 3-3 1.8 1.8-3 3-2.2.4.4-2.2Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ) : null}
                    {selectedImageElement?.textWidget ? (
                      <button
                        className="btn btn-secondary"
                        type="button"
                        title="Edytuj tekst"
                        aria-label="Edytuj tekst"
                        onClick={() => onEditText?.(selectedImageElement, selectedImageId)}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                      >
                        <span style={{ fontFamily: "Rubik, sans-serif", fontSize: 23, fontWeight: 800, lineHeight: 1 }}>T</span>
                      </button>
                    ) : null}
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Usuń"
                      aria-label="Usuń"
                      onClick={deleteSelectedImage}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 20, lineHeight: 1 }}>🗑</span>
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Przytnij"
                      aria-label="Przytnij"
                      onClick={startCropSelectedImage}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: selectedImageElement?.textWidget ? "none" : "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 19, lineHeight: 1 }}>✂</span>
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title="Obróć"
                      aria-label="Obróć"
                      onClick={startRotateSelectedImage}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      <span style={{ fontSize: 20, lineHeight: 1 }}>↻</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {selectedShapeElement && selectedShapeRect ? (
            <div
              style={{
                position: "absolute",
                left: (((Number(selectedShapeRect.x || 0) + Number(selectedShapeRect.width || 0)) / WIDTH) * (displayWidth || WIDTH)) + 12,
                top: (((Number(selectedShapeRect.y || 0) + Number(selectedShapeRect.height || 0) / 2) / totalHeight) * (displayHeight || totalHeight)),
                transform: "translateY(-50%)",
                zIndex: 6,
                pointerEvents: "auto",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gap: 8,
                  padding: 8,
                  borderRadius: 12,
                  background: boardTheme.helperBg,
                  border: boardTheme.helperBorder,
                }}
              >
                {!shapeVertexEditEnabled ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={toggleSelectedShapeStyle}
                  onContextMenu={(e) => e.preventDefault()}
                  title={String(selectedShapeElement?.lineStyle || "") === "dashed" ? "Ustaw ciągłą" : "Ustaw przerywaną"}
                  style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                >
                  {(() => {
                    const currentIsDashed = String(selectedShapeElement?.lineStyle || "") === "dashed";
                    const previewDashed = !currentIsDashed;
                    const dash = previewDashed ? "4 3" : undefined;
                    const tool = String(selectedShapeElement?.tool || "line");
                    if (tool === "circle") {
                      return (
                        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                          <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray={dash} />
                        </svg>
                      );
                    }
                    if (tool === "polygon") {
                      return (
                        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 4.8 L18.8 9.6 L16.2 18.2 L7.8 18.2 L5.2 9.6 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray={dash} />
                        </svg>
                      );
                    }
                    return (
                      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                        <line x1="4" y1="18" x2="20" y2="6" stroke="currentColor" strokeWidth="2" strokeDasharray={dash} />
                      </svg>
                    );
                  })()}
                </button>
                ) : null}
                {(() => {
                  const toolType = String(selectedShapeElement?.tool || "");
                  if (toolType !== "line" && toolType !== "polygon") return null;
                  if (shapeVertexEditEnabled) {
                    return (
                      <>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={applyShapeVertexEdit}
                          onContextMenu={(e) => e.preventDefault()}
                          title="Zastosuj edycję punktów"
                          aria-label="Zastosuj edycję punktów"
                          style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M5 12.8 L9.5 17.2 L19 7.8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={cancelShapeVertexEdit}
                          onContextMenu={(e) => e.preventDefault()}
                          title="Anuluj edycję punktów"
                          aria-label="Anuluj edycję punktów"
                          style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M7 7 L17 17 M17 7 L7 17" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                          </svg>
                        </button>
                      </>
                    );
                  }
                  return (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={startShapeVertexEdit}
                      onContextMenu={(e) => e.preventDefault()}
                      title="Edytuj punkty"
                      style={{ width: 44, height: 44, padding: 0, display: "grid", placeItems: "center", ...touchSafeButtonStyle }}
                    >
                      {toolType === "polygon" ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            d="M12 5.1 L18.5 9.7 L16 17.9 L8 17.9 L5.5 9.7 Z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle cx="12" cy="5.1" r="1.25" fill="currentColor" />
                          <circle cx="18.5" cy="9.7" r="1.25" fill="currentColor" />
                          <circle cx="16" cy="17.9" r="1.25" fill="currentColor" />
                          <circle cx="8" cy="17.9" r="1.25" fill="currentColor" />
                          <circle cx="5.5" cy="9.7" r="1.25" fill="currentColor" />
                        </svg>
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="6" cy="18" r="1.8" fill="currentColor" />
                        <circle cx="18" cy="6" r="1.8" fill="currentColor" />
                        <line x1="7.8" y1="16.2" x2="16.2" y2="7.8" stroke="currentColor" strokeWidth="1.8" />
                        </svg>
                      )}
                    </button>
                  );
                })()}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          ...bottomUtilityAnchorStyle,
          bottom: 14,
          zIndex: 4,
          display: "flex",
          gap: 8,
          alignItems: "center",
          justifyContent: bottomUtilityAlign,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            padding: "2px 6px",
            borderRadius: 6,
            background: "transparent",
            color: themeMode === "light" ? "rgba(15,23,42,0.72)" : "rgba(248,250,252,0.78)",
            border: "none",
            boxShadow: "none",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.2,
            pointerEvents: "none",
          }}
        >
          Strona {currentPage + 1} / {pageCount}
        </div>
        {zoomPercent > 100 ? (
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => scrollHorizontally(-1)}
            onContextMenu={(e) => e.preventDefault()}
            disabled={!canScrollLeft}
            style={{ minWidth: 48, paddingInline: 14, pointerEvents: "auto", ...touchSafeButtonStyle }}
            aria-label="Przesuń w lewo"
          >
            ←
          </button>
        ) : null}
      </div>

      {bottomLeftTopControls ? (
        <div
          style={{
            position: "absolute",
            ...bottomUtilityAnchorStyle,
            bottom: 52,
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <div style={{ pointerEvents: "auto" }}>{bottomLeftTopControls}</div>
        </div>
      ) : null}

      {zoomPercent > 100 ? (
        <>
          <div
            style={{
              position: "absolute",
              left: 14,
              bottom: 62,
              zIndex: 4,
              pointerEvents: "none",
              display: "none",
            }}
          >
            <div style={{ pointerEvents: "auto" }}>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => scrollHorizontally(-1)}
                onContextMenu={(e) => e.preventDefault()}
                disabled={!canScrollLeft}
                style={{
                  width: 46,
                  height: 46,
                  minWidth: 46,
                  maxWidth: 46,
                  minHeight: 46,
                  maxHeight: 46,
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                  ...touchSafeButtonStyle,
                }}
            aria-label="Przesuń w lewo"
              >
                ←
              </button>
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              right: 14,
              bottom: 110,
              zIndex: 4,
              pointerEvents: "none",
              display: "none",
            }}
          >
            <div style={{ pointerEvents: "auto" }}>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => scrollHorizontally(1)}
                onContextMenu={(e) => e.preventDefault()}
                disabled={!canScrollRight}
                style={{ minWidth: 48, paddingInline: 14, ...touchSafeButtonStyle }}
                aria-label="Przesun w prawo"
              >
                →
              </button>
            </div>
          </div>
        </>
      ) : null}

      {true ? (
        <div
          style={{
            position: "absolute",
            ...sideAnchorStyle,
            bottom: floatingControlsBottomInset,
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <div
            data-demo-tutorial-target="bottom-nav"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              alignItems: sideAlign,
              pointerEvents: "auto",
            }}
          >
            <button
              className="btn btn-secondary"
              type="button"
              onClick={handleAdvanceOrAddPage}
              onContextMenu={(e) => e.preventDefault()}
              disabled={advanceOrAddDisabled}
              style={{
                minWidth: 48,
                paddingInline: 14,
                display: "none",
                ...touchSafeButtonStyle,
              }}
              aria-label={showAddPageButton ? "Dodaj stronę" : "Przejdź niżej"}
            >
              {showAddPageButton ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontWeight: 900, fontSize: 18, lineHeight: 1 }}>+</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="4" y="3.5" width="16" height="17" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 8h8M8 12h8M8 16h6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </span>
              ) : "↓"}
            </button>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                alignItems: sideAlign,
                flexWrap: "nowrap",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn btn-secondary"
                type="button"
                onClick={handlePageUp}
                onContextMenu={(e) => e.preventDefault()}
                disabled={!canPageUp}
                style={{
                  minWidth: compactCanvasButtonSize,
                  width: compactCanvasButtonSize,
                  height: compactCanvasButtonSize,
                  padding: "0 8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15,
                  lineHeight: 1,
                  ...touchSafeButtonStyle,
                }}
                aria-label="Przejdź wyżej"
              >
                ↑
              </button>
              <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setPageScrollStepMenuOpen((open) => !open)}
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    minWidth: compactCanvasButtonSize,
                    width: compactCanvasButtonSize,
                    height: 26,
                    padding: "0 6px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    lineHeight: 1,
                    fontWeight: 800,
                    ...touchSafeButtonStyle,
                  }}
                  title="Krok przewijania"
                  aria-label={`Krok przewijania: ${pageScrollStepLabel} strony`}
                  aria-expanded={pageScrollStepMenuOpen}
                >
                  {pageScrollStepLabel}
                </button>
                {pageScrollStepMenuOpen ? (
                  <div style={pageScrollStepMenuStyle}>
                    {PAGE_SCROLL_STEP_OPTIONS.map((option) => {
                      const active = option.value === pageScrollStepRatio;
                      return (
                        <button
                          key={option.label}
                          className={`btn ${active ? "btn-primary" : "btn-secondary"}`}
                          type="button"
                          onClick={() => {
                            setPageScrollStepRatio(option.value);
                            setPageScrollStepMenuOpen(false);
                          }}
                          onContextMenu={(e) => e.preventDefault()}
                          style={pageScrollStepOptionStyle}
                          aria-pressed={active}
                          aria-label={`Przewijaj o ${option.label} strony`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              {bottomRightTopControls ? (
                <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  {bottomRightTopControls}
                </div>
              ) : null}
              <button
                className="btn btn-secondary"
                type="button"
                onClick={handleAdvanceOrAddPage}
                onContextMenu={(e) => e.preventDefault()}
                disabled={advanceOrAddDisabled}
                style={{
                  minWidth: compactCanvasButtonSize,
                  width: compactCanvasButtonSize,
                  height: compactCanvasButtonSize,
                  padding: "0 8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15,
                  lineHeight: 1,
                  ...touchSafeButtonStyle,
                }}
                aria-label={showAddPageButton ? "Dodaj stronę" : "Przejdź niżej"}
              >
                {showAddPageButton ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontWeight: 900, fontSize: 15, lineHeight: 1 }}>+</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
                      <rect x="4" y="3.5" width="16" height="17" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8 8h8M8 12h8M8 16h6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </span>
                ) : "↓"}
              </button>
            </div>
            {bottomRightControls ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 6,
                  alignItems: "center",
                  flexWrap: "nowrap",
                  justifyContent: controlsOnLeft ? "flex-start" : "flex-end",
                }}
              >
                {zoomPercent > 100 ? (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => scrollHorizontally(1)}
                    onContextMenu={(e) => e.preventDefault()}
                    disabled={!canScrollRight}
                    style={{ minWidth: 48, paddingInline: 14, ...touchSafeButtonStyle }}
                    aria-label="Przesuń w prawo"
                  >
                    →
                  </button>
                ) : null}
                {bottomRightControls}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {bottomCenterControls ? (
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: floatingControlsBottomInset,
            zIndex: 4,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {bottomCenterControls}
        </div>
      ) : null}
    </div>
  );
}

function quantizePoint(value, step) {
  const safeStep = Math.max(0.05, Number(step || 0.25));
  const numeric = Number(value || 0);
  return Math.round(numeric / safeStep) * safeStep;
}

function distancePointToLine(point, start, end) {
  const x = Number(point?.x || 0);
  const y = Number(point?.y || 0);
  const x1 = Number(start?.x || 0);
  const y1 = Number(start?.y || 0);
  const x2 = Number(end?.x || 0);
  const y2 = Number(end?.y || 0);
  const dx = x2 - x1;
  const dy = y2 - y1;
  const denom = Math.hypot(dx, dy);
  if (denom < 1e-6) return Math.hypot(x - x1, y - y1);
  return Math.abs((dy * x - dx * y + x2 * y1 - y2 * x1) / denom);
}

function distanceBetweenPoints(pointA, pointB) {
  return Math.hypot(
    Number(pointA?.x || 0) - Number(pointB?.x || 0),
    Number(pointA?.y || 0) - Number(pointB?.y || 0)
  );
}

function getTurnCosine(prev, curr, next) {
  const ax = Number(curr?.x || 0) - Number(prev?.x || 0);
  const ay = Number(curr?.y || 0) - Number(prev?.y || 0);
  const bx = Number(next?.x || 0) - Number(curr?.x || 0);
  const by = Number(next?.y || 0) - Number(curr?.y || 0);
  const aLen = Math.hypot(ax, ay);
  const bLen = Math.hypot(bx, by);
  if (aLen < 1e-6 || bLen < 1e-6) return 1;
  return (ax * bx + ay * by) / (aLen * bLen);
}

function optimizeFreehandCommittedPoints(points, tool, options = {}) {
  const draftTool = String(tool || "");
  if (draftTool !== "pen" && draftTool !== "highlighter") {
    return Array.isArray(points) ? points : [];
  }

  const src = Array.isArray(points) ? points : [];
  if (!src.length) return [];

  const isLowMemoryDevice = !!options.isLowMemoryDevice;
  const isVeryLowMemoryDevice = !!options.isVeryLowMemoryDevice;
  const isHighlighter = draftTool === "highlighter";

  const baseEpsilon = isHighlighter ? 0.5 : 0.18;
  const simplifyEpsilon = isVeryLowMemoryDevice
    ? baseEpsilon + (isHighlighter ? 0.2 : 0.12)
    : isLowMemoryDevice
      ? baseEpsilon + (isHighlighter ? 0.1 : 0.06)
      : baseEpsilon;

  return optimizeCommittedStrokePoints(src, {
    quantStep: isHighlighter ? 0.35 : 0.25,
    simplifyEpsilon,
    keepSmallCount: isHighlighter ? 14 : 20,
  });
}

function optimizeCommittedStrokePoints(points, options = {}) {
  const src = Array.isArray(points) ? points : [];
  if (!src.length) return [];

  const quantStep = Math.max(0.1, Number(options.quantStep || 0.25));
  const simplifyEpsilon = Math.max(0.05, Number(options.simplifyEpsilon || 0.35));
  const keepSmallCount = Math.max(6, Number(options.keepSmallCount || 12));

  const quantized = [];
  for (let i = 0; i < src.length; i++) {
    const p = src[i];
    const next = {
      x: quantizePoint(p?.x, quantStep),
      y: quantizePoint(p?.y, quantStep),
    };
    const prev = quantized[quantized.length - 1];
    if (prev && Math.abs(prev.x - next.x) < 1e-6 && Math.abs(prev.y - next.y) < 1e-6) {
      continue;
    }
    quantized.push(next);
  }

  if (quantized.length <= 2 || quantized.length <= keepSmallCount) {
    return quantized;
  }

  const simplified = [quantized[0]];
  let lastKeptIndex = 0;
  for (let i = 1; i < quantized.length - 1; i++) {
    const prev = simplified[simplified.length - 1];
    const curr = quantized[i];
    const next = quantized[i + 1];
    const dist = distancePointToLine(curr, prev, next);
    const localPrev = quantized[i - 1];
    const localNext = quantized[i + 1];
    const turnCos = getTurnCosine(localPrev, curr, localNext);
    const isStrongTurn = turnCos <= 0.76;
    const isReversal = turnCos <= 0.2;
    const skippedRunLength = i - lastKeptIndex;
    const pathDistanceFromKept = distanceBetweenPoints(prev, curr);
    const nextDistance = distanceBetweenPoints(curr, next);
    const preserveRunAnchor =
      skippedRunLength >= 8 &&
      pathDistanceFromKept >= simplifyEpsilon * 4 &&
      nextDistance >= simplifyEpsilon;

    if (dist >= simplifyEpsilon || isStrongTurn || isReversal || preserveRunAnchor) {
      simplified.push(curr);
      lastKeptIndex = i;
    }
  }
  simplified.push(quantized[quantized.length - 1]);

  return simplified.length >= 2 ? simplified : quantized;
}
